// @ts-nocheck
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SeriesSettingsFlyout } from './series-settings-flyout';
import { ShareSeriesDialog } from './share-series-dialog';
import { ActionItemThread } from './action-item-thread';
import { useAgentChatOpener } from '@/components/agent-chat';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';
import { supabase } from '@saas-boilerplate/shared';
import { useWorkspace } from '@/contexts/context';
import { describeWorkspaceDataRetention } from '@/lib/workspace-retention';
import { useDesktopApp } from '@/hooks/use-desktop-app';
import { useAuth } from '@/contexts/auth-context';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  Brain,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CheckCircle2,
  Clock,
  Check,
  Copy,
  Gavel,
  Info,
  Monitor,
  FileText,
  Lightbulb,
  Link2,
  List,
  ListChecks,
  Loader2,
  Lock,
  Mail,
  MessageSquare,
  MessagesSquare,
  MoreVertical,
  Pause,
  Play,
  Pencil,
  Plus,
  Settings,
  Share2,
  Sparkles,
  Timer,
  Trash2,
  Upload,
  Users,
  Video,
  XCircle,
} from 'lucide-react';
import { invokeEdgeFn } from '@/lib/edge-functions';
import { timed, startTimer } from '@/lib/perf';
import { MeetingEnrolWizard, MEETING_TYPES } from '@/components/meetings-enrol-wizard';
import { MeetingOccurrencePicker } from './meeting-occurrence-picker';
import { CheckInRecorder, CheckInList } from './check-in-recorder';
import { AppTopBar } from '@/components/app-top-bar';
import { MarkdownLite } from '@/components/ui/markdown-lite';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { cn } from '@/components/ui/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function getSeriesPrimaryMeeting(s: any) {
  // Cancelled occurrences are hidden from the active list: a calendar removal/cancellation must not
  // keep representing the series. A series whose occurrences are ALL cancelled returns null and is
  // dropped from the active explorer list (its history, if any, is still in the DB).
  const ms = (s.meetings || []).filter((m: any) => m && m.status !== 'cancelled');
  const now = Date.now();
  const future = ms
    .filter((m: any) => m.starts_at && new Date(m.starts_at).getTime() >= now)
    .sort(
      (a: any, b: any) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
    );
  if (future.length) return future[0];
  const withTime = ms
    .filter((m: any) => m.starts_at)
    .sort((a: any, b: any) => new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime());
  return withTime[0] || ms[0] || null;
}

function attendeeBubbleName(attendee: any): string {
  const explicitName = String(
    attendee?.name || attendee?.display_name || attendee?.full_name || '',
  ).trim();
  if (explicitName) return explicitName;
  const email = String(attendee?.email || '').trim();
  if (email) {
    const local = email.split('@')[0] || '';
    const cleaned = local
      .replace(/[._-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (cleaned) {
      return cleaned
        .split(' ')
        .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1))
        .join(' ');
    }
    return email;
  }
  const userId = String(attendee?.user_id || '').trim();
  if (userId) return `Member ${userId.slice(0, 8)}`;
  return 'Invitee';
}

function attendeeBubbleInitial(attendee: any): string {
  const label = attendeeBubbleName(attendee);
  return (label.slice(0, 1) || '?').toUpperCase();
}

/** Red/green/amber ring around an attendee bubble by RSVP. */
function rsvpRingClass(rsvp: any): string {
  switch (String(rsvp || '').toLowerCase()) {
    case 'accepted':
      return 'ring-2 ring-emerald-500';
    case 'declined':
      return 'ring-2 ring-rose-500';
    default:
      return 'ring-1 ring-border';
  }
}
function rsvpShortLabel(rsvp: any): string {
  switch (String(rsvp || '').toLowerCase()) {
    case 'accepted':
      return 'Accepted';
    case 'declined':
      return 'Declined';
    default:
      return 'No answer';
  }
}

function AttendeeBubbleStack({
  attendees,
  maxVisible = 2,
  compact = false,
  showRsvp = false,
}: {
  attendees: any[];
  maxVisible?: number;
  compact?: boolean;
  /** Ring each bubble by RSVP and wrap the stack in a tooltip summarising accepted/declined/pending. */
  showRsvp?: boolean;
}) {
  if (!attendees.length) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-full border border-dashed border-border/60 bg-muted/60 font-semibold text-muted-foreground',
          compact ? 'h-6 w-6 text-[8px]' : 'h-8 w-8 text-[10px]',
        )}
        title="No invitees"
      >
        -
      </div>
    );
  }
  const visible = attendees.slice(0, maxVisible);
  const remainder = attendees.length - visible.length;
  const stack = (
    <div className={cn('flex', compact ? '-space-x-1' : '-space-x-2')}>
      {visible.map((a) => (
        <div
          key={a.id}
          className={cn(
            'flex items-center justify-center rounded-full border border-background bg-muted font-semibold text-muted-foreground',
            compact ? 'h-6 w-6 text-[8px]' : 'h-8 w-8 border-2 text-[10px]',
            showRsvp && rsvpRingClass(a.rsvp),
          )}
          title={showRsvp ? `${attendeeBubbleName(a)} - ${rsvpShortLabel(a.rsvp)}` : attendeeBubbleName(a)}
        >
          {attendeeBubbleInitial(a)}
        </div>
      ))}
      {remainder > 0 ? (
        <div
          className={cn(
            'flex items-center justify-center rounded-full border border-background bg-muted/80 font-bold text-muted-foreground',
            compact ? 'h-6 w-6 text-[8px]' : 'h-8 w-8 border-2 text-[10px]',
          )}
          title={`${remainder} more invitee${remainder === 1 ? '' : 's'}`}
        >
          +{remainder}
        </div>
      ) : null}
    </div>
  );

  if (!showRsvp) return stack;

  const norm = (a: any) => String(a?.rsvp || '').toLowerCase();
  // Headings sit on the dark (bg-primary) tooltip, so use light shades for contrast.
  const groups = [
    { label: 'Accepted', cls: 'text-emerald-300', items: attendees.filter((a) => norm(a) === 'accepted') },
    { label: 'Declined', cls: 'text-rose-300', items: attendees.filter((a) => norm(a) === 'declined') },
    {
      label: 'No answer',
      cls: 'text-primary-foreground/70',
      items: attendees.filter((a) => norm(a) !== 'accepted' && norm(a) !== 'declined'),
    },
  ].filter((g) => g.items.length > 0);
  const GROUP_CAP = 8;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div tabIndex={0} className="cursor-help">
          {stack}
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="end" className="max-w-xs space-y-2 text-xs leading-relaxed">
        <p className="font-medium">
          {attendees.length} invitee{attendees.length === 1 ? '' : 's'}
        </p>
        {groups.map((g) => (
          <div key={g.label}>
            <p className={cn('font-semibold', g.cls)}>
              {g.label} · {g.items.length}
            </p>
            <ul className="mt-0.5 space-y-0.5 pl-2.5">
              {g.items.slice(0, GROUP_CAP).map((a) => (
                <li key={a.id || a.user_id || a.email} className="truncate">
                  {attendeeBubbleName(a)}
                </li>
              ))}
              {g.items.length > GROUP_CAP ? (
                <li className="text-muted-foreground">+{g.items.length - GROUP_CAP} more</li>
              ) : null}
            </ul>
          </div>
        ))}
      </TooltipContent>
    </Tooltip>
  );
}

const ATTENDEE_PREVIEW_CHUNK_SIZE = 25;

async function fetchAttendeesByMeetingIds(meetingIds: string[]) {
  const byMeeting = new Map<string, any[]>();
  for (let i = 0; i < meetingIds.length; i += ATTENDEE_PREVIEW_CHUNK_SIZE) {
    const batch = meetingIds.slice(i, i + ATTENDEE_PREVIEW_CHUNK_SIZE);
    const { data, error } = await supabase
      .from('meeting_attendees')
      .select('id, meeting_id, email, user_id, rsvp')
      .in('meeting_id', batch);
    if (error) return { byMeeting: {}, error };
    for (const row of data || []) {
      const meetingId = String(row.meeting_id || '');
      if (!meetingId) continue;
      if (!byMeeting.has(meetingId)) byMeeting.set(meetingId, []);
      byMeeting.get(meetingId)!.push(row);
    }
  }
  return { byMeeting: Object.fromEntries(byMeeting), error: null };
}

async function fetchRowsByMeetingIds(
  table: string,
  meetingIds: string[],
  columns = 'meeting_id',
  /** Status values to leave out, e.g. archived action items that should not be counted. */
  excludeStatuses?: string[],
) {
  const rows: any[] = [];
  for (let i = 0; i < meetingIds.length; i += ATTENDEE_PREVIEW_CHUNK_SIZE) {
    const batch = meetingIds.slice(i, i + ATTENDEE_PREVIEW_CHUNK_SIZE);
    let query = supabase
      .from(table)
      .select(columns)
      .in('meeting_id', batch);
    if (excludeStatuses && excludeStatuses.length > 0) {
      query = query.not('status', 'in', `(${excludeStatuses.join(',')})`);
    }
    const { data, error } = await query;
    if (error) return { rows: [], error };
    rows.push(...(data || []));
  }
  return { rows, error: null };
}

function formatDurationMins(start?: string | null, end?: string | null) {
  if (!start || !end) return null;
  const a = new Date(start).getTime();
  const b = new Date(end).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b <= a) return null;
  return Math.round((b - a) / 60000);
}

/** Explorer list clock line - avoid "Next occurrence" when there is no upcoming session (e.g. past one-offs). */
function formatExplorerSeriesWhenLine(series: any, primaryMeeting: any | null): string {
  const dur = formatDurationMins(primaryMeeting?.starts_at, primaryMeeting?.ends_at);
  const raw = primaryMeeting?.starts_at;
  const start = raw ? new Date(raw) : null;
  if (!start || Number.isNaN(start.getTime())) {
    return 'Schedule a time on this series to see the next occurrence.';
  }
  const detail = `${start.toLocaleDateString(undefined, { weekday: 'long' })} at ${start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}${dur != null ? ` • ${dur} mins` : ''}`;
  const isFuture = start.getTime() >= Date.now();
  const recurring = Boolean(series?.is_recurring_series);

  if (recurring && isFuture) return `Next occurrence: ${detail}`;
  if (recurring && !isFuture) return `Last occurrence: ${detail}`;
  if (!recurring && isFuture) return `Meeting: ${detail}`;
  return `Occurred: ${detail}`;
}

function toLocalDayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfWeekSunday(d: Date): Date {
  const x = new Date(d);
  x.setHours(12, 0, 0, 0);
  const dow = x.getDay();
  x.setDate(x.getDate() - dow);
  x.setHours(0, 0, 0, 0);
  return x;
}

function flattenSeriesMeetings(seriesList: any[]) {
  const out: { series: any; meeting: any }[] = [];
  for (const s of seriesList) {
    for (const m of s.meetings || []) {
      if (!m?.starts_at) continue;
      out.push({ series: s, meeting: m });
    }
  }
  return out;
}

function groupMeetingsByDayKey(rows: { series: any; meeting: any }[]) {
  const map = new Map<string, { series: any; meeting: any }[]>();
  for (const row of rows) {
    const d = new Date(row.meeting.starts_at);
    if (Number.isNaN(d.getTime())) continue;
    const key = toLocalDayKey(d);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(row);
  }
  for (const arr of map.values()) {
    arr.sort(
      (a, b) =>
        new Date(a.meeting.starts_at).getTime() - new Date(b.meeting.starts_at).getTime(),
    );
  }
  return map;
}

function buildMonthGridCells(anchorMonth: Date) {
  const year = anchorMonth.getFullYear();
  const month = anchorMonth.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();
  const cells: { date: Date; inMonth: boolean }[] = [];
  for (let i = 0; i < firstDow; i++) {
    const day = prevMonthDays - firstDow + i + 1;
    cells.push({ date: new Date(year, month - 1, day), inMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(year, month, d), inMonth: true });
  }
  let trailing = 1;
  while (cells.length % 7 !== 0) {
    cells.push({ date: new Date(year, month + 1, trailing), inMonth: false });
    trailing++;
  }
  return cells;
}

function calendarEventVariant(meetingType: string | undefined): 'primary' | 'tertiary' {
  if (meetingType === 'retrospective' || meetingType === 'one_on_one') return 'tertiary';
  return 'primary';
}

function CalendarProviderIcon({
  provider,
  connected,
}: {
  provider: 'google' | 'microsoft';
  connected: boolean;
}) {
  const glow = connected
    ? 'shadow-[0_0_0_1px_rgba(16,185,129,0.25),0_0_12px_rgba(16,185,129,0.35)]'
    : 'shadow-[0_0_0_1px_rgba(239,68,68,0.22),0_0_12px_rgba(239,68,68,0.28)]';

  return (
    <span
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center overflow-hidden rounded-md bg-white',
        glow,
      )}
      aria-label={provider === 'google' ? 'Google Calendar' : 'Microsoft Calendar'}
      title={`${provider === 'google' ? 'Google Calendar' : 'Microsoft Calendar'} ${connected ? 'synced' : 'not synced'}`}
    >
      {provider === 'google' ? (
        <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
          <path fill="#4285F4" d="M44 33.2V14.8a3 3 0 0 0-3-3H7a3 3 0 0 0-3 3v18.4h40z" />
          <path fill="#34A853" d="M4 33.2v6a3 3 0 0 0 3 3h34a3 3 0 0 0 3-3v-6H4z" />
          <path fill="#FBBC05" d="M31 33.2h13v6a3 3 0 0 1-3 3H31v-9z" />
          <path fill="#EA4335" d="M4 33.2h9v9H7a3 3 0 0 1-3-3v-6z" />
          <rect x="14" y="15" width="20" height="18" rx="2" fill="#fff" />
          <text x="24" y="28" textAnchor="middle" fontSize="12" fontWeight="700" fill="#1A73E8">31</text>
        </svg>
      ) : (
        <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
          <path fill="#0A64C5" d="M6 10l15-3v34l-15-3z" />
          <path fill="#1B75D0" d="M21 7h21v34H21z" />
          <rect x="26" y="18" width="12" height="12" rx="1.5" fill="#fff" />
          <path fill="#1B75D0" d="M26 22h12v2H26zm0 4h12v2H26zm4-8h2v12h-2z" />
          <text x="14" y="29" textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff">O</text>
        </svg>
      )}
    </span>
  );
}

function MeetingsCalendarSection({
  seriesList,
  loadingList,
  calendarCursor,
  calendarZoom,
  openSession,
}: {
  seriesList: any[];
  loadingList: boolean;
  calendarCursor: Date;
  calendarZoom: 'day' | 'week' | 'month';
  openSession: (series: any, meetingId?: string) => void | Promise<void>;
}) {
  const flatMeetings = useMemo(() => flattenSeriesMeetings(seriesList), [seriesList]);
  const byDay = useMemo(() => groupMeetingsByDayKey(flatMeetings), [flatMeetings]);
  const monthAnchor = useMemo(
    () => new Date(calendarCursor.getFullYear(), calendarCursor.getMonth(), 1),
    [calendarCursor],
  );
  const monthCells = useMemo(() => buildMonthGridCells(monthAnchor), [monthAnchor]);
  const weekStart = useMemo(() => startOfWeekSunday(calendarCursor), [calendarCursor]);

  const dayKeyCursor = toLocalDayKey(calendarCursor);
  const meetingsThisDay = byDay.get(dayKeyCursor) || [];

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    });
  }, [weekStart]);

  if (loadingList) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {calendarZoom === 'month' && (
        <section className="overflow-hidden rounded-3xl border border-outline-variant/10 bg-surface-container-lowest shadow-2xl shadow-stone-200/50">
          <div className="grid grid-cols-7 border-b border-outline-variant/10 bg-surface-container-low/50">
            {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((label) => (
              <div
                key={label}
                className="py-4 text-center text-[10px] font-bold uppercase tracking-widest text-stone-400"
              >
                {label}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {monthCells.map((cell, idx) => {
              const key = toLocalDayKey(cell.date);
              const dayMeetings = byDay.get(key) || [];
              const isToday = isSameLocalDay(cell.date, new Date());
              const showMuted = !cell.inMonth;
              return (
                <div
                  key={`${key}-${idx}`}
                  className={cn(
                    'min-h-[140px] space-y-2 border-b border-outline-variant/5 p-3',
                    idx % 7 !== 6 && 'border-r border-outline-variant/5',
                  )}
                >
                  <div
                    className={cn(
                      'relative text-left font-semibold',
                      showMuted ? 'text-stone-300' : 'text-stone-800',
                    )}
                  >
                    {isToday && cell.inMonth && (
                      <div
                        className="absolute left-0 top-0 -z-10 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10"
                        aria-hidden
                      />
                    )}
                    <span className="inline-block">{cell.date.getDate()}</span>
                    {isToday && cell.inMonth && (
                      <div className="mt-2 w-full text-center text-[9px] font-bold uppercase tracking-widest text-amber-700">
                        TODAY
                      </div>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    {dayMeetings.slice(0, 3).map(({ series, meeting }) => {
                      const variant = calendarEventVariant(series.meeting_type);
                      const t = new Date(meeting.starts_at);
                      const timeStr = t.toLocaleTimeString(undefined, {
                        hour: 'numeric',
                        minute: '2-digit',
                      });
                      const done =
                        meeting.status === 'completed' || meeting.status === 'done';
                      return (
                        <button
                          key={meeting.id}
                          type="button"
                          onClick={() => void openSession(series, meeting.id)}
                          className={cn(
                            'group w-full rounded-full border-l-[5px] px-2.5 py-1.5 text-left shadow-sm transition-colors',
                            variant === 'primary'
                              ? 'border-primary bg-primary-container/15 hover:bg-primary-container/25'
                              : 'border-tertiary bg-tertiary-container/20 hover:bg-tertiary-container/30',
                            done && 'opacity-60',
                            done &&
                              variant === 'primary' &&
                              'border-primary/50 bg-primary-container/10 hover:bg-primary-container/15',
                          )}
                        >
                          <p
                            className={cn(
                              'text-[11px] font-bold leading-tight',
                              variant === 'primary'
                                ? 'text-on-primary-container'
                                : 'text-on-tertiary-container',
                            )}
                          >
                            {series.title}
                          </p>
                          <p
                            className={cn(
                              'text-[9px]',
                              variant === 'primary' ? 'text-primary/75' : 'text-tertiary/75',
                            )}
                          >
                            {timeStr}
                          </p>
                        </button>
                      );
                    })}
                    {dayMeetings.length > 3 ? (
                      <p className="text-[10px] font-medium text-muted-foreground">
                        +{dayMeetings.length - 3} more
                      </p>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {calendarZoom === 'week' && (
        <section className="overflow-hidden rounded-3xl border border-outline-variant/10 bg-card shadow-lg">
          <div className="grid grid-cols-7 border-b border-outline-variant/10 bg-muted/40">
            {weekDays.map((d) => (
              <div
                key={d.toISOString()}
                className="border-r border-outline-variant/10 py-3 text-center last:border-r-0"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  {d.toLocaleDateString(undefined, { weekday: 'short' })}
                </div>
                <div
                  className={cn(
                    'mt-1 font-display text-lg font-semibold',
                    isSameLocalDay(d, new Date()) ? 'text-primary' : 'text-foreground',
                  )}
                >
                  {d.getDate()}
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 divide-x divide-outline-variant/10">
            {weekDays.map((d) => {
              const key = toLocalDayKey(d);
              const dm = byDay.get(key) || [];
              return (
                <div key={key} className="min-h-[200px] space-y-2 p-2">
                  {dm.map(({ series, meeting }) => (
                    <button
                      key={meeting.id}
                      type="button"
                      onClick={() => void openSession(series, meeting.id)}
                      className="w-full rounded-lg border-l-4 border-primary bg-primary-container/20 p-2 text-left text-[11px] font-bold text-on-primary-container hover:bg-primary-container/30"
                    >
                      <span className="line-clamp-2">{series.title}</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {calendarZoom === 'day' && (
        <section className="rounded-3xl border border-outline-variant/10 bg-card p-8 shadow-lg">
          <h3 className="font-display text-xl font-semibold text-headline">
            {calendarCursor.toLocaleDateString(undefined, {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </h3>
          {meetingsThisDay.length === 0 ? (
            <p className="mt-6 text-sm text-muted-foreground">No sessions on this day.</p>
          ) : (
            <ul className="mt-6 space-y-3">
              {meetingsThisDay.map(({ series, meeting }) => {
                const t = new Date(meeting.starts_at);
                return (
                  <li key={meeting.id}>
                    <button
                      type="button"
                      onClick={() => void openSession(series, meeting.id)}
                      className="flex w-full items-center justify-between rounded-xl border border-border/60 bg-muted/30 px-4 py-3 text-left transition-colors hover:bg-muted/50"
                    >
                      <span className="font-medium">{series.title}</span>
                      <span className="text-sm text-muted-foreground">
                        {t.toLocaleTimeString(undefined, {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

type FlaggedUpcoming = {
  seriesId: string;
  meetingId: string;
  seriesTitle: string;
  startsAt: string;
  noAgenda: boolean;
  lowResponseRate: boolean;
  staleActions: boolean;
  staleActionCount: number;
  accepted: number;
  total: number;
};

/** Number of days an open action item can sit without updates before being considered "stale". */
const STALE_ACTION_DAYS = 7;

function MeetingsWeeklySummaryCard({
  flagged,
  onOpenMeeting,
}: {
  flagged: FlaggedUpcoming[];
  onOpenMeeting?: (seriesId: string, meetingId: string) => void;
}) {
  const fmtDate = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <div className="group relative overflow-hidden rounded-2xl bg-tertiary/10 p-6">
      <div className="relative z-10">
        <h3 className="mb-1 font-display text-lg font-semibold text-headline">Up next</h3>
        <p className="mb-4 text-xs text-tertiary">
          {flagged.length === 0
            ? 'No upcoming meetings need attention. Your agendas and invites look set.'
            : `${flagged.length} upcoming meeting${flagged.length === 1 ? '' : 's'} need${flagged.length === 1 ? 's' : ''} attention.`}
        </p>

        {flagged.length > 0 && (
          <ul className="space-y-2">
            {flagged.slice(0, 4).map((f) => {
              const responsePct = f.total > 0 ? Math.round((f.accepted / f.total) * 100) : 0;
              return (
                <li key={f.meetingId}>
                  <button
                    type="button"
                    onClick={() => onOpenMeeting?.(f.seriesId, f.meetingId)}
                    className="group/row flex w-full flex-col gap-1 rounded-md bg-white/40 px-3 py-2 text-left transition-colors hover:bg-white/70"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-xs font-medium text-foreground">
                        {f.seriesTitle}
                      </span>
                      <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                        {fmtDate(f.startsAt)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {f.noAgenda && (
                        <span className="rounded-full border border-amber-300/60 bg-amber-100/60 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-900">
                          No agenda
                        </span>
                      )}
                      {f.lowResponseRate && (
                        <span className="rounded-full border border-rose-300/60 bg-rose-100/60 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-rose-900">
                          {responsePct}% accepted ({f.total - f.accepted}/{f.total} pending)
                        </span>
                      )}
                      {f.staleActions && (
                        <span className="rounded-full border border-stone-300/60 bg-stone-100/80 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-stone-700">
                          {f.staleActionCount} stale action{f.staleActionCount === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div
        className="pointer-events-none absolute -bottom-8 -right-8 h-24 w-24 rounded-full bg-gradient-to-tr from-primary-container to-tertiary opacity-20 blur-2xl transition-transform duration-700 group-hover:scale-150"
        aria-hidden
      />
    </div>
  );
}

/**
 * Sidebar CTA for the meetings page. Matches the dashboard's Ask Upstand block visually but the
 * copy is framed forward: prep, agendas, and what's heading at you, vs. the dashboard's
 * past-leaning "what happened" framing.
 */
function MeetingsAskUpstandCard({ flaggedCount }: { flaggedCount: number }) {
  const openAgent = useAgentChatOpener();
  const copy = flaggedCount > 0
    ? `${flaggedCount} upcoming meeting${flaggedCount === 1 ? '' : 's'} need attention - ask what to prep, what's outstanding from last time, or who hasn't replied.`
    : 'Heading into your week - ask what to prep, what\'s outstanding, or who you\'re still waiting on.';

  return (
    <div className="relative overflow-hidden rounded-2xl border border-stone-700/60 bg-gradient-to-br from-stone-700 via-stone-700 to-stone-800 p-8 text-stone-100 shadow-lg">
      <div
        className="pointer-events-none absolute -right-8 -top-12 h-40 w-40 rounded-full bg-primary/20 blur-3xl"
        aria-hidden
      />
      <div className="relative">
        <h3 className="mb-1 flex items-center gap-2 font-display text-lg font-semibold text-white">
          <Sparkles className="h-4 w-4 text-amber-300" strokeWidth={2} />
          Ask Upstand
        </h3>
        <p className="mb-6 text-sm leading-relaxed text-stone-200/95">{copy}</p>
        <Button
          type="button"
          variant="secondary"
          className="w-full bg-white/15 text-white hover:bg-white/25"
          onClick={() => openAgent()}
        >
          Ask Upstand
          <span className="ml-2 hidden rounded border border-white/20 px-1.5 py-0.5 font-mono text-[10px] text-white/70 sm:inline">
            ⌘J
          </span>
        </Button>
      </div>
    </div>
  );
}

type QuickFilterId = 'all' | 'missing_agenda' | 'open_actions';

function MeetingsQuickFiltersAside({
  quickFilter,
  setQuickFilter,
  explorerCounts,
  flaggedUpcoming,
  onOpenMeeting,
}: {
  quickFilter: QuickFilterId;
  setQuickFilter: (f: QuickFilterId) => void;
  explorerCounts: { all: number; missing_agenda: number; open_actions: number };
  flaggedUpcoming: FlaggedUpcoming[];
  onOpenMeeting?: (seriesId: string, meetingId: string) => void;
}) {
  return (
    <aside className="col-span-12 space-y-8 lg:col-span-3">
      <div className="rounded-lg border border-white/40 bg-white/40 p-6 shadow-sm backdrop-blur-xl">
        <h3 className="mb-1 font-display text-lg font-semibold text-headline">Quick filters</h3>
        <p className="mb-4 text-xs text-tertiary">Slice the list by what needs your attention.</p>
        <ul className="space-y-4">
          {(
            [
              { id: 'all' as const, label: 'All meetings' },
              { id: 'missing_agenda' as const, label: 'Missing agenda' },
              { id: 'open_actions' as const, label: 'Open actions' },
            ] as const
          ).map((f) => {
            const count = explorerCounts[f.id];
            const active = quickFilter === f.id;
            return (
              <li key={f.id}>
                <button
                  type="button"
                  onClick={() => setQuickFilter(f.id)}
                  className={cn(
                    'flex w-full items-center justify-between text-left transition-colors',
                    active ? 'text-primary' : 'text-muted-foreground hover:text-primary',
                  )}
                >
                  <span
                    className={cn(
                      'flex items-center gap-3 text-sm font-medium',
                      active ? '' : 'font-normal',
                    )}
                  >
                    <span
                      className={cn(
                        'h-1.5 w-1.5 shrink-0 rounded-full',
                        active ? 'bg-primary' : 'bg-muted-foreground/25',
                      )}
                    />
                    {f.label}
                  </span>
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-[10px] font-medium tabular-nums',
                      active ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {count}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      <MeetingsWeeklySummaryCard flagged={flaggedUpcoming} onOpenMeeting={onOpenMeeting} />
      <MeetingsAskUpstandCard flaggedCount={flaggedUpcoming.length} />
    </aside>
  );
}

function OtherMeetingsList({
  series,
  loading,
  currentWorkspaceId,
  onOpenInApp,
}: {
  series: any[];
  loading: boolean;
  /** Used to decide whether to open in-app or jump to /meet/direct/<slug>. */
  currentWorkspaceId: string | null | undefined;
  /** Same-workspace meetings open directly via this callback - no /meet/direct/ round-trip. */
  onOpenInApp: (meetingId: string) => void;
}) {
  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (series.length === 0) {
    return (
      <div className="rounded-lg border border-border/60 bg-card/30 py-16 text-center">
        <p className="text-muted-foreground">No meetings from other workspaces.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {series.map((s: any) => {
        const focused = s.next_meeting ?? s.last_meeting;
        const isUpcoming = !!s.next_meeting;
        const sameWorkspace =
          Boolean(currentWorkspaceId) && s.workspace_id === currentWorkspaceId;
        const directHref = `/meet/direct/${encodeURIComponent(s.slug)}`;
        const start = focused?.starts_at ? new Date(focused.starts_at) : null;
        const monthShort = start ? start.toLocaleDateString(undefined, { month: 'short' }) : '-';
        const dayNum = start ? start.getDate() : '-';
        const whenLine = start
          ? start.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
          : 'No scheduled time';
        return (
          <div
            key={s.series_id}
            className="group relative flex w-full min-w-0 items-center gap-4 rounded-lg bg-card px-4 py-3 shadow-sm md:gap-5"
          >
            <div className="flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-lg border border-border/40 bg-muted/40">
              <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{monthShort}</span>
              <span className="font-display text-xl font-bold leading-tight text-primary">{dayNum}</span>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-display text-lg font-semibold leading-snug text-foreground">{s.title}</h3>
              <p className="mt-1 flex flex-wrap items-center gap-1.5 text-sm leading-snug text-muted-foreground">
                <Clock className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                <span>{whenLine}</span>
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className={cn(
                'rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em]',
                isUpcoming
                  ? 'border-amber-300/40 bg-amber-100/80 text-amber-900'
                  : 'border-border/40 bg-muted/40 text-muted-foreground',
              )}>
                {isUpcoming ? 'Upcoming' : 'Past'}
              </span>
              {sameWorkspace && focused?.id ? (
                <button
                  type="button"
                  onClick={() => onOpenInApp(focused.id as string)}
                  className="rounded-md border border-border/60 bg-background px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:border-primary/30 hover:text-primary"
                >
                  View →
                </button>
              ) : (
                <a
                  href={directHref}
                  className="rounded-md border border-border/60 bg-background px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:border-primary/30 hover:text-primary"
                >
                  View →
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function Meetings({
  userProfile,
  openEnrollFromNav,
  onConsumedOpenEnrollFromNav,
  meetingsListNavTick = 0,
  onSignOut,
  onSwitchWorkspace,
  onHelp,
  onSupport,
  openSessionForMeetingId = null,
  onConsumedOpenSessionForMeetingId,
  onNavigateToDashboard,
}: {
  userProfile?: { full_name?: string; email?: string; role?: string } | null;
  /** From sidebar "Enrol meeting": switch to enrolment flow. */
  openEnrollFromNav?: boolean;
  onConsumedOpenEnrollFromNav?: () => void;
  /** Bumps when sidebar "Meetings" is clicked - return to list / leave add flow. */
  meetingsListNavTick?: number;
  onSignOut?: () => void;
  onSwitchWorkspace?: () => void;
  onHelp?: () => void;
  onSupport?: () => void;
  openSessionForMeetingId?: {
    meetingId: string;
    seq: number;
    preferredTab?: 'agenda' | 'checkins' | 'summary' | 'actions' | 'decisions' | 'people' | 'transcript' | 'chat' | 'recording';
  } | null;
  onConsumedOpenSessionForMeetingId?: () => void;
  /** Return to the dashboard - used by the meeting-detail breadcrumb when opened from the dashboard. */
  onNavigateToDashboard?: () => void;
} = {}) {
  const { workspace, membership } = useWorkspace();
  const { user } = useAuth();
  const [view, setView] = useState<'list' | 'enroll' | 'session'>('list');
  const [seriesList, setSeriesList] = useState<any[]>([]);
  const [seriesAttendeePreview, setSeriesAttendeePreview] = useState<Record<string, any[]>>({});
  /** Upcoming meetings (next 14 days) flagged for the "Up next" card: missing agenda or low RSVP rate. */
  const [flaggedUpcoming, setFlaggedUpcoming] = useState<FlaggedUpcoming[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedSeries, setSelectedSeries] = useState<any | null>(null);
  /** Where the session was opened from, so the detail breadcrumb/back returns there (DR-1). */
  const [sessionOrigin, setSessionOrigin] = useState<'meetings' | 'dashboard'>('meetings');
  const [meetingsForSeries, setMeetingsForSeries] = useState<any[]>([]);
  const [selectedMeeting, setSelectedMeeting] = useState<any | null>(null);
  const [loadingSessionMeetings, setLoadingSessionMeetings] = useState(false);
  const [sessionTabOverride, setSessionTabOverride] = useState<
    'agenda' | 'checkins' | 'summary' | 'actions' | 'decisions' | 'people' | 'transcript' | 'chat' | 'recording' | null
  >(null);
  const [listMode, setListMode] = useState<'list' | 'calendar'>('list');
  const [listResyncingSeriesId, setListResyncingSeriesId] = useState<string | null>(null);
  /** Synchronous guard: React state can lag so double-clicks still fire two syncs. */
  const listResyncMeetingLockRef = useRef(new Set<string>());
  /** Ignore stale openSession results when user opens another series quickly. */
  const sessionLoadSeqRef = useRef(0);
  const [deleteSeriesDialog, setDeleteSeriesDialog] = useState<{ id: string; title: string } | null>(
    null,
  );
  const [deletingSeriesId, setDeletingSeriesId] = useState<string | null>(null);
  const [quickFilter, setQuickFilter] = useState<QuickFilterId>('all');
  const [calendarCursor, setCalendarCursor] = useState(() => {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    return d;
  });
  const [calendarZoom, setCalendarZoom] = useState<'day' | 'week' | 'month'>('month');
  // "My meetings" = this workspace's series; "Other meetings" = invited as attendee elsewhere
  const [meetingTab, setMeetingTab] = useState<'my' | 'shared'>('my');
  const [otherSeriesList, setOtherSeriesList] = useState<any[]>([]);
  const [loadingOtherList, setLoadingOtherList] = useState(false);

  const calendarMonthYearLabel = useMemo(
    () =>
      calendarCursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
    [calendarCursor],
  );

  const navigateCalendar = useCallback((dir: -1 | 1) => {
    setCalendarCursor((prev) => {
      const d = new Date(prev);
      if (calendarZoom === 'month') {
        d.setMonth(d.getMonth() + dir);
        d.setDate(1);
      } else if (calendarZoom === 'week') {
        d.setDate(d.getDate() + dir * 7);
      } else {
        d.setDate(d.getDate() + dir);
      }
      return d;
    });
  }, [calendarZoom]);

  const loadSeries = useCallback(async () => {
    if (!workspace?.id) return;
    setLoadingList(true);
    const stopLoad = startTimer('meetings:load-series');
    const { data, error } = await supabase
      .from('meeting_series')
      .select(
        `
        *,
        meetings (
          id, starts_at, ends_at, status, calendar_event_id, video_meeting_url,
          agenda_items ( id ),
          action_items ( id, status, updated_at ),
          meeting_attendees ( user_id, email )
        )
      `,
      )
      .eq('workspace_id', workspace.id)
      .order('updated_at', { ascending: false });
    stopLoad();
    if (error) {
      toast.error('Failed to load meetings', { description: error.message });
      setSeriesList([]);
    } else {
      setSeriesList(data || []);
    }
    setLoadingList(false);
  }, [workspace?.id]);

  /** Reset session-detail state to the list. Used by both the user-driven Back action and the
   *  popstate listener; kept distinct so backToMeetingsList can decide whether to pop history. */
  const closeSessionToList = useCallback(() => {
    setLoadingSessionMeetings(false);
    setView('list');
    setSelectedSeries(null);
    setSelectedMeeting(null);
    setSessionTabOverride(null);
    void loadSeries();
  }, [loadSeries]);

  const backToMeetingsList = useCallback(() => {
    // The in-app "All meetings" link must deterministically land on the LIST - it is not a Back
    // button. It used to call history.back() whenever the current entry was a meeting detail, but
    // hopping between occurrences pushes a /meetings?meeting=<id> entry each time, so back() popped
    // to the *previous meeting* instead of the list (exactly the "weird back-mirroring" bug).
    // Normalise the URL to /meetings and reset to the list directly. The popstate listener below
    // still gives the browser Back/Forward buttons their natural stepwise behaviour through the
    // stack, independent of this link.
    if (window.location.pathname.replace(/\/$/, '') !== '/meetings' || window.location.search) {
      window.history.replaceState({}, '', '/meetings');
    }
    closeSessionToList();
  }, [closeSessionToList]);

  /** Two-way URL ↔ session-detail binding (browser back/forward behaves like in-app Back). */
  useEffect(() => {
    if (view === 'session' && selectedMeeting?.id) {
      const desired = `/meetings?meeting=${encodeURIComponent(selectedMeeting.id)}`;
      if (window.location.pathname + window.location.search !== desired) {
        window.history.pushState({ meetingDetail: true }, '', desired);
      }
    }
  }, [view, selectedMeeting?.id]);

  useEffect(() => {
    const handler = () => {
      if (window.location.pathname.replace(/\/$/, '') !== '/meetings') return;
      const meetingId = new URLSearchParams(window.location.search).get('meeting');
      if (meetingId) {
        if (view !== 'session' || selectedMeeting?.id !== meetingId) {
          void openSessionByMeetingId(meetingId);
        }
      } else if (view !== 'list') {
        closeSessionToList();
      }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, selectedMeeting?.id]);

  useEffect(() => {
    const pairs = seriesList
      .map((s) => ({ seriesId: s.id, meetingId: getSeriesPrimaryMeeting(s)?.id }))
      .filter((x) => x.seriesId && x.meetingId);
    if (!pairs.length) {
      setSeriesAttendeePreview({});
      return;
    }
    let cancelled = false;
    (async () => {
      const meetingIds = [...new Set(pairs.map((x) => x.meetingId))];
      const { byMeeting, error } = await fetchAttendeesByMeetingIds(meetingIds);
      if (cancelled || error) return;
      const bySeries: Record<string, any[]> = {};
      for (const pair of pairs) {
        bySeries[pair.seriesId] = byMeeting[pair.meetingId] || [];
      }
      if (!cancelled) setSeriesAttendeePreview(bySeries);
    })();
    return () => {
      cancelled = true;
    };
  }, [seriesList]);

  // Forward-looking "Up next" card: pull attendees for every upcoming meeting in the next 7 days
  // and flag those that have no agenda set, a low RSVP-accepted rate, or unresolved stale action
  // items in the series. Recurring series are deduped to their next upcoming meeting so the card
  // doesn't spam multiple instances of the same standup.
  useEffect(() => {
    const now = Date.now();
    const horizon = now + 7 * 24 * 60 * 60 * 1000;
    const staleCutoff = now - STALE_ACTION_DAYS * 24 * 60 * 60 * 1000;
    type UpcomingPick = {
      seriesId: string;
      seriesTitle: string;
      meetingId: string;
      startsAt: string;
      agendaCount: number;
      staleActionCount: number;
    };
    // "Up next" is a personal view - only flag MY meetings (owned or attended), not shared-workspace
    // ones visible via RLS. (Mirrors isMineSeries, inlined because that helper is defined below.)
    const myId = user?.id;
    const myEmailLc = String(user?.email || '').trim().toLowerCase();
    const isMine = (s: any): boolean =>
      Boolean(s?.owner_user_id && s.owner_user_id === myId) ||
      ((s?.meetings || []) as any[]).some((m: any) =>
        ((m?.meeting_attendees || []) as any[]).some(
          (a: any) =>
            (a?.user_id && a.user_id === myId) ||
            (a?.email && String(a.email).trim().toLowerCase() === myEmailLc),
        ),
      );
    const mineSeries = (seriesList as any[]).filter(isMine);
    // Tally stale action items per series (any open/in-progress action whose updated_at is older
    // than the cutoff - across every meeting in the series, not just the upcoming one).
    const staleBySeries = new Map<string, number>();
    for (const s of mineSeries) {
      let stale = 0;
      const ms = ((s.meetings || []) as any[]).filter(Boolean);
      for (const m of ms) {
        const actions = (m?.action_items || []) as Array<{ status?: string; updated_at?: string | null }>;
        for (const a of actions) {
          if (a?.status !== 'open' && a?.status !== 'in_progress') continue;
          const t = a?.updated_at ? new Date(a.updated_at).getTime() : NaN;
          if (Number.isFinite(t) && t < staleCutoff) stale++;
        }
      }
      if (stale > 0) staleBySeries.set(s.id, stale);
    }
    // Pick the next-upcoming meeting per series within the 7-day window.
    const nextPerSeries = new Map<string, UpcomingPick>();
    for (const s of mineSeries) {
      const ms = ((s.meetings || []) as any[]).filter(Boolean);
      for (const m of ms) {
        if (!m?.starts_at || m?.status === 'cancelled') continue;
        const t = new Date(m.starts_at).getTime();
        if (Number.isNaN(t) || t < now || t > horizon) continue;
        const existing = nextPerSeries.get(s.id);
        if (existing && new Date(existing.startsAt).getTime() <= t) continue;
        nextPerSeries.set(s.id, {
          seriesId: s.id,
          seriesTitle: s.title || 'Meeting',
          meetingId: m.id,
          startsAt: m.starts_at,
          agendaCount: ((m.agenda_items || []) as any[]).length,
          staleActionCount: staleBySeries.get(s.id) || 0,
        });
      }
    }
    const upcoming = [...nextPerSeries.values()];
    if (upcoming.length === 0) {
      setFlaggedUpcoming([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const meetingIds = upcoming.map((u) => u.meetingId);
      const { byMeeting, error } = await fetchAttendeesByMeetingIds(meetingIds);
      if (cancelled || error) return;
      const flagged: FlaggedUpcoming[] = upcoming
        .map((u) => {
          const attendees = (byMeeting[u.meetingId] || []) as Array<{ rsvp: string | null }>;
          // Don't count organizer rows toward attendance health (they're implicit yes).
          const responders = attendees.filter((a) => a.rsvp !== undefined);
          const total = responders.length;
          const accepted = responders.filter((a) => a.rsvp === 'accepted').length;
          const acceptedPct = total > 0 ? accepted / total : 1;
          const noAgenda = u.agendaCount === 0;
          // "Low response rate" = fewer than 50% accepted *and* at least 3 invitees (avoid
          // noisy flags on tiny 1:1s where one declining tanks the percentage).
          const lowResponseRate = total >= 3 && acceptedPct < 0.5;
          const staleActions = u.staleActionCount > 0;
          return {
            seriesId: u.seriesId,
            seriesTitle: u.seriesTitle,
            meetingId: u.meetingId,
            startsAt: u.startsAt,
            noAgenda,
            lowResponseRate,
            staleActions,
            staleActionCount: u.staleActionCount,
            accepted,
            total,
          };
        })
        .filter((f) => f.noAgenda || f.lowResponseRate || f.staleActions)
        .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
      if (!cancelled) setFlaggedUpcoming(flagged);
    })();
    return () => {
      cancelled = true;
    };
  }, [seriesList, user?.id, user?.email]);

  const resyncSeriesFromCalendarList = useCallback(
    async (series: any) => {
      const m = getSeriesPrimaryMeeting(series);
      if (!m?.id || !workspace?.id) return;
      if (listResyncMeetingLockRef.current.has(m.id)) return;
      listResyncMeetingLockRef.current.add(m.id);
      setListResyncingSeriesId(series.id);
      try {
        const json = await invokeEdgeFn<{
          synced?: number;
          meetings_synced?: number;
          deleted_meeting_ids?: string[];
        }>(
          'calendar-sync-meeting',
          {
            meeting_id: m.id,
            workspace_id: workspace.id,
            sync_all_in_series: true,
          },
        );
        const n = json?.synced ?? 0;
        const ms = json?.meetings_synced ?? 1;
        const deletedCount = Array.isArray(json?.deleted_meeting_ids)
          ? json.deleted_meeting_ids.length
          : 0;
        toast.success(
          ms > 1
            ? `Calendar re-synced for ${ms} meetings (times, details, invitees)`
            : `Calendar re-synced - ${n} invitee${n === 1 ? '' : 's'} updated`,
        );
        if (deletedCount > 0) {
          toast.info(
            `${deletedCount} meeting${deletedCount === 1 ? '' : 's'} no longer in your calendar - open ${deletedCount === 1 ? 'it' : 'them'} to keep or cancel`,
          );
        }
        await loadSeries();
      } catch (e: any) {
        toast.error(e?.message || 'Re-sync failed');
      } finally {
        listResyncMeetingLockRef.current.delete(m.id);
        setListResyncingSeriesId(null);
      }
    },
    [workspace?.id, loadSeries],
  );

  const deleteSeriesConfirmed = useCallback(async () => {
    if (!deleteSeriesDialog?.id) return;
    const id = deleteSeriesDialog.id;
    setDeletingSeriesId(id);
    try {
      // Cancel any scheduled Recall bots first - the FK cascade will wipe meeting_transcripts
      // moments later, so this is our last chance to tell Recall to terminate the bots.
      // Failures here shouldn't block the delete; they're audited in meeting_transcript_runs.
      if (workspace?.id) {
        try {
          await invokeEdgeFn('transcript-orchestrator', {
            action: 'cancel_bot',
            series_id: id,
            workspace_id: workspace.id,
          });
        } catch (cancelErr) {
          console.error('Failed to cancel bots before deleting series', cancelErr);
        }
      }
      // `.select()` so we can tell a real delete from an RLS no-op: deletion is owner-only, so a
      // non-owner's delete affects 0 rows and returns NO error - without this it would falsely report
      // "Meeting deleted". Surface the permission failure instead.
      const { data: deletedRows, error } = await supabase
        .from('meeting_series')
        .delete()
        .eq('id', id)
        .select('id');
      if (error) throw error;
      if (!deletedRows || deletedRows.length === 0) {
        toast.error('Only the meeting owner can delete this meeting.');
        setDeleteSeriesDialog(null);
        return;
      }
      toast.success('Meeting deleted');
      if (selectedSeries?.id === id) {
        setLoadingSessionMeetings(false);
        setView('list');
        setSelectedSeries(null);
        setSelectedMeeting(null);
        setSessionTabOverride(null);
      }
      setDeleteSeriesDialog(null);
      await loadSeries();
    } catch (e: any) {
      toast.error(e?.message || 'Could not delete meeting');
    } finally {
      setDeletingSeriesId(null);
    }
  }, [deleteSeriesDialog, selectedSeries?.id, loadSeries, workspace?.id]);

  useEffect(() => {
    loadSeries();
  }, [loadSeries]);

  // Load meetings from other workspaces where the current user is just an attendee
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoadingOtherList(true);
    (async () => {
      const { data, error } = await timed('meetings:attendee-list', () => supabase.rpc('attendee_meetings_for_self'));
      if (cancelled) return;
      if (!error) {
        setOtherSeriesList(((data as any[]) ?? []).filter((s) => !s.is_organizer));
      }
      setLoadingOtherList(false);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    if (!openEnrollFromNav) return;
    setView('enroll');
    setSelectedSeries(null);
    setSelectedMeeting(null);
    setSessionTabOverride(null);
    setLoadingSessionMeetings(false);
    onConsumedOpenEnrollFromNav?.();
  }, [openEnrollFromNav]);

  useEffect(() => {
    if (!meetingsListNavTick) return;
    setView('list');
    setSelectedSeries(null);
    setSelectedMeeting(null);
    setSessionTabOverride(null);
    setLoadingSessionMeetings(false);
  }, [meetingsListNavTick]);

  /** A series is "mine" if I own it or attend any of its meetings; otherwise (visible only because
   *  the workspace is Shared) it belongs in the "Shared meetings" tab. */
  const currentUserEmail = String(user?.email || '').trim().toLowerCase();
  const isMineSeries = useCallback(
    (s: any): boolean => {
      if (!s) return false;
      if (s.owner_user_id && user?.id && s.owner_user_id === user.id) return true;
      return (s.meetings || []).some((m: any) =>
        (m.meeting_attendees || []).some(
          (a: any) =>
            (a.user_id && a.user_id === user?.id) ||
            (a.email && String(a.email).trim().toLowerCase() === currentUserEmail),
        ),
      );
    },
    [user?.id, currentUserEmail],
  );
  const mySeries = useMemo(() => seriesList.filter((s) => isMineSeries(s)), [seriesList, isMineSeries]);
  const sharedSeriesLocal = useMemo(
    () => seriesList.filter((s) => !isMineSeries(s)),
    [seriesList, isMineSeries],
  );
  /** The list feeding the visible tab: My meetings (owned/attended) vs Shared (visible-not-mine). */
  const activeSeriesBase = useMemo(
    () => (meetingTab === 'shared' ? sharedSeriesLocal : mySeries),
    [meetingTab, sharedSeriesLocal, mySeries],
  );
  /** "Shared" = visible-but-not-mine in this workspace + meetings shared from other workspaces. */
  const sharedTabCount = sharedSeriesLocal.length + otherSeriesList.length;
  const showMeetingTabs = sharedTabCount > 0 || loadingOtherList;

  /** Upcoming / dated series first; drafts with no meeting times last. */
  const seriesListSorted = useMemo(() => {
    const sortKey = (s: any) => {
      const ms = (s.meetings || []).filter(Boolean);
      const now = Date.now();
      const future = ms
        .filter((m: any) => m.starts_at && new Date(m.starts_at).getTime() >= now)
        .sort(
          (a: any, b: any) =>
            new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
        );
      if (future.length) {
        return { tier: 0 as const, t: new Date(future[0].starts_at).getTime() };
      }
      const anyPast = ms
        .filter((m: any) => m.starts_at)
        .sort(
          (a: any, b: any) =>
            new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime(),
        );
      if (anyPast.length) {
        return { tier: 1 as const, t: new Date(anyPast[0].starts_at).getTime() };
      }
      return { tier: 2 as const, t: new Date(s.updated_at || s.created_at || 0).getTime() };
    };
    return [...activeSeriesBase].sort((a, b) => {
      const ka = sortKey(a);
      const kb = sortKey(b);
      if (ka.tier !== kb.tier) return ka.tier - kb.tier;
      if (ka.tier === 0) return ka.t - kb.t;
      return kb.t - ka.t;
    });
  }, [activeSeriesBase]);

  const explorerCounts = useMemo(() => {
    const now = Date.now();
    let missingAgenda = 0;
    let openActions = 0;
    for (const s of activeSeriesBase) {
      if (seriesNeedsAgenda(s, now)) missingAgenda++;
      if (seriesHasOpenActions(s)) openActions++;
    }
    return { all: activeSeriesBase.length, missing_agenda: missingAgenda, open_actions: openActions };
  }, [activeSeriesBase]);

  const listForExplorer = useMemo(() => {
    const now = Date.now();
    // Drop series whose occurrences are ALL cancelled (e.g. a fully-removed calendar series):
    // getSeriesPrimaryMeeting returns null for those, so they shouldn't clutter the active list.
    const base = seriesListSorted.filter((s) => getSeriesPrimaryMeeting(s) != null);
    if (quickFilter === 'all') return base;
    if (quickFilter === 'missing_agenda') {
      return base.filter((s) => seriesNeedsAgenda(s, now));
    }
    return base.filter((s) => seriesHasOpenActions(s));
  }, [seriesListSorted, quickFilter]);

  const openSession = useCallback(
    async (
      series: any,
      meetingId?: string,
      preferredTab?: 'agenda' | 'checkins' | 'summary' | 'actions' | 'decisions' | 'people' | 'transcript' | 'chat' | 'recording',
      origin: 'meetings' | 'dashboard' = 'meetings',
    ) => {
      const seq = ++sessionLoadSeqRef.current;
      setLoadingSessionMeetings(true);
      setSelectedSeries(series);
      setSessionOrigin(origin);
      setView('session');      track('meeting_viewed');
      setSessionTabOverride(preferredTab ?? null);
      setSelectedMeeting(null);
      setMeetingsForSeries([]);
      try {
        const { data: ms, error } = await supabase
          .from('meetings')
          .select('*')
          .eq('series_id', series.id)
          .order('starts_at', { ascending: true });
        if (sessionLoadSeqRef.current !== seq) return;
        if (error) throw error;
        const rows = ms || [];
        setMeetingsForSeries(rows);
        if (meetingId) {
          setSelectedMeeting(rows.find((m) => m.id === meetingId) || rows[0] || null);
        } else {
          // Default focus: today's meeting stays focused for the REST of the day after it ends
          // (people come back for notes/actions), only rolling to the next occurrence from the
          // next day. If a later occurrence still happens today (e.g. twice-daily), prefer it.
          const now = new Date();
          const todayKey = now.toDateString();
          const upcoming = rows.find(
            (m) => m.starts_at && new Date(m.starts_at).getTime() >= now.getTime(),
          );
          const upcomingIsToday = Boolean(
            upcoming?.starts_at && new Date(upcoming.starts_at).toDateString() === todayKey,
          );
          const todaysPast = [...rows]
            .reverse()
            .find((m) => {
              if (!m.starts_at) return false;
              const d = new Date(m.starts_at);
              return d.getTime() < now.getTime() && d.toDateString() === todayKey;
            });
          setSelectedMeeting(
            (upcomingIsToday ? upcoming : todaysPast || upcoming) || rows[rows.length - 1] || null,
          );
        }
      } catch (e: any) {
        if (sessionLoadSeqRef.current !== seq) return;
        toast.error(e?.message || 'Could not load meetings for this series.');
        setView('list');
        setSelectedSeries(null);
        setSelectedMeeting(null);
        setMeetingsForSeries([]);
      } finally {
        if (sessionLoadSeqRef.current === seq) {
          setLoadingSessionMeetings(false);
        }
      }
    },
    [],
  );

  const openSessionByMeetingId = useCallback(
    async (
      meetingId: string,
      preferredTab?:
        | 'agenda' | 'checkins' | 'summary' | 'actions' | 'decisions' | 'people' | 'transcript' | 'chat' | 'recording'
        | null,
    ) => {
      const { data: row, error: rowErr } = await supabase
        .from('meetings')
        .select('series_id')
        .eq('id', meetingId)
        .maybeSingle();
      if (rowErr || !row?.series_id) {
        toast.error('Could not open this meeting.');
        return false;
      }
      const { data: series, error: sErr } = await supabase
        .from('meeting_series')
        .select(`
          *,
          meetings ( id, starts_at, ends_at, status, calendar_event_id, video_meeting_url )
        `)
        .eq('id', row.series_id)
        .single();
      if (sErr || !series) {
        toast.error('Could not load meeting series.');
        return false;
      }
      await openSession(series, meetingId, preferredTab ?? undefined, 'dashboard');
      return true;
    },
    [openSession],
  );

  useEffect(() => {
    const req = openSessionForMeetingId;
    if (!req?.meetingId || !workspace?.id) return;
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await openSessionByMeetingId(req.meetingId, req.preferredTab);
      if (!cancelled) onConsumedOpenSessionForMeetingId?.();
    })();
    return () => {
      cancelled = true;
    };
  }, [
    openSessionForMeetingId?.meetingId,
    openSessionForMeetingId?.seq,
    workspace?.id,
    openSessionByMeetingId,
    onConsumedOpenSessionForMeetingId,
  ]);

  const inboundDomain =
    // Reuses the existing Resend domain (already configured for outbound `noreply@…`), so inbound
    // can live on the free tier - MX records are additive and don't affect outbound SPF/DKIM.
    import.meta.env.VITE_CALENDAR_INBOUND_DOMAIN || 'updates.upstand.ai';
  const inboundAddress = workspace?.inbound_key
    ? `meet+${workspace.inbound_key}@${inboundDomain}`
    : '';

  if (view === 'enroll' && workspace && user) {
    return (
      <div className="-m-4 flex min-h-[calc(100dvh-2rem)] flex-1 flex-col">
        <MeetingEnrolWizard
          workspace={workspace}
          userId={user.id}
          inboundAddress={inboundAddress}
          userProfile={userProfile}
          onSignOut={onSignOut}
          onSwitchWorkspace={onSwitchWorkspace}
          onHelp={onHelp}
          onCancel={() => {
            setView('list');
            loadSeries();
          }}
          onSupport={onSupport}
          onCreated={async (seriesId) => {
            const { data: s } = await supabase
              .from('meeting_series')
              .select(
                `*, meetings ( id, starts_at, ends_at, status, calendar_event_id, video_meeting_url )`,
              )
              .eq('id', seriesId)
              .single();
            if (s) {
              setView('list');
              loadSeries();
              openSession(s);
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className="relative min-h-full pb-3">
      <AppTopBar
        userProfile={userProfile}
        workspaceName={workspace?.name}
        onHelp={onHelp}
        onSignOut={onSignOut}
        onSwitchWorkspace={onSwitchWorkspace}
        breadcrumb={
          view === 'session' && selectedSeries ? (
            <nav
              aria-label="Breadcrumb"
              className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground"
            >
              <button
                type="button"
                onClick={() => backToMeetingsList()}
                className="inline-flex shrink-0 items-center gap-1 rounded transition-colors hover:text-foreground"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Meetings
              </button>
              <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-50" aria-hidden />
              <span className="truncate font-medium text-foreground">{selectedSeries.title}</span>
            </nav>
          ) : undefined
        }
      />
      <div className="mx-auto max-w-[1400px] space-y-5 px-2 sm:px-4 lg:px-6">
      {view === 'list' && (
        <>
          <section className="mb-6 space-y-4 sm:mb-8">
            {listMode === 'list' ? (
              <div className="space-y-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <h2 className="font-display text-2xl font-semibold leading-tight tracking-tight text-headline md:text-3xl">
                      Meetings
                    </h2>
                    <p className="mt-1.5 max-w-2xl font-sans text-sm font-light leading-relaxed text-muted-foreground">
                      Manage your digital presence and collaborative flow.
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={() => {
                      setView('enroll');
                      setSelectedSeries(null);
                    }}
                    className="h-auto shrink-0 rounded-lg bg-gradient-to-br from-primary to-primary-container px-6 py-4 text-base font-medium text-primary-foreground shadow-lg shadow-primary/20 transition-transform hover:scale-[1.01]"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Enrol meeting
                  </Button>
                </div>
                <div className="border-t border-border/40 pt-4" aria-hidden />
                <div className="rounded-lg border border-outline-variant/15 bg-card px-4 py-3 shadow-sm">
                  <div className="flex flex-wrap items-center gap-3">
                    {showMeetingTabs && (
                      <div className="flex gap-1 rounded-lg bg-muted/60 p-1.5">
                        <button
                          type="button"
                          onClick={() => setMeetingTab('my')}
                          className={cn(
                            'flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-medium transition-colors',
                            meetingTab === 'my'
                              ? 'bg-background text-primary shadow-sm'
                              : 'text-muted-foreground hover:text-foreground',
                          )}
                        >
                          My meetings
                        </button>
                        <button
                          type="button"
                          onClick={() => setMeetingTab('shared')}
                          className={cn(
                            'flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-medium transition-colors',
                            meetingTab === 'shared'
                              ? 'bg-background text-primary shadow-sm'
                              : 'text-muted-foreground hover:text-foreground',
                          )}
                        >
                          Shared meetings
                          {sharedTabCount > 0 && (
                            <span className={cn(
                              'rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
                              meetingTab === 'shared' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
                            )}>
                              {sharedTabCount}
                            </span>
                          )}
                        </button>
                      </div>
                    )}
                    <div className="flex gap-1 rounded-lg bg-muted/60 p-1.5 sm:ml-auto">
                      <button
                        type="button"
                        onClick={() => setListMode('list')}
                        className={cn(
                          'flex items-center justify-center gap-2 rounded-md px-5 py-2.5 text-sm font-medium transition-colors',
                          listMode === 'list'
                            ? 'bg-background text-primary shadow-sm'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        <List className="h-4 w-4 shrink-0" strokeWidth={listMode === 'list' ? 2.5 : 2} />
                        List view
                      </button>
                      <button
                        type="button"
                        onClick={() => setListMode('calendar')}
                        className={cn(
                          'flex items-center justify-center gap-2 rounded-md px-5 py-2.5 text-sm font-medium transition-colors',
                          listMode === 'calendar'
                            ? 'bg-background text-primary shadow-sm'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        <CalendarDays className="h-4 w-4 shrink-0" />
                        Calendar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <h2 className="font-display text-2xl font-semibold leading-tight tracking-tight text-headline md:text-3xl">
                      Meetings
                    </h2>
                    <p className="mt-1.5 max-w-2xl font-sans text-sm font-light leading-relaxed text-muted-foreground">
                      Manage your digital presence and collaborative flow.
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={() => {
                      setView('enroll');
                      setSelectedSeries(null);
                    }}
                    className="h-auto shrink-0 rounded-lg bg-gradient-to-br from-primary to-primary-container px-6 py-4 text-base font-medium text-primary-foreground shadow-lg shadow-primary/20 transition-transform hover:scale-[1.01]"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Enrol meeting
                  </Button>
                </div>
                <div className="border-t border-border/40 pt-4" aria-hidden />
                <div className="rounded-lg border border-outline-variant/15 bg-card px-4 py-3 shadow-sm">
                  <div className="flex flex-wrap items-center gap-3">
                    {showMeetingTabs && (
                      <div className="flex gap-1 rounded-lg bg-muted/60 p-1.5">
                        <button
                          type="button"
                          onClick={() => setMeetingTab('my')}
                          className={cn(
                            'flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-medium transition-colors',
                            meetingTab === 'my'
                              ? 'bg-background text-primary shadow-sm'
                              : 'text-muted-foreground hover:text-foreground',
                          )}
                        >
                          My meetings
                        </button>
                        <button
                          type="button"
                          onClick={() => setMeetingTab('shared')}
                          className={cn(
                            'flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-medium transition-colors',
                            meetingTab === 'shared'
                              ? 'bg-background text-primary shadow-sm'
                              : 'text-muted-foreground hover:text-foreground',
                          )}
                        >
                          Shared meetings
                          {sharedTabCount > 0 && (
                            <span className={cn(
                              'rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
                              meetingTab === 'shared' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
                            )}>
                              {sharedTabCount}
                            </span>
                          )}
                        </button>
                      </div>
                    )}
                    <div className="flex gap-1 rounded-lg bg-muted/60 p-1.5 sm:ml-auto">
                      <button
                        type="button"
                        onClick={() => setListMode('list')}
                        className={cn(
                          'flex items-center justify-center gap-2 rounded-md px-5 py-2.5 text-sm font-medium transition-colors',
                          listMode === 'list'
                            ? 'bg-background text-primary shadow-sm'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        <List className="h-4 w-4 shrink-0" strokeWidth={listMode === 'list' ? 2.5 : 2} />
                        List view
                      </button>
                      <button
                        type="button"
                        onClick={() => setListMode('calendar')}
                        className={cn(
                          'flex items-center justify-center gap-2 rounded-md px-5 py-2.5 text-sm font-medium transition-colors',
                          listMode === 'calendar'
                            ? 'bg-background text-primary shadow-sm'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        <CalendarDays className="h-4 w-4 shrink-0" />
                        Calendar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {listMode === 'list' ? (
          <div className="grid grid-cols-12 gap-6 lg:items-start">
            <div className="col-span-12 space-y-4 lg:col-span-9">
              {loadingList ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  {listForExplorer.length === 0 ? (
                    // On the Shared tab, cross-workspace meetings render in their own section below,
                    // so suppress the empty box when those exist.
                    meetingTab === 'shared' && otherSeriesList.length > 0 ? null : (
                    <div className="rounded-lg border border-border/60 bg-card/30 py-16 text-center">
                      <p className="text-muted-foreground">
                        {meetingTab === 'shared'
                          ? 'No shared meetings.'
                          : seriesList.length === 0
                            ? 'No meetings yet. Enrol your first session below.'
                            : 'No meetings match this filter.'}
                      </p>
                    </div>
                    )
                  ) : (
                    <div className="space-y-2">
                      {(() => {
                        const __now = Date.now();
                        let __lastGroup: 'upcoming' | 'past' | null = null;
                        return listForExplorer.flatMap((s) => {
                          const __nextForGroup = getSeriesPrimaryMeeting(s);
                          const __isUpcoming = Boolean(
                            __nextForGroup?.starts_at &&
                              new Date(__nextForGroup.starts_at).getTime() >= __now,
                          );
                          const __group: 'upcoming' | 'past' = __isUpcoming ? 'upcoming' : 'past';
                          const __showDivider = __lastGroup === 'upcoming' && __group === 'past';
                          __lastGroup = __group;

                          const renderRow = (() => {
                        const nextM = getSeriesPrimaryMeeting(s);
                        const cadencePill = s.is_recurring_series
                          ? {
                              label: 'Recurring',
                              className:
                                'border-tertiary/25 bg-tertiary/15 text-tertiary',
                            }
                          : {
                              label: 'One-off',
                              className:
                                'border-amber-300/40 bg-amber-100/80 text-amber-900',
                            };
                        const provider =
                          s.calendar_provider === 'google' || s.calendar_provider === 'microsoft'
                            ? (s.calendar_provider as 'google' | 'microsoft')
                            : null;
                        const hasCalendarLink = Boolean(provider && nextM?.calendar_event_id);
                        const start = nextM?.starts_at ? new Date(nextM.starts_at) : null;
                        const whenLine = formatExplorerSeriesWhenLine(s, nextM);

                        const monthShort = start
                          ? start.toLocaleDateString(undefined, { month: 'short' })
                          : '-';
                        const dayNum = start ? start.getDate() : '-';

                        return (
                          <div
                            key={s.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => openSession(s)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openSession(s);
                              }
                            }}
                            className="group relative flex w-full min-w-0 cursor-pointer items-center gap-4 rounded-lg bg-card px-4 py-3 shadow-sm transition-all duration-300 hover:shadow-xl hover:shadow-primary/5 md:gap-5"
                          >
                            <div className="flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-lg border border-border/40 bg-muted/40">
                              <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
                                {monthShort}
                              </span>
                              <span className="font-display text-xl font-bold leading-tight text-primary">{dayNum}</span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="font-display text-lg font-semibold leading-snug text-foreground">
                                {s.title}
                              </h3>
                              <p className="mt-1 flex flex-wrap items-center gap-1.5 text-sm leading-snug text-muted-foreground">
                                <Clock className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                                <span>{whenLine}</span>
                              </p>
                            </div>
                            <div className="hidden shrink-0 flex-col items-end gap-2 sm:flex">
                              <AttendeeBubbleStack attendees={seriesAttendeePreview[s.id] || []} maxVisible={2} />
                              <div
                                className={cn(
                                  'rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em]',
                                  cadencePill.className,
                                )}
                              >
                                {cadencePill.label}
                              </div>
                              {String(s.capture_method || 'bot') === 'desktop' && (
                                <div className="flex items-center gap-1 rounded border border-border/60 bg-muted/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
                                  <Monitor className="h-2.5 w-2.5" />
                                  Desktop App
                                </div>
                              )}
                            </div>
                            <div className="ml-auto flex shrink-0 items-center gap-1.5 pl-2">
                              {provider ? (
                                <div className="flex shrink-0">
                                  <CalendarProviderIcon
                                    provider={provider}
                                    connected={hasCalendarLink}
                                  />
                                </div>
                              ) : null}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button
                                    type="button"
                                    className="shrink-0 rounded-md p-2 text-muted-foreground transition-colors hover:text-primary disabled:opacity-50"
                                    aria-label="Meeting actions"
                                    disabled={
                                      listResyncingSeriesId === s.id || deletingSeriesId === s.id
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {listResyncingSeriesId === s.id || deletingSeriesId === s.id ? (
                                      <Loader2 className="h-5 w-5 animate-spin" />
                                    ) : (
                                      <MoreVertical className="h-5 w-5" />
                                    )}
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                  {/* Re-sync (recreates the meeting from the calendar) is a management
                                      action - owner-only, matching the backend. In a Shared workspace
                                      you can SEE others' meetings but must not re-sync them. */}
                                  {hasCalendarLink && nextM?.id && s.owner_user_id === user?.id ? (
                                    <DropdownMenuItem
                                      disabled={listResyncingSeriesId === s.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        void resyncSeriesFromCalendarList(s);
                                      }}
                                    >
                                      Re-sync
                                    </DropdownMenuItem>
                                  ) : null}
                                  {/* Delete is owner-only (matches the RLS) - don't show it to others,
                                      where it would no-op and previously toasted a false success. */}
                                  {s.owner_user_id === user?.id && (
                                    <DropdownMenuItem
                                      variant="destructive"
                                      disabled={deletingSeriesId === s.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteSeriesDialog({
                                          id: s.id,
                                          title: (s.title || 'Untitled meeting').trim() || 'Untitled meeting',
                                        });
                                      }}
                                    >
                                      Delete meeting…
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        );
                          })();
                          return __showDivider
                            ? [
                                <div
                                  key={`__past-divider-${s.id}`}
                                  className="flex items-center gap-3 px-1 pt-4 pb-1"
                                  aria-hidden
                                >
                                  <div className="h-px flex-1 bg-border/60" />
                                  <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                                    Past meetings
                                  </span>
                                  <div className="h-px flex-1 bg-border/60" />
                                </div>,
                                renderRow,
                              ]
                            : [renderRow];
                        });
                      })()}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      setView('enroll');
                      setSelectedSeries(null);
                    }}
                    className="group flex w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-border/50 py-7 text-center transition-colors hover:border-primary/30"
                  >
                    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted transition-colors group-hover:bg-primary/10">
                      <Plus className="h-5 w-5 text-muted-foreground transition-colors group-hover:text-primary" />
                    </div>
                    <p className="font-medium text-muted-foreground">
                      Click to enrol a new meeting into Upstand
                    </p>
                    <p className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground/70">
                      Connect Google Calendar or Outlook
                    </p>
                  </button>
                </>
              )}
            </div>

            <MeetingsQuickFiltersAside
              quickFilter={quickFilter}
              setQuickFilter={setQuickFilter}
              explorerCounts={explorerCounts}
              flaggedUpcoming={flaggedUpcoming}
              onOpenMeeting={(seriesId) => {
                const target = seriesList.find((s) => s.id === seriesId);
                if (target) void openSession(target);
              }}
            />
          </div>
          ) : (
            <div className="grid grid-cols-12 gap-8 lg:items-start">
              <div className="col-span-12 min-w-0 space-y-6 lg:col-span-9">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="shrink-0 font-medium text-muted-foreground">{calendarMonthYearLabel}</p>
                  <div className="flex flex-wrap items-center justify-end gap-3 md:gap-4">
                    <div
                      className="inline-flex rounded-2xl bg-muted/60 p-1"
                      role="tablist"
                      aria-label="Calendar zoom"
                    >
                      {(['day', 'week', 'month'] as const).map((z) => (
                        <button
                          key={z}
                          type="button"
                          role="tab"
                          aria-selected={calendarZoom === z}
                          onClick={() => setCalendarZoom(z)}
                          className={cn(
                            'rounded-xl px-6 py-2 text-xs font-bold transition-colors',
                            calendarZoom === z
                              ? 'bg-background text-primary shadow-sm ring-1 ring-black/5'
                              : 'text-muted-foreground hover:text-primary',
                          )}
                        >
                          {z === 'day' ? 'Day' : z === 'week' ? 'Week' : 'Month'}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 rounded-full bg-muted/80 hover:bg-muted"
                        onClick={() => navigateCalendar(-1)}
                        aria-label="Previous"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 rounded-full bg-muted/80 hover:bg-muted"
                        onClick={() => navigateCalendar(1)}
                        aria-label="Next"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                </div>
                <MeetingsCalendarSection
                  seriesList={listForExplorer}
                  loadingList={loadingList}
                  calendarCursor={calendarCursor}
                  calendarZoom={calendarZoom}
                  openSession={openSession}
                />
              </div>
              <MeetingsQuickFiltersAside
                quickFilter={quickFilter}
                setQuickFilter={setQuickFilter}
                explorerCounts={explorerCounts}
                flaggedUpcoming={flaggedUpcoming}
                onOpenMeeting={(seriesId) => {
                  const target = seriesList.find((s) => s.id === seriesId);
                  if (target) void openSession(target);
                }}
              />
            </div>
          )}

          {/* Cross-workspace meetings you were invited to - shown under the Shared tab. */}
          {meetingTab === 'shared' && (otherSeriesList.length > 0 || loadingOtherList) && (
            <section className="mt-8">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                From other workspaces
              </h3>
              <OtherMeetingsList
                series={otherSeriesList}
                loading={loadingOtherList}
                currentWorkspaceId={workspace?.id || null}
                onOpenInApp={(meetingId) => {
                  setMeetingTab('my');
                  void openSessionByMeetingId(meetingId);
                }}
              />
            </section>
          )}
        </>
      )}

      {view === 'session' && selectedSeries && loadingSessionMeetings && (
        <div
          className="flex min-h-[min(480px,70vh)] flex-col items-center justify-center gap-4 px-4 py-16"
          role="status"
          aria-busy="true"
          aria-live="polite"
        >
          <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" strokeWidth={1.75} />
          <p className="text-sm font-medium text-muted-foreground">Loading sessions…</p>
          <Button type="button" variant="outline" size="sm" onClick={() => backToMeetingsList()}>
            Back to meetings
          </Button>
        </div>
      )}

      {view === 'session' &&
        selectedSeries &&
        !loadingSessionMeetings &&
        selectedMeeting &&
        user &&
        workspace && (
        <MeetingSessionView
          series={selectedSeries}
          meeting={selectedMeeting}
          meetings={meetingsForSeries}
          workspace={workspace}
          userId={user.id}
          membership={membership}
          inboundAddress={inboundAddress}
          preferredSessionTab={sessionTabOverride}
          onConsumePreferredSessionTab={() => setSessionTabOverride(null)}
          onSelectMeeting={(m) => setSelectedMeeting(m)}
          onRefreshSeries={async () => {
            const { data: s } = await supabase
              .from('meeting_series')
              .select('*')
              .eq('id', selectedSeries.id)
              .single();
            if (s) setSelectedSeries(s);
            const { data: ms } = await supabase
              .from('meetings')
              .select('*')
              .eq('series_id', selectedSeries.id)
              .order('starts_at', { ascending: true });
            setMeetingsForSeries(ms || []);
          }}
        />
      )}

      {view === 'session' && selectedSeries && !loadingSessionMeetings && !selectedMeeting && (
        <div className="py-8 text-center">
          <p className="mb-4 text-muted-foreground">No session instance found for this series.</p>
          <Button variant="outline" onClick={() => backToMeetingsList()}>
            Back
          </Button>
        </div>
      )}

      <Dialog
        open={deleteSeriesDialog != null}
        onOpenChange={(open) => {
          if (!open) setDeleteSeriesDialog(null);
        }}
      >
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Delete meeting?</DialogTitle>
            <DialogDescription>
              This permanently removes &ldquo;{deleteSeriesDialog?.title}&rdquo; and all related
              sessions and data from Upstand. You can enrol the same calendar event again afterwards.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteSeriesDialog(null)}
              disabled={deletingSeriesId != null}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void deleteSeriesConfirmed()}
              disabled={deletingSeriesId != null}
            >
              {deletingSeriesId != null ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting…
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}

const MEETING_TYPE_DEFAULT_DESCRIPTION: Record<string, string> = {
  team: 'Cross-functional alignment on roadmap execution, design reviews, and engineering velocity for the current sprint cycle.',
  standup: 'Short daily alignment on progress, blockers, and commitments across the team.',
  one_on_one: 'A private space for coaching, feedback, and career alignment.',
  retrospective: 'Reflect on outcomes, celebrate wins, and capture improvements for the next cycle.',
  project_review: 'Review milestones, risks, and decisions across active initiatives.',
};

function meetingTypeDefaultDescription(meetingType: string | undefined): string {
  const t = meetingType || 'team';
  return MEETING_TYPE_DEFAULT_DESCRIPTION[t] || MEETING_TYPE_DEFAULT_DESCRIPTION.team;
}

function seriesUsesCalendarSource(series: any): boolean {
  const p = series?.calendar_provider;
  if (p === 'google' || p === 'microsoft') return true;
  return series?.enrolment_source === 'oauth_picker';
}

/**
 * Quick-filter predicate: true when the series has an upcoming meeting whose agenda is still empty.
 * Past-only series are excluded - agendas can't be set retroactively.
 */
function seriesNeedsAgenda(series: any, nowMs: number): boolean {
  const meetings = ((series?.meetings || []) as any[]).filter(Boolean);
  if (meetings.length === 0) return false;
  const upcoming = meetings
    .filter((m: any) => m?.starts_at && new Date(m.starts_at).getTime() >= nowMs)
    .sort(
      (a: any, b: any) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
    )[0];
  if (!upcoming) return false;
  const agenda = (upcoming.agenda_items || []) as any[];
  return agenda.length === 0;
}

/** Quick-filter predicate: true when ANY meeting in the series has an open or in-progress action item. */
function seriesHasOpenActions(series: any): boolean {
  const meetings = ((series?.meetings || []) as any[]).filter(Boolean);
  for (const m of meetings) {
    const actions = (m?.action_items || []) as Array<{ status?: string }>;
    if (actions.some((a) => a?.status === 'open' || a?.status === 'in_progress')) return true;
  }
  return false;
}

/** Display copy for the series blurb. Calendar-linked series skip generic meeting-type filler. */
function seriesDescription(series: any): string {
  const custom = series?.description;
  if (typeof custom === 'string' && custom.trim()) return custom.trim();
  if (seriesUsesCalendarSource(series)) return '';
  return meetingTypeDefaultDescription(series?.meeting_type);
}

function pastSessionRowTitle(m: any, index: number): string {
  const explicit = typeof m?.title === 'string' ? m.title.trim() : '';
  if (explicit) return explicit;
  return '';
}

function formatSessionTimeRange(m: any): string {
  const hs = m?.starts_at ? new Date(m.starts_at) : null;
  const he = m?.ends_at ? new Date(m.ends_at) : null;
  if (!hs || Number.isNaN(hs.getTime())) return 'Time TBD';
  const opt: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };
  if (he && !Number.isNaN(he.getTime())) {
    return `${hs.toLocaleTimeString(undefined, opt)} - ${he.toLocaleTimeString(undefined, opt)}`;
  }
  return hs.toLocaleTimeString(undefined, opt);
}

/** Session stack: discrete steps + 600ms spring-like easing (aligned with session timeline page). */
const SESSION_TIMELINE_MS = 600;
const SESSION_TIMELINE_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
const sessionTimelineTransitionStyle = {
  transitionProperty: 'box-shadow, opacity, background-color, border-color, transform',
  transitionDuration: `${SESSION_TIMELINE_MS}ms`,
  transitionTimingFunction: SESSION_TIMELINE_EASE,
} as const;

function startOfLocalDay(d: Date): number {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

/** `sorted` = newest first (starts_at desc). Default open = minimum |starts_at − now|. */
function pickClosestToNowMeetingId(sorted: { id: string; starts_at?: string | null }[]): string | null {
  if (!sorted.length) return null;
  const now = Date.now();
  let bestId: string | null = null;
  let bestDist = Infinity;
  for (const m of sorted) {
    if (!m.starts_at) continue;
    const t = new Date(m.starts_at).getTime();
    if (Number.isNaN(t)) continue;
    const d = Math.abs(t - now);
    if (d < bestDist) {
      bestDist = d;
      bestId = m.id;
    }
  }
  return bestId ?? sorted[0]!.id;
}

/** Compare scheduled local calendar day to “today” for the session header. */
function formatMeetingDateVersusToday(starts_at: string | null | undefined): string {
  if (!starts_at) return '';
  const d = new Date(starts_at);
  if (Number.isNaN(d.getTime())) return '';
  const todayStart = startOfLocalDay(new Date());
  const dayStart = startOfLocalDay(d);
  const diffDays = Math.round((dayStart - todayStart) / 86400000);
  if (diffDays === 0) return 'Scheduled today';
  if (diffDays === 1) return 'In 1 day';
  if (diffDays > 1) return `In ${diffDays} days`;
  if (diffDays === -1) return 'Yesterday';
  if (diffDays < -1) return `${-diffDays} days ago`;
  return '';
}

function attendeeDisplayLabel(
  a: any,
  members: { user_id: string; label: string }[],
): string {
  if (!a) return 'Guest';
  if (a.user_id) {
    const m = members.find((x) => x.user_id === a.user_id);
    if (m?.label) return m.label;
  }
  const dn = typeof a.display_name === 'string' ? a.display_name.trim() : '';
  if (dn) return dn;
  if (a.email?.trim()) return a.email.trim();
  return 'Guest';
}

function actionAssigneeDisplay(
  action: any,
  attendees: any[],
  members: { user_id: string; label: string }[],
): string {
  const n = action?.assignee_name?.trim();
  if (n) return n;
  const uid = action?.assignee_user_id;
  if (uid) {
    const m = members.find((x) => x.user_id === uid);
    if (m?.label) return m.label;
    const att = attendees.find((x) => x.user_id === uid);
    if (att) return attendeeDisplayLabel(att, members);
    return `Member (${uid.slice(0, 8)}…)`;
  }
  const em = action?.assignee_email?.trim();
  if (em) return em;
  return '';
}

function actionToAssigneePick(action: any, attendees: any[]): string {
  const n = action?.assignee_name?.trim();
  if (n) {
    const nameLower = n.toLowerCase();
    const att = attendees.find((x) =>
      typeof x.display_name === 'string' && x.display_name.trim().toLowerCase() === nameLower,
    );
    if (att) return `a:${att.id}`;
    return `m:${encodeURIComponent(n)}`;
  }
  const uid = action?.assignee_user_id;
  if (uid) {
    const att = attendees.find((x) => x.user_id === uid);
    if (att) return `a:${att.id}`;
    return `u:${uid}`;
  }
  const em = action?.assignee_email?.trim();
  if (em) {
    const att = attendees.find((x) => !x.user_id && (x.email || '').trim() === em);
    if (att) return `a:${att.id}`;
    return `e:${encodeURIComponent(em)}`;
  }
  return '_none';
}

function assigneePayloadFromPick(pick: string, attendees: any[]) {
  const base = { assignee_user_id: null, assignee_email: null, assignee_name: null };
  if (!pick || pick === '_none' || pick === '__manual__') return base;
  if (pick.startsWith('a:')) {
    const row = attendees.find((x) => x.id === pick.slice(2));
    if (!row) return base;
    if (row.user_id) return { ...base, assignee_user_id: row.user_id };
    const em = (row.email || '').trim();
    return em ? { ...base, assignee_email: em } : base;
  }
  if (pick.startsWith('u:')) return { ...base, assignee_user_id: pick.slice(2) };
  if (pick.startsWith('e:')) {
    try {
      const em = decodeURIComponent(pick.slice(2)).trim();
      return em ? { ...base, assignee_email: em } : base;
    } catch {
      return base;
    }
  }
  if (pick.startsWith('m:')) {
    try {
      const name = decodeURIComponent(pick.slice(2)).trim();
      return name ? { ...base, assignee_name: name } : base;
    } catch {
      return base;
    }
  }
  return base;
}

const ACTION_ITEM_STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
  // Archived by the cleaner. Filtered out of this screen, but kept in the vocabulary so a row
  // that does reach a dropdown never renders as the wrong status.
  { value: 'dropped', label: 'Archived' },
] as const;

function normalizeActionItemStatus(status: string | undefined): string {
  if (status === 'open' || status === 'in_progress' || status === 'done' || status === 'dropped') {
    return status;
  }
  return 'open';
}

function actionStatusSelectTriggerClass(status: string): string {
  switch (status) {
    case 'done':
      return 'bg-emerald-100 text-emerald-900 border-emerald-200';
    case 'in_progress':
      return 'bg-amber-100 text-amber-900 border-amber-200';
    case 'dropped':
      return 'bg-orange-100 text-orange-900 border-orange-200';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

function attendeeRsvpLabel(rsvp: string | null | undefined): string {
  if (rsvp === 'accepted') return 'Accepted';
  if (rsvp === 'declined') return 'Declined';
  if (rsvp === 'tentative') return 'Tentative';
  return 'No response';
}

function renderAssigneeSelectItems(
  pickVal: string | undefined,
  attendees: any[],
  members: { user_id: string; label: string }[],
  orphanUserIds: string[],
  orphanEmails: string[],
) {
  let customName: string | null = null;
  if (pickVal?.startsWith('m:')) {
    try {
      customName = decodeURIComponent(pickVal.slice(2));
    } catch {
      customName = null;
    }
  }
  return (
    <>
      <SelectItem value="_none">Unassigned</SelectItem>
      {attendees.map((att) => (
        <SelectItem key={att.id} value={`a:${att.id}`}>
          {attendeeDisplayLabel(att, members)}
        </SelectItem>
      ))}
      {orphanUserIds.map((uid) => (
        <SelectItem key={uid} value={`u:${uid}`}>
          {members.find((m) => m.user_id === uid)?.label || `Member (${uid.slice(0, 8)}…)`}
        </SelectItem>
      ))}
      {orphanEmails.map((em) => (
        <SelectItem key={em} value={`e:${encodeURIComponent(em)}`}>
          {em}
        </SelectItem>
      ))}
      {customName ? <SelectItem value={pickVal!}>{customName}</SelectItem> : null}
      <SelectItem value="__manual__">Other (type name)…</SelectItem>
    </>
  );
}

const SPEAKER_TIMELINE_PALETTE = [
  'bg-emerald-500',
  'bg-violet-500',
  'bg-sky-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-teal-500',
  'bg-indigo-500',
  'bg-orange-500',
];

function formatMmSs(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function SpeakerTimeline({ segments }: { segments: { speaker: string | null; start_ms: number | null; end_ms: number | null }[] }) {
  // Discard segments without timing - we can't place them.
  const timed = segments
    .filter((s) => typeof s.start_ms === 'number' && typeof s.end_ms === 'number' && (s.end_ms as number) > (s.start_ms as number))
    .map((s) => ({ speaker: s.speaker || 'Unknown', start_ms: s.start_ms as number, end_ms: s.end_ms as number }));
  if (timed.length === 0) return null;

  const totalMs = Math.max(...timed.map((s) => s.end_ms));
  if (totalMs <= 0) return null;

  // Unique speakers in first-appearance order.
  const speakers: string[] = [];
  for (const s of timed) {
    if (!speakers.includes(s.speaker)) speakers.push(s.speaker);
  }

  // Tick spacing: aim for 5-7 ticks across the span.
  const totalMin = totalMs / 60_000;
  const stepMin = (() => {
    if (totalMin <= 5) return 1;
    if (totalMin <= 15) return 2;
    if (totalMin <= 30) return 5;
    if (totalMin <= 90) return 10;
    return 15;
  })();
  const ticks: number[] = [];
  for (let t = 0; t <= totalMs; t += stepMin * 60_000) ticks.push(t);

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Speaker activity
        </Label>
        <span className="font-mono text-[10px] text-muted-foreground">
          0:00 - {formatMmSs(totalMs)}
        </span>
      </div>
      <div className="rounded-md border border-border bg-surface-container-low/40 p-3">
        {/* Tick row */}
        <div className="relative mb-1 h-3 text-[10px] text-muted-foreground">
          {ticks.map((t) => (
            <span
              key={t}
              className="absolute -translate-x-1/2 font-mono"
              style={{ left: `${(t / totalMs) * 100}%` }}
            >
              {formatMmSs(t)}
            </span>
          ))}
        </div>
        {/* Speaker rows */}
        <div className="space-y-1.5">
          {speakers.map((sp, i) => {
            const color = SPEAKER_TIMELINE_PALETTE[i % SPEAKER_TIMELINE_PALETTE.length];
            const ownSegments = timed.filter((s) => s.speaker === sp);
            return (
              <div key={sp} className="flex items-center gap-2">
                <div className="w-28 shrink-0 truncate text-xs font-medium text-foreground/80" title={sp}>
                  {sp}
                </div>
                <div className="relative h-3 flex-1 rounded-sm bg-muted/40">
                  {ownSegments.map((s, j) => {
                    const left = (s.start_ms / totalMs) * 100;
                    const width = ((s.end_ms - s.start_ms) / totalMs) * 100;
                    return (
                      <div
                        key={j}
                        className={cn('absolute top-0 h-full rounded-sm', color)}
                        style={{
                          left: `${left}%`,
                          width: `${Math.max(0.15, width)}%`,
                          opacity: 0.85,
                        }}
                        title={`${sp} - ${formatMmSs(s.start_ms)} to ${formatMmSs(s.end_ms)}`}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MeetingPastTimelineRow({
  meeting: m,
  series,
  rowIndex,
  stats,
  attendees,
  onSelectMeeting,
}: {
  meeting: any;
  series: any;
  rowIndex: number;
  stats?: {
    agenda: number;
    checkins: number;
    hasNotes: boolean;
    actions: number;
    decisions: number;
    accepted: number;
    attendees: number;
  };
  attendees: any[];
  onSelectMeeting: (x: any) => void;
}) {
  const d = m.starts_at ? new Date(m.starts_at) : null;
  const dayNum = d && !Number.isNaN(d.getTime()) ? d.getDate() : '-';
  const mon = d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
    : '-';

  const nowMs = Date.now();
  const startMs = d && !Number.isNaN(d.getTime()) ? d.getTime() : Number.POSITIVE_INFINITY;
  const isPastMeeting = startMs < nowMs;
  const agendaCount = stats?.agenda ?? 0;
  const checkins = stats?.checkins ?? 0;
  const hasNotes = Boolean(stats?.hasNotes);
  const actionsCount = stats?.actions ?? 0;
  const decisionsCount = stats?.decisions ?? 0;
  const acceptedCount = stats?.accepted ?? 0;
  const attendeeTotal = stats?.attendees ?? 0;
  const rowTitle = pastSessionRowTitle(m, rowIndex);

  return (
    <section className="relative grid grid-cols-[minmax(72px,140px)_1fr] items-start gap-3 md:gap-6 lg:gap-8">
      <div className="relative pt-4">
        <div className="absolute right-[-7px] top-6 z-10 h-2.5 w-2.5 rounded-full bg-outline-variant ring-4 ring-background" />
        <div className="pr-8 text-right md:pr-12">
          <div className="font-display text-2xl font-bold leading-none text-muted-foreground">{dayNum}</div>
          <div className="mt-1 text-[9px] font-bold uppercase tracking-widest text-stone-400">{mon}</div>
        </div>
      </div>
      <article
        className="rounded-lg border border-transparent bg-muted/50 p-6 shadow-sm hover:border-outline-variant/10 hover:shadow-md"
        style={sessionTimelineTransitionStyle}
      >
        <button
          type="button"
          onClick={() => onSelectMeeting(m)}
          className="group flex w-full cursor-pointer items-center justify-between text-left"
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-6 md:gap-8">
            <div>
              {rowTitle ? (
                <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                  {rowTitle}
                </p>
              ) : null}
            </div>
            {rowTitle ? <div className="hidden h-8 w-px bg-outline-variant/20 sm:block" /> : null}
            <div className="flex flex-wrap items-center gap-4 md:gap-6">
              <div className="flex items-center gap-1.5">
                <List className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <span className="text-xs font-medium text-muted-foreground">Agenda</span>
                {agendaCount > 0 ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" strokeWidth={1.75} />
                ) : (
                  <XCircle className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                )}
              </div>
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <span className="text-xs font-medium text-muted-foreground">Check-ins ({checkins})</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Pencil className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <span className="text-xs font-medium text-muted-foreground">Notes</span>
                {hasNotes ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" strokeWidth={1.75} />
                ) : (
                  <XCircle className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                )}
              </div>
              <div className="flex items-center gap-2">
                <ListChecks className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <span className="text-xs font-medium text-muted-foreground">
                  Action items ({actionsCount})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Gavel className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <span className="text-xs font-medium text-muted-foreground">
                  Decisions ({decisionsCount})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <span className="text-xs font-medium text-muted-foreground">
                  Attendees ({acceptedCount}/{attendeeTotal} Accepted)
                </span>
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3 pl-2">
            <AttendeeBubbleStack attendees={attendees} maxVisible={2} compact />
            <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-1" />
          </div>
        </button>
      </article>
    </section>
  );
}

/** Split pasted agenda input into one row per line; trims bullets / numeric prefixes. */
function splitAgendaInputLines(raw: string): string[] {
  return raw
    .split(/\r?\n/)
    .map((line) =>
      line
        .trim()
        .replace(/^[\-\*\u2022]\s+/, '')
        .replace(/^\d+[\).\]]\s+/, ''),
    )
    .filter((t) => t.length > 0);
}

/**
 * Flatten AI-generated markdown to clean plain text for copy/paste + email - so pasting into
 * Google Docs or an email doesn't carry ## headings, ** bold markers, backticks, links, or
 * em/en dashes. Bullets normalise to "• ".
 */
function stripMarkdownForPaste(input: string): string {
  return String(input || '')
    .replace(/\r\n/g, '\n')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '') // "## Heading" → "Heading"
    .replace(/\*\*([^*]+?)\*\*/g, '$1') // **bold** → bold
    .replace(/__([^_]+?)__/g, '$1') // __bold__ → bold
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [text](url) → text
    .replace(/`([^`]+)`/g, '$1') // `code` → code
    .replace(/^\s{0,3}[-*+]\s+/gm, '• ') // list bullets → •
    .replace(/\*([^*\n]+?)\*/g, '$1') // *italic* → italic
    .replace(/\*/g, '') // any stray asterisks
    .replace(/[-–]/g, '-') // em/en dash → hyphen
    .replace(/[ \t]+\n/g, '\n') // trailing spaces
    .replace(/\n{3,}/g, '\n\n') // collapse blank runs
    .trim();
}

/**
 * Heuristic: does the long summary's "Next steps" section already cover the action items? If so the
 * separate ACTION ITEMS block in a shared email is redundant and can be dropped. True when ≥60% of
 * action-item titles are represented (by word overlap) in the next-steps text.
 */
function actionsMirrorNextSteps(longSummary: string, actions: any[]): boolean {
  if (!actions.length) return false;
  const m = longSummary.match(/next steps?\s*:?\s*\n([\s\S]*?)(?=\n\s*#{1,6}\s|$)/i);
  if (!m) return false;
  const steps = m[1].toLowerCase();
  const titles = actions.map((a) => String(a?.title || '').trim()).filter(Boolean);
  if (!titles.length) return false;
  const represented = titles.filter((t) => {
    const words = t.toLowerCase().replace(/[^a-z0-9 ]/g, ' ').split(/\s+/).filter((w) => w.length > 3);
    if (!words.length) return steps.includes(t.toLowerCase());
    return words.filter((w) => steps.includes(w)).length / words.length >= 0.6;
  }).length;
  return represented / titles.length >= 0.6;
}

function MeetingSessionView({
  series,
  meeting,
  meetings,
  workspace,
  userId,
  membership,
  inboundAddress,
  preferredSessionTab,
  onConsumePreferredSessionTab,
  onSelectMeeting,
  onRefreshSeries,
}: {
  series: any;
  meeting: any;
  meetings: any[];
  workspace: any;
  userId: string;
  membership: any;
  inboundAddress: string;
  preferredSessionTab?: 'agenda' | 'checkins' | 'summary' | 'actions' | 'decisions' | 'people' | 'transcript' | 'chat' | 'recording' | null;
  onConsumePreferredSessionTab?: () => void;
  onSelectMeeting: (m: any) => void;
  onRefreshSeries: () => Promise<void>;
}) {
  const [sessionTab, setSessionTab] = useState<
    'agenda' | 'checkins' | 'summary' | 'actions' | 'decisions' | 'people' | 'transcript' | 'chat' | 'recording'
  >('agenda');
  const [copiedTab, setCopiedTab] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  // Owner refine: free-text instruction that re-derives the AI artifacts (summary/decisions/actions/agenda)
  // per the prompt - applied via transcript-orchestrator → applyRefinedInsights.
  const [refinePrompt, setRefinePrompt] = useState('');
  const [refineBusy, setRefineBusy] = useState(false);
  // Owner-only private notes (separate table meeting_private_notes, gated by RLS to the meeting
  // owner). Rendered on the Notes tab only when the viewer is the owner.
  const [privateNotes, setPrivateNotes] = useState('');
  const [savingPrivate, setSavingPrivate] = useState(false);
  const [decisions, setDecisions] = useState<any[]>([]);
  const [actions, setActions] = useState<any[]>([]);
  const [agenda, setAgenda] = useState<any[]>([]);
  const [checkIns, setCheckIns] = useState<any[]>([]);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [transcript, setTranscript] = useState<any | null>(null);
  /** Has loadDetail() resolved for the current meeting? Used to suppress "Bot did not join" warnings
   *  before we've actually fetched meeting_transcripts (would otherwise flash on every navigation). */
  const [transcriptLoaded, setTranscriptLoaded] = useState(false);
  const [transcriptBusy, setTranscriptBusy] = useState(false);
  const [inviteBotBusy, setInviteBotBusy] = useState(false);
  const [seriesBotEnabledLocal, setSeriesBotEnabledLocal] = useState(Boolean(series?.transcript_bot_enabled));
  /** meeting_series.capture_method - 'desktop' means the Upstand desktop app records this series
   *  and no bot is ever scheduled (docs/PRD_DEVICE_AUTO_RECORD.md). */
  const [seriesCaptureMethodLocal, setSeriesCaptureMethodLocal] = useState(
    String((series as any)?.capture_method || 'bot'),
  );
  const desktopApp = useDesktopApp(workspace?.id ?? null);
  /** workspaces.settings.allow_auto_desktop_recording - admin kill switch for automatic desktop
   *  recording. Unset means allowed: the per-series "Desktop App" choice is the real gate, and an
   *  admin turns this off to withdraw it workspace-wide. */
  const autoDesktopRecordingAllowed =
    (workspace?.settings as { allow_auto_desktop_recording?: unknown } | null)
      ?.allow_auto_desktop_recording !== false;
  const [seriesFlyoutOpen, setSeriesFlyoutOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [resolvedSeriesBotName, setResolvedSeriesBotName] = useState<string | null>(null);
  const [recordingState, setRecordingState] = useState<{
    loading: boolean;
    status: 'ready' | 'processing' | 'no_recording' | 'unavailable' | 'expired' | 'error';
    video: { url: string; expires_at: string | null } | null;
    audio: { url: string; expires_at: string | null } | null;
    error: string | null;
  } | null>(null);
  const [approvalBusy, setApprovalBusy] = useState<'approve' | 'cancel' | null>(null);
  const [attendees, setAttendees] = useState<any[]>([]);
  const [meetingAttendeePreview, setMeetingAttendeePreview] = useState<Record<string, any[]>>({});
  const [newDecision, setNewDecision] = useState('');
  const [newAction, setNewAction] = useState('');
  const [newActionAssigneePick, setNewActionAssigneePick] = useState('_none');
  const [newActionAssigneeManual, setNewActionAssigneeManual] = useState('');
  const [actionManualEditId, setActionManualEditId] = useState<string | null>(null);
  const [actionManualEditText, setActionManualEditText] = useState('');
  const [actionStatusSavingId, setActionStatusSavingId] = useState<string | null>(null);
  // Per-item updates thread (comments + status timeline) on the Actions tab.
  const [actionThreadOpenId, setActionThreadOpenId] = useState<string | null>(null);
  /** Inline title edit for action items. When set, the matching row swaps its read-only title for an input. */
  const [actionTitleEditId, setActionTitleEditId] = useState<string | null>(null);
  const [actionTitleEditText, setActionTitleEditText] = useState('');
  /** Inline body edit for decisions. */
  const [decisionEditId, setDecisionEditId] = useState<string | null>(null);
  const [decisionEditText, setDecisionEditText] = useState('');
  const [transferOpen, setTransferOpen] = useState(false);
  /** Calendar event for the focused occurrence was hard-deleted; prompt keep-vs-cancel. */
  const [calendarDeletedOpen, setCalendarDeletedOpen] = useState(false);
  const [cancellingCalendarDeleted, setCancellingCalendarDeleted] = useState(false);
  const [members, setMembers] = useState<{ user_id: string; label: string }[]>([]);
  // Phase A - manual speaker attribution (docs/PRD_HYBRID_VOICE_ATTRIBUTION.md).
  const [speakerResolutions, setSpeakerResolutions] = useState<any[]>([]);
  const [reassignLabel, setReassignLabel] = useState<string | null>(null);
  const [reassignGuest, setReassignGuest] = useState('');
  const [reassignGuestEmail, setReassignGuestEmail] = useState('');
  const [reassignBusy, setReassignBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [transferTo, setTransferTo] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [newAgendaTitle, setNewAgendaTitle] = useState('');
  const [agendaEditingId, setAgendaEditingId] = useState<string | null>(null);
  const [agendaEditDraft, setAgendaEditDraft] = useState('');
  const [agendaSavingId, setAgendaSavingId] = useState<string | null>(null);
  const [agendaAddBusy, setAgendaAddBusy] = useState(false);
  const [agendaReorderBusy, setAgendaReorderBusy] = useState(false);
  const [agendaDeleteDialog, setAgendaDeleteDialog] = useState<{ id: string; title: string } | null>(
    null,
  );
  const [agendaDeletingId, setAgendaDeletingId] = useState<string | null>(null);
  // Per-agenda-block manual notes editing (distinct from title editing above).
  const [agendaNotesEditingId, setAgendaNotesEditingId] = useState<string | null>(null);
  const [agendaNotesDraft, setAgendaNotesDraft] = useState('');
  const [agendaNotesSavingId, setAgendaNotesSavingId] = useState<string | null>(null);
  const [syncingAtt, setSyncingAtt] = useState(false);
  const syncAttendeesMeetingLockRef = useRef(new Set<string>());
  const autoSyncedAttendeesMeetingIdsRef = useRef(new Set<string>());
  const [meetingStats, setMeetingStats] = useState<
    Record<
      string,
      {
        agenda: number;
        checkins: number;
        hasNotes: boolean;
        actions: number;
        decisions: number;
        accepted: number;
        attendees: number;
      }
    >
  >({});
  /** Which timeline session has the full detail card - only one; stepper/row clicks change it. */
  const [focusedMeetingId, setFocusedMeetingId] = useState<string | null>(null);
  const timelineStackRef = useRef<HTMLDivElement | null>(null);
  const lastSyncedMeetingIdFromParent = useRef<string | undefined>(undefined);
  const [timelineScrollEl, setTimelineScrollEl] = useState<HTMLDivElement | null>(null);
  const timelineScrollElRef = useRef<HTMLDivElement | null>(null);
  const bindTimelineScrollRef = useCallback((node: HTMLDivElement | null) => {
    timelineScrollElRef.current = node;
    setTimelineScrollEl(node);
  }, []);
  /** Blocks overlapping navigations during the 600ms motion. */
  const timelineSteppingRef = useRef(false);
  const focusedMeetingIdRef = useRef<string | null>(null);
  const didInitialTimelineScrollRef = useRef(false);
  const detailLoadSeqRef = useRef(0);

  useEffect(() => {
    setSeriesBotEnabledLocal(Boolean(series?.transcript_bot_enabled));
    setSeriesCaptureMethodLocal(String((series as any)?.capture_method || 'bot'));
  }, [series?.id, series?.transcript_bot_enabled, (series as any)?.capture_method]);

  useEffect(() => {
    lastSyncedMeetingIdFromParent.current = undefined;
    didInitialTimelineScrollRef.current = false;
  }, [series?.id]);

  // Resolve the bot name that the series owner's bot will use:
  // owner's workspace_members.bot_name → workspace.bot_name → null
  useEffect(() => {
    const ownerId = series?.owner_user_id;
    const workspaceFallback = ((workspace as any)?.bot_name as string | null)?.trim() || null;
    if (!ownerId) {
      setResolvedSeriesBotName(workspaceFallback);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('workspace_members')
        .select('bot_name')
        .eq('workspace_id', workspace.id)
        .eq('user_id', ownerId)
        .maybeSingle();
      if (cancelled) return;
      const personal = ((data as any)?.bot_name as string | null)?.trim() || null;
      setResolvedSeriesBotName(personal || workspaceFallback);
    })();
    return () => {
      cancelled = true;
    };
  }, [series?.id, series?.owner_user_id, workspace?.id, (workspace as any)?.bot_name]);

  const sessionsChronoDesc = useMemo(() => {
    return [...meetings].sort((a, b) => {
      const ta = a.starts_at ? new Date(a.starts_at).getTime() : -1;
      const tb = b.starts_at ? new Date(b.starts_at).getTime() : -1;
      return tb - ta;
    });
  }, [meetings]);

  const meetingsIdsKey = useMemo(() => meetings.map((m) => m.id).sort().join(','), [meetings]);

  useEffect(() => {
    if (!meetings.length) {
      setFocusedMeetingId(null);
      lastSyncedMeetingIdFromParent.current = undefined;
      return;
    }
    const sorted = [...meetings].sort((a, b) => {
      const ta = a.starts_at ? new Date(a.starts_at).getTime() : -1;
      const tb = b.starts_at ? new Date(b.starts_at).getTime() : -1;
      return tb - ta;
    });
    const firstId = sorted[0]?.id;
    if (!firstId) {
      setFocusedMeetingId(null);
      return;
    }

    const parentId = meeting?.id && meetings.some((x) => x.id === meeting.id) ? meeting.id : null;
    if (parentId && parentId !== lastSyncedMeetingIdFromParent.current) {
      lastSyncedMeetingIdFromParent.current = parentId;
      setFocusedMeetingId(parentId);
      return;
    }

    setFocusedMeetingId((prev) => {
      if (prev && meetings.some((x) => x.id === prev)) return prev;
      return pickClosestToNowMeetingId(sorted) ?? firstId;
    });
  }, [meeting?.id, meetingsIdsKey, series?.id, meetings.length]);

  const focusedMeeting = useMemo(() => {
    if (!focusedMeetingId) return meeting || sessionsChronoDesc[0];
    return (meetings.find((x) => x.id === focusedMeetingId) ?? sessionsChronoDesc[0]) || meeting;
  }, [focusedMeetingId, meetings, sessionsChronoDesc, meeting]);
  const focusedAttendees = attendees.length
    ? attendees
    : (meetingAttendeePreview[focusedMeeting?.id] || []);

  focusedMeetingIdRef.current = focusedMeetingId;

  useEffect(() => {
    if (!focusedMeetingId) return;
    const m = meetings.find((x) => x.id === focusedMeetingId);
    if (!m) return;
    if (m.id === meeting?.id) return;
    onSelectMeeting(m);
  }, [focusedMeetingId, meetings, meeting?.id, onSelectMeeting]);

  const chronologicallyLatestId = sessionsChronoDesc[0]?.id;
  const isFocusedChronologicallyLatest = focusedMeeting?.id === chronologicallyLatestId;

  /** Stepper / row click: expand this session only (replaces the previous expanded). */
  const navigateToMeeting = useCallback((m: any) => {
    if (!m?.id) return;
    if (timelineSteppingRef.current) return;
    if (m.id === focusedMeetingIdRef.current) return;
    timelineSteppingRef.current = true;
    setFocusedMeetingId(m.id);
    requestAnimationFrame(() => {
      const el = timelineStackRef.current;
      if (el) {
        el.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
      }
      window.setTimeout(() => {
        timelineSteppingRef.current = false;
      }, 180);
    });
  }, []);

  useEffect(() => {
    if (!timelineScrollEl || !focusedMeetingId || didInitialTimelineScrollRef.current) return;
    const el = timelineStackRef.current;
    if (!el) return;
    didInitialTimelineScrollRef.current = true;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
    });
  }, [timelineScrollEl, focusedMeetingId, meetingsIdsKey]);

  const isOwner = series.owner_user_id === userId;
  // Per-meeting management is OWNER-ONLY - matches the meeting_series UPDATE RLS policy
  // (user_can_manage_meeting_series). Workspace admins manage the workspace, not others' meetings;
  // in a Shared workspace you can SEE others' meetings but must not re-sync, edit, or manage them.
  const canManage = isOwner;
  /**
   * Agenda is editable by any attendee, not just owner/admin - so a team can still
   * stage the agenda when the meeting owner is out sick. RLS on agenda_items already
   * gates by user_can_access_meeting (owner, attendee user_id, or attendee email);
   * mirror that here so the UI affordances match.
   */
  const { user: authUserForAgenda } = useAuth();
  const myEmailLowerForAgenda = (authUserForAgenda?.email || '').trim().toLowerCase();
  const isAttendeeOnMeeting = attendees.some((a: any) => {
    if (a?.user_id && a.user_id === userId) return true;
    const em = String(a?.email || '').trim().toLowerCase();
    return Boolean(myEmailLowerForAgenda) && em === myEmailLowerForAgenda;
  });
  const canEditAgenda = canManage || isAttendeeOnMeeting;

  const assigneeOrphanUserIds = useMemo(() => {
    const s = new Set<string>();
    for (const ac of actions) {
      if (ac.assignee_user_id && !attendees.some((t) => t.user_id === ac.assignee_user_id)) {
        s.add(ac.assignee_user_id);
      }
    }
    return [...s];
  }, [actions, attendees]);

  const assigneeOrphanEmails = useMemo(() => {
    const s = new Set<string>();
    for (const ac of actions) {
      const em = ac.assignee_email?.trim();
      if (!em) continue;
      if (attendees.some((t) => !t.user_id && (t.email || '').trim() === em)) continue;
      s.add(em);
    }
    return [...s];
  }, [actions, attendees]);

  useEffect(() => {
    if (!meetings.length) return;
    let cancelled = false;
    (async () => {
      const meetingIds = meetings.map((m) => String(m.id)).filter(Boolean);
      const countRows = (rows: any[]) => {
        const out: Record<string, number> = {};
        for (const row of rows) {
          const key = String(row?.meeting_id || '');
          if (!key) continue;
          out[key] = (out[key] || 0) + 1;
        }
        return out;
      };

      const [
        actionsRes,
        decisionsRes,
        agendaRes,
        checkinsRes,
        notesRes,
        attendeesRes,
      ] = await Promise.all([
        fetchRowsByMeetingIds('action_items', meetingIds, 'meeting_id', ['dropped']),
        fetchRowsByMeetingIds('decisions', meetingIds, 'meeting_id'),
        fetchRowsByMeetingIds('agenda_items', meetingIds, 'meeting_id'),
        fetchRowsByMeetingIds('pre_meeting_check_ins', meetingIds, 'meeting_id'),
        fetchRowsByMeetingIds('meeting_notes', meetingIds, 'meeting_id, content'),
        fetchAttendeesByMeetingIds(meetingIds),
      ]);

      if (cancelled) return;

      const actionsByMeeting = actionsRes.error ? {} : countRows(actionsRes.rows);
      const decisionsByMeeting = decisionsRes.error ? {} : countRows(decisionsRes.rows);
      const agendaByMeeting = agendaRes.error ? {} : countRows(agendaRes.rows);
      const checkinsByMeeting = checkinsRes.error ? {} : countRows(checkinsRes.rows);

      const notesByMeeting: Record<string, boolean> = {};
      if (!notesRes.error) {
        for (const row of notesRes.rows || []) {
          const key = String(row?.meeting_id || '');
          if (!key) continue;
          if (typeof row?.content === 'string' && row.content.trim().length > 0) {
            notesByMeeting[key] = true;
          }
        }
      }

      const attendeesByMeeting = attendeesRes.error ? {} : attendeesRes.byMeeting;
      const stats: Record<
        string,
        { agenda: number; checkins: number; hasNotes: boolean; actions: number; decisions: number; accepted: number; attendees: number }
      > = {};

      for (const m of meetings) {
        const id = String(m.id);
        const attendeeRows = attendeesByMeeting[id] || [];
        const accepted = attendeeRows.filter((a: any) => a?.rsvp === 'accepted').length;
        stats[id] = {
          agenda: agendaByMeeting[id] || 0,
          checkins: checkinsByMeeting[id] || 0,
          hasNotes: Boolean(notesByMeeting[id]),
          actions: actionsByMeeting[id] || 0,
          decisions: decisionsByMeeting[id] || 0,
          accepted,
          attendees: attendeeRows.length,
        };
      }

      setMeetingStats(stats);
    })();
    return () => {
      cancelled = true;
    };
  }, [meetings]);

  useEffect(() => {
    const meetingIds = meetings.map((m) => m.id).filter(Boolean);
    if (!meetingIds.length) {
      setMeetingAttendeePreview({});
      return;
    }
    let cancelled = false;
    (async () => {
      const { byMeeting, error } = await fetchAttendeesByMeetingIds(meetingIds);
      if (cancelled || error) return;
      if (!cancelled) setMeetingAttendeePreview(byMeeting);
    })();
    return () => {
      cancelled = true;
    };
  }, [meetings]);

  const cancelAgendaEdit = useCallback(() => {
    setAgendaEditingId(null);
    setAgendaEditDraft('');
  }, []);

  const cancelAgendaNotesEdit = useCallback(() => {
    setAgendaNotesEditingId(null);
    setAgendaNotesDraft('');
  }, []);

  const loadDetail = useCallback(async () => {
    if (!focusedMeeting?.id) return;
    const meetingId = focusedMeeting.id;
    const seq = ++detailLoadSeqRef.current;
    const [{ data: n }, { data: d }, { data: a }, { data: g }, { data: c }, { data: att }, { data: mt }, { data: chat }, { data: res }] =
      await Promise.all([
        supabase.from('meeting_notes').select('*').eq('meeting_id', meetingId).maybeSingle(),
        supabase.from('decisions').select('*').eq('meeting_id', meetingId).order('sort_order'),
        supabase.from('action_items').select('*').eq('meeting_id', meetingId).neq('status', 'dropped').order('created_at'),
        supabase.from('agenda_items').select('*').eq('meeting_id', meetingId).order('sort_order'),
        supabase.from('pre_meeting_check_ins').select('id, meeting_id, user_id, body_text, content_type, media_path, media_mime, transcript_text, transcript_status, submitted_at').eq('meeting_id', meetingId),
        supabase.from('meeting_attendees').select('*').eq('meeting_id', meetingId),
        supabase.from('meeting_transcripts').select('*').eq('meeting_id', meetingId).maybeSingle(),
        supabase.from('meeting_chat_messages').select('*').eq('meeting_id', meetingId).order('sent_at'),
        supabase.from('meeting_speaker_resolutions').select('*').eq('meeting_id', meetingId),
      ]);
    // Ignore stale responses when the user switched occurrences quickly.
    if (detailLoadSeqRef.current !== seq || focusedMeetingIdRef.current !== meetingId) return;
    setNotes(n?.content || '');
    setDecisions(d || []);
    setActions(a || []);
    setAgenda(g || []);
    setCheckIns(c || []);
    setAttendees(att || []);
    setTranscript(mt || null);
    setSpeakerResolutions(res || []);
    setChatMessages(chat || []);
    setTranscriptLoaded(true);

    // Owner-only private notes live in their own RLS-gated table. Skip the fetch for non-owners
    // (RLS would return nothing anyway) and clear any stale value.
    if (isOwner) {
      const { data: pn } = await supabase
        .from('meeting_private_notes')
        .select('content')
        .eq('meeting_id', meetingId)
        .maybeSingle();
      if (detailLoadSeqRef.current === seq && focusedMeetingIdRef.current === meetingId) {
        setPrivateNotes(pn?.content || '');
      }
    } else {
      setPrivateNotes('');
    }
  }, [focusedMeeting?.id, isOwner]);

  // Phase A - map diarisation labels ("Speaker A") to resolved display names + Guest N numbering.
  const speakerResolution = useMemo(() => {
    const byLabel = new Map<string, any>();
    for (const r of speakerResolutions || []) byLabel.set(String(r.diarisation_label), r);
    const memberLabelById = new Map<string, string>();
    for (const m of members || []) memberLabelById.set(String(m.user_id), m.label);
    const isAnon = (label: string) => /^speaker\b/i.test(String(label || '').trim());
    const guestNum = new Map<string, number>();
    let n = 0;
    const segs = Array.isArray(transcript?.transcript_segments) ? transcript.transcript_segments : [];
    for (const seg of segs) {
      const label = String(seg?.speaker || '').trim();
      if (!label || guestNum.has(label)) continue;
      if (isAnon(label) && !byLabel.get(label)) guestNum.set(label, ++n);
    }
    const displayFor = (label: string): string => {
      const key = String(label || '').trim();
      if (!key) return '';
      const r = byLabel.get(key);
      if (r) {
        if (r.resolved_display_name) return String(r.resolved_display_name);
        if (r.resolved_user_id) return memberLabelById.get(String(r.resolved_user_id)) || 'Member';
        if (r.guest_label) return String(r.guest_label);
      }
      if (isAnon(key) && guestNum.has(key)) return `Guest ${guestNum.get(key)}`;
      return key;
    };
    // Phase D: confidence chip (only for auto-resolved labels) + below-threshold "Looks like X?"
    // suggestions parked on the transcript by identify-meeting-speakers (unresolved labels only).
    const confidenceFor = (label: string): number | null => {
      const r = byLabel.get(String(label || '').trim());
      return r && r.resolved_by === 'auto' && r.confidence != null ? Number(r.confidence) : null;
    };
    // Floor for showing a suggestion at all (mirrors SUGGESTION_THRESHOLD in pyannote-webhook). Below
    // this an acoustic match is a coin-flip, not a hint - don't nudge the owner toward a wrong name.
    // Also filters meetings whose suggestions were parked before the floor existed.
    const SUGGESTION_MIN_CONFIDENCE = 0.5;
    const suggestions: Array<{ label: string; user_id: string; display_name: string; confidence: number }> = [];
    const rawSug = (transcript as any)?.speaker_suggestions;
    if (rawSug && typeof rawSug === 'object') {
      for (const [label, v] of Object.entries(rawSug as Record<string, any>)) {
        if (byLabel.get(label)) continue; // already resolved → no suggestion
        if (v && (v as any).user_id && (Number((v as any).confidence) || 0) >= SUGGESTION_MIN_CONFIDENCE) {
          suggestions.push({
            label,
            user_id: String((v as any).user_id),
            display_name: String((v as any).display_name || 'a teammate'),
            confidence: Number((v as any).confidence) || 0,
          });
        }
      }
    }
    // Every UNNAMED voice still in the transcript, in first-appearance order, each with its auto-suggestion
    // (if any survived the floor). This drives a discoverable "Name the speakers" card so the owner can
    // assign voices even when there are no voiceprints / no suggestions at all (e.g. a fresh desktop
    // recording labelled "Voice 1 / Voice 2"). Matches every anonymous label shape: "Speaker A",
    // hybrid "100-speaker_1", desktop "Voice 2" - the old isAnon missed the latter two.
    const needsNaming = (label: string) => {
      const s = String(label || '').trim();
      return /^(?:\d+-)?speaker(?:\b|[\s_])/i.test(s) || /^voice\s*\d+/i.test(s);
    };
    const sugByLabel = new Map(suggestions.map((s) => [s.label, s]));
    const unresolvedVoices: Array<{ label: string; suggestion: (typeof suggestions)[number] | null }> = [];
    const seenVoice = new Set<string>();
    for (const seg of segs) {
      const label = String(seg?.speaker || '').trim();
      if (!label || seenVoice.has(label)) continue;
      if (needsNaming(label) && !byLabel.get(label)) {
        seenVoice.add(label);
        unresolvedVoices.push({ label, suggestion: sugByLabel.get(label) || null });
      }
    }
    return { byLabel, displayFor, confidenceFor, suggestions, unresolvedVoices };
  }, [speakerResolutions, members, transcript]);

  const saveSpeakerResolution = useCallback(
    async (opts: { resolved_user_id?: string | null; guest_label?: string | null; clear?: boolean; label?: string }) => {
      const label = opts.label ?? reassignLabel;
      if (!label || !focusedMeeting?.id) return;
      setReassignBusy(true);
      try {
        await invokeEdgeFn('update-meeting-speaker-resolution', {
          meeting_id: focusedMeeting.id,
          diarisation_label: label,
          resolved_user_id: opts.clear ? null : opts.resolved_user_id ?? null,
          guest_label: opts.clear ? null : opts.guest_label ?? null,
        });
        setReassignLabel(null);
        setReassignGuest('');
        setReassignGuestEmail('');
        await loadDetail();
        toast.success('Speaker updated');
      } catch (e: any) {
        toast.error(e?.message || 'Failed to update speaker');
      } finally {
        setReassignBusy(false);
      }
    },
    [reassignLabel, focusedMeeting?.id, loadDetail],
  );

  // The attribution picker offers this meeting's attendees first (the people most likely on the mic),
  // then the rest of the workspace as a fallback. Attendees already carry user_id + display_name.
  const speakerPickerCandidates = useMemo(() => {
    const memberById = new Map(members.map((m) => [m.user_id, m.label]));
    const fromAttendees = new Map<string, { user_id: string; label: string }>();
    for (const a of attendees as Array<{ user_id?: string | null; display_name?: string | null; email?: string | null }>) {
      if (!a.user_id || fromAttendees.has(a.user_id)) continue;
      fromAttendees.set(a.user_id, {
        user_id: a.user_id,
        label: (a.display_name || memberById.get(a.user_id) || a.email || 'Attendee').trim(),
      });
    }
    return { attendees: [...fromAttendees.values()], others: members.filter((m) => !fromAttendees.has(m.user_id)) };
  }, [attendees, members]);

  // Save a free-text guest. If an email is supplied, also send an explicit invite (view artifacts +
  // consent to a voiceprint + create their own account). Name-only saves with no invite / no voiceprint.
  const saveGuest = useCallback(async () => {
    const label = reassignLabel;
    const name = reassignGuest.trim();
    const email = reassignGuestEmail.trim().toLowerCase();
    if (!label || !name || !focusedMeeting?.id) return;
    setReassignBusy(true);
    try {
      await invokeEdgeFn('update-meeting-speaker-resolution', {
        meeting_id: focusedMeeting.id,
        diarisation_label: label,
        guest_label: name,
        guest_email: email || null,
      });
      if (email) {
        await invokeEdgeFn('invite-meeting-guest', { meeting_id: focusedMeeting.id, email, name });
      }
      setReassignLabel(null);
      setReassignGuest('');
      setReassignGuestEmail('');
      await loadDetail();
      toast.success(email ? `Saved - invite sent to ${email}` : 'Speaker updated');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save guest');
    } finally {
      setReassignBusy(false);
    }
  }, [reassignLabel, reassignGuest, reassignGuestEmail, focusedMeeting?.id, loadDetail]);

  const resendCorrectedRecap = useCallback(async () => {
    if (!focusedMeeting?.id) return;
    setResendBusy(true);
    try {
      await invokeEdgeFn('resend-corrected-recap', { meeting_id: focusedMeeting.id });
      toast.success('Corrected recap resent');
      await loadDetail();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to resend recap');
    } finally {
      setResendBusy(false);
    }
  }, [focusedMeeting?.id, loadDetail]);

  const confirmDeleteAgendaItem = useCallback(async () => {
    const target = agendaDeleteDialog;
    if (!target?.id || !canEditAgenda) return;
    const id = target.id;
    const wasEditingThis = agendaEditingId === id;
    setAgendaDeletingId(id);
    try {
      const { error } = await supabase.from('agenda_items').delete().eq('id', id);
      if (error) {
        toast.error(error.message);
        return;
      }
      if (wasEditingThis) cancelAgendaEdit();
      toast.success('Agenda item removed');
      setAgendaDeleteDialog(null);
      await loadDetail();
    } finally {
      setAgendaDeletingId(null);
    }
  }, [agendaDeleteDialog, agendaEditingId, canEditAgenda, cancelAgendaEdit, loadDetail]);

  // Reload the focused meeting's detail when it changes AND whenever the user switches tabs, so stale
  // state (e.g. a transcript that read "failed" but has since recovered to ready server-side) is corrected
  // without a full page refresh. loadDetail is a silent in-place refetch (no loading flicker) guarded by a
  // sequence ref, so re-running it on each tab switch is safe.
  useEffect(() => {
    loadDetail();
  }, [loadDetail, sessionTab]);

  // While a bot is scheduled/live (has a provider_bot_id and hasn't left), poll the transcript row
  // so the live bot status - and the Pause/Resume control - appear when the bot joins and clear when
  // it leaves, without re-opening the meeting. Stops once the bot has left.
  useEffect(() => {
    const meetingId = focusedMeeting?.id;
    if (!meetingId || !transcript?.provider_bot_id || transcript?.bot_left_at) return;
    // Stop once nothing live remains to observe: the bot failed / wasn't admitted, or the recording
    // finished AND insights are done. Keeps polling through 'recording'/'processing' and while
    // insights generate, then stops - so an old meeting whose bot_left_at was never written (the
    // known webhook-ordering gotcha) doesn't poll every 15s forever.
    const botStatus = String(transcript?.status || '').toLowerCase();
    const insightsStatus = String(transcript?.extraction_status || '').toLowerCase();
    const transcriptTerminal =
      botStatus === 'failed' ||
      botStatus === 'not_admitted' ||
      (botStatus === 'ready' && (insightsStatus === 'ready' || insightsStatus === 'failed'));
    if (transcriptTerminal) return;
    const startsAtMs = focusedMeeting?.starts_at ? new Date(focusedMeeting.starts_at).getTime() : null;
    const interval = setInterval(async () => {
      // Don't poll before the meeting starts - there's no live bot status yet, and a scheduled
      // future meeting would otherwise hit the DB every 15s for nothing. The timer still ticks
      // cheaply and starts fetching once the start time passes, so we don't need the effect to
      // re-run when the clock crosses it.
      if (startsAtMs && Date.now() < startsAtMs) return;
      const { data } = await supabase
        .from('meeting_transcripts')
        .select('*')
        .eq('meeting_id', meetingId)
        .maybeSingle();
      if (data && focusedMeetingIdRef.current === meetingId) setTranscript(data);
    }, 15000);
    return () => clearInterval(interval);
  }, [focusedMeeting?.id, focusedMeeting?.starts_at, transcript?.provider_bot_id, transcript?.bot_left_at, transcript?.status, transcript?.extraction_status]);

  useEffect(() => {
    // Prevent displaying stale details from a previously focused occurrence.
    setNotes('');
    setPrivateNotes('');
    setDecisions([]);
    setActions([]);
    setAgenda([]);
    cancelAgendaEdit();
    cancelAgendaNotesEdit();
    setAgendaDeleteDialog(null);
    setAgendaDeletingId(null);
    setCheckIns([]);
    setChatMessages([]);
    setAttendees([]);
    setTranscript(null);
    setTranscriptLoaded(false);
    setRecordingState(null);
  }, [focusedMeeting?.id, cancelAgendaEdit, cancelAgendaNotesEdit]);

  const loadRecording = useCallback(async () => {
    if (!focusedMeeting?.id) return;
    setRecordingState({ loading: true, status: 'unavailable', video: null, audio: null, error: null });
    try {
      const json = await invokeEdgeFn<{
        status: 'ready' | 'processing' | 'no_recording' | 'unavailable' | 'expired';
        video: { url: string; expires_at: string | null } | null;
        audio: { url: string; expires_at: string | null } | null;
      }>('recording-url', {
        meeting_id: focusedMeeting.id,
        workspace_id: workspace.id,
      });
      setRecordingState({
        loading: false,
        status: json.status,
        video: json.video,
        audio: json.audio,
        error: null,
      });
    } catch (e: any) {
      setRecordingState({
        loading: false,
        status: 'error',
        video: null,
        audio: null,
        error: e?.message || 'Failed to load recording',
      });
    }
  }, [focusedMeeting?.id, workspace.id]);

  useEffect(() => {
    if (sessionTab !== 'recording') return;
    if (recordingState && !recordingState.error) return;
    const hasIds = Array.isArray((transcript as any)?.recording_ids) && (transcript as any).recording_ids.length > 0;
    if (!hasIds) {
      setRecordingState({ loading: false, status: 'no_recording', video: null, audio: null, error: null });
      return;
    }
    void loadRecording();
  }, [sessionTab, transcript, recordingState, loadRecording]);

  // Reset tab only when the SERIES changes (i.e. the user navigated to a different meeting series).
  // Switching dates within the same series via the picker preserves the active tab.
  useEffect(() => {
    if (preferredSessionTab) return;
    setSessionTab('agenda');
  }, [series?.id]);

  useEffect(() => {
    if (!preferredSessionTab) return;
    if (!meeting?.id || focusedMeeting?.id !== meeting.id) return;
    setSessionTab(preferredSessionTab);
    onConsumePreferredSessionTab?.();
  }, [preferredSessionTab, onConsumePreferredSessionTab, focusedMeeting?.id, meeting?.id]);

  useEffect(() => {
    (async () => {
      const { data: memberRows, error: memberError } = await supabase
        .from('workspace_members')
        .select('user_id')
        .eq('workspace_id', workspace.id)
        .eq('status', 'active');
      if (memberError) {
        setMembers([]);
        return;
      }

      const userIds = [...new Set((memberRows || []).map((r: any) => String(r.user_id || '')).filter(Boolean))];
      if (!userIds.length) {
        setMembers([]);
        return;
      }

      const { data: profilesRows } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', userIds);

      const profileById = new Map<string, { full_name?: string | null; email?: string | null }>();
      for (const row of profilesRows || []) {
        profileById.set(String((row as any).id || ''), row as any);
      }

      const rows = userIds.map((userId) => {
        const profile = profileById.get(userId);
        return {
          user_id: userId,
          label: profile?.full_name || profile?.email || userId,
        };
      });
      setMembers(rows);
    })();
  }, [workspace.id]);

  const checkInSubmitterLabel = (submitterId: string) => {
    if (submitterId === userId) return 'You';
    const entry = members.find((m) => m.user_id === submitterId);
    const raw = entry?.label;
    if (raw && raw !== submitterId) return raw;
    return `Member (${submitterId.slice(0, 8)}…)`;
  };

  /** Serialise the currently-open tab's captured data to plain text for the "Copy all" button. */
  const buildTabCopyText = (tab: typeof sessionTab): string => {
    switch (tab) {
      case 'agenda':
        return agenda
          .map((it: any, i: number) => {
            const title = String(it.title ?? '').trim() || 'Untitled';
            const time = it.time_minutes != null ? ` (${it.time_minutes} min)` : '';
            const note = String(it.notes || it.auto_summary || '').trim();
            const head = `${i + 1}. ${title}${time}`;
            return note ? `${head}\n   ${note.replace(/\n/g, '\n   ')}` : head;
          })
          .join('\n');
      case 'checkins':
        return checkIns
          .map((c: any) => {
            const who = checkInSubmitterLabel(String(c.user_id));
            const body = String(c.body_text || c.transcript_text || '').trim();
            return body ? `${who}:\n${body}` : `${who}: (no text)`;
          })
          .join('\n\n');
      case 'summary': {
        const parts: string[] = [];
        const short = String(transcript?.short_summary || '').trim();
        const long = String(transcript?.long_summary || '').trim();
        if (short) parts.push(`SHORT SUMMARY\n${short}`);
        if (long) parts.push(`FULL SUMMARY\n${long}`);
        if (notes.trim()) parts.push(`NOTES\n${notes.trim()}`);
        return parts.join('\n\n');
      }
      case 'actions':
        return actions
          .map((a: any) => {
            const who = actionAssigneeDisplay(a, attendees, members) || 'Unassigned';
            const status =
              ACTION_ITEM_STATUS_OPTIONS.find((s) => s.value === normalizeActionItemStatus(a.status))?.label ||
              'Open';
            return `- ${String(a.title || '').trim()} - ${who} · ${status}`;
          })
          .join('\n');
      case 'decisions':
        return decisions.map((d: any) => `- ${String(d.body || '').trim()}`).join('\n');
      case 'chat':
        return chatMessages
          .map((m: any) => {
            const when = m.sent_at ? new Date(m.sent_at).toLocaleString() : '';
            return `${m.sender_name || 'Unknown'}${when ? ` (${when})` : ''}: ${String(m.body || '').trim()}`;
          })
          .join('\n');
      case 'transcript': {
        const segs = transcript?.transcript_segments;
        if (Array.isArray(segs) && segs.length > 0) {
          return segs
            .map((seg: any) => {
              const who = seg.speaker ? speakerResolution.displayFor(String(seg.speaker)) : '';
              const text = String(seg.text || '').trim();
              return who ? `${who}: ${text}` : text;
            })
            .join('\n');
        }
        return String(transcript?.transcript_text || '').trim();
      }
      default:
        return '';
    }
  };

  const sessionTabLabel = (tab: typeof sessionTab): string =>
    (({
      agenda: 'Agenda',
      checkins: 'Check-ins',
      summary: 'Notes',
      actions: 'Action items',
      decisions: 'Decisions',
      transcript: 'Transcript',
      chat: 'Chat',
      people: 'People',
      recording: 'Recording',
    }) as Record<string, string>)[tab] || '';

  /** Copy one tab's captured data to the clipboard, prefixed with a meeting/tab header. */
  const copyTab = async (tab: typeof sessionTab) => {
    const body = buildTabCopyText(tab).trim();
    if (!body) {
      toast.info('Nothing to copy on this tab yet.');
      return;
    }
    const title = String(focusedMeeting?.title || '').trim();
    const header = [title, sessionTabLabel(tab)].filter(Boolean).join(' - ');
    try {
      // Flatten markdown so it pastes clean into Google Docs / email (no ## ** or em-dashes).
      await navigator.clipboard.writeText(stripMarkdownForPaste(header ? `${header}\n\n${body}` : body));
      setCopiedTab(tab);
      toast.success('Copied to clipboard');
      window.setTimeout(() => setCopiedTab((c) => (c === tab ? null : c)), 1500);
    } catch {
      toast.error('Couldn’t copy - clipboard access was blocked.');
    }
  };

  /** Small right-aligned "Copy" button for a tab's header; null when the tab has nothing to copy. */
  const renderCopyButton = (tab: typeof sessionTab) => {
    if (!buildTabCopyText(tab).trim()) return null;
    return (
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void copyTab(tab)}
          title="Copy this tab to the clipboard"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-primary"
        >
          {copiedTab === tab ? (
            <Check className="h-3.5 w-3.5" strokeWidth={1.8} />
          ) : (
            <Copy className="h-3.5 w-3.5" strokeWidth={1.8} />
          )}
          <span>{copiedTab === tab ? 'Copied' : 'Copy'}</span>
        </button>
      </div>
    );
  };

  const saveNotes = async () => {
    setSaving(true);
    const { error } = await supabase.from('meeting_notes').upsert(
      {
        meeting_id: focusedMeeting.id,
        content: notes,
        updated_by: userId,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'meeting_id' },
    );
    setSaving(false);
    if (error) toast.error(error.message);
    else toast.success('Notes saved');
  };

  const savePrivateNotes = async () => {
    setSavingPrivate(true);
    const { error } = await supabase.from('meeting_private_notes').upsert(
      {
        meeting_id: focusedMeeting.id,
        content: privateNotes,
        updated_by: userId,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'meeting_id' },
    );
    setSavingPrivate(false);
    if (error) toast.error(error.message);
    else toast.success('Private notes saved');
  };

  const addDecision = async () => {
    if (!newDecision.trim()) return;
    const { error } = await supabase.from('decisions').insert({
      meeting_id: focusedMeeting.id,
      body: newDecision.trim(),
      created_by: userId,
      sort_order: decisions.length,
    });
    if (error) toast.error(error.message);
    else {
      setNewDecision('');
      loadDetail();
    }
  };

  const saveActionAssignee = async (actionId: string, pick: string) => {
    const payload = assigneePayloadFromPick(pick, attendees);
    const { error } = await supabase
      .from('action_items')
      .update({ ...payload, updated_at: new Date().toISOString() })
      .eq('id', actionId);
    if (error) toast.error(error.message);
    else loadDetail();
  };

  const saveActionStatus = async (actionId: string, status: string) => {
    const prevStatus = actions.find((a) => a.id === actionId)?.status || null;
    setActionStatusSavingId(actionId);
    const { error } = await supabase
      .from('action_items')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', actionId);
    setActionStatusSavingId(null);
    if (error) toast.error(error.message);
    else {
      // Record the change in the follow-through trail (rendered by the nudge/recap emails and
      // the per-item thread). Best-effort - the status change itself already saved.
      if (userId && status !== prevStatus) {
        void supabase.from('action_item_updates').insert({
          action_item_id: actionId,
          author_user_id: userId,
          author_email: authUserForAgenda?.email || null,
          kind: 'status_change',
          old_status: prevStatus,
          new_status: status,
          source: 'app',
        });
        if (status === 'done') track('action_item_completed', { source: 'app' });
      }
      loadDetail();
    }
  };

  const saveActionTitle = async (actionId: string, title: string) => {
    const t = title.trim();
    if (!t) return;
    const { error } = await supabase
      .from('action_items')
      .update({ title: t, updated_at: new Date().toISOString() })
      .eq('id', actionId);
    if (error) toast.error(error.message);
    else {
      setActionTitleEditId(null);
      setActionTitleEditText('');
      loadDetail();
    }
  };

  const deleteAction = async (actionId: string) => {
    if (!window.confirm('Delete this action item?')) return;
    const { error } = await supabase.from('action_items').delete().eq('id', actionId);
    if (error) toast.error(error.message);
    else loadDetail();
  };

  const saveDecisionBody = async (decisionId: string, body: string) => {
    const t = body.trim();
    if (!t) return;
    const { error } = await supabase
      .from('decisions')
      .update({ body: t })
      .eq('id', decisionId);
    if (error) toast.error(error.message);
    else {
      setDecisionEditId(null);
      setDecisionEditText('');
      loadDetail();
    }
  };

  const deleteDecision = async (decisionId: string) => {
    if (!window.confirm('Delete this decision?')) return;
    const { error } = await supabase.from('decisions').delete().eq('id', decisionId);
    if (error) toast.error(error.message);
    else loadDetail();
  };

  const addAction = async () => {
    if (!newAction.trim()) return;
    let pick = newActionAssigneePick;
    if (pick === '__manual__') {
      const t = newActionAssigneeManual.trim();
      pick = t ? `m:${encodeURIComponent(t)}` : '_none';
    }
    const assignee = assigneePayloadFromPick(pick, attendees);
    const { error } = await supabase.from('action_items').insert({
      meeting_id: focusedMeeting.id,
      title: newAction.trim(),
      status: 'open',
      ...assignee,
    });
    if (error) toast.error(error.message);
    else {
      setNewAction('');
      setNewActionAssigneePick('_none');
      setNewActionAssigneeManual('');
      loadDetail();
    }
  };


  const addAgendaItem = async () => {
    if (!canEditAgenda) return;
    const lines = splitAgendaInputLines(newAgendaTitle);
    if (!lines.length) return;
    setAgendaAddBusy(true);
    try {
      const rows = lines.map((title, idx) => ({
        meeting_id: focusedMeeting.id,
        title,
        sort_order: agenda.length + idx,
        created_by: userId,
      }));
      const { error } = await supabase.from('agenda_items').insert(rows);
      if (error) toast.error(error.message);
      else {
        setNewAgendaTitle('');
        loadDetail();
      }
    } finally {
      setAgendaAddBusy(false);
    }
  };

  const saveAgendaEdit = async () => {
    if (!agendaEditingId || !canEditAgenda) return;
    const t = agendaEditDraft.trim();
    if (!t) {
      toast.error('Agenda title cannot be empty.');
      return;
    }
    setAgendaSavingId(agendaEditingId);
    const { error } = await supabase.from('agenda_items').update({ title: t }).eq('id', agendaEditingId);
    setAgendaSavingId(null);
    if (error) toast.error(error.message);
    else {
      cancelAgendaEdit();
      loadDetail();
    }
  };

  const startAgendaNotesEdit = (item: any) => {
    setAgendaNotesEditingId(String(item.id));
    // Notes and the AI summary are one field: seed the editor from the manual notes if present,
    // otherwise the transcript-derived AI summary, so editing just refines whatever is on screen.
    const manualRaw = String(item.notes ?? '');
    setAgendaNotesDraft(manualRaw.trim() ? manualRaw : String(item.auto_summary ?? ''));
  };

  const saveAgendaNotes = async (itemId: string) => {
    if (!canEditAgenda) return;
    setAgendaNotesSavingId(itemId);
    const { error } = await supabase
      .from('agenda_items')
      .update({ notes: agendaNotesDraft })
      .eq('id', itemId);
    setAgendaNotesSavingId(null);
    if (error) toast.error(error.message);
    else {
      cancelAgendaNotesEdit();
      loadDetail();
    }
  };

  useEffect(() => {
    if (!agendaEditingId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelAgendaEdit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [agendaEditingId, cancelAgendaEdit]);

  const reorderAgendaStep = async (fromIndex: number, delta: -1 | 1) => {
    if (!canEditAgenda || agendaReorderBusy || agendaEditingId) return;
    const toIndex = fromIndex + delta;
    if (toIndex < 0 || toIndex >= agenda.length) return;
    const next = [...agenda];
    const [removed] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, removed);
    const updates = next.map((row, idx) => ({ id: String(row.id), sort_order: idx }));
    setAgendaReorderBusy(true);
    try {
      const results = await Promise.all(
        updates.map(({ id, sort_order }) =>
          supabase.from('agenda_items').update({ sort_order }).eq('id', id),
        ),
      );
      const firstErr = results.find((r) => r.error)?.error;
      if (firstErr) toast.error(firstErr.message);
      await loadDetail();
    } finally {
      setAgendaReorderBusy(false);
    }
  };

  const [playingCheckInId, setPlayingCheckInId] = useState<string | null>(null);

  const triggerTranscriptAction = async (
    action: 'sync_status' | 'retry_capture' | 'regenerate_insights' | 'pause_recording' | 'resume_recording',
  ) => {
    if (!focusedMeeting?.id || !workspace?.id) return;
    setTranscriptBusy(true);
    try {
      await invokeEdgeFn('transcript-orchestrator', {
        action,
        meeting_id: focusedMeeting.id,
        workspace_id: workspace.id,
      });
      const actionMessages: Record<string, string> = {
        retry_capture: 'Transcript bot re-queued. Status will update shortly.',
        sync_status: 'Bot and transcript status refresh requested. Status will update shortly.',
        regenerate_insights: 'Transcript insights regenerated.',
        pause_recording: 'Recording paused.',
        resume_recording: 'Recording resumed.',
      };
      toast.success(actionMessages[action] ?? 'Done.');
      await loadDetail();
      await onRefreshSeries();
    } catch (e: any) {
      toast.error(e?.message || 'Transcript action failed');
    } finally {
      setTranscriptBusy(false);
    }
  };

  // Owner plays a single check-in into the live call (transcript-orchestrator → play-check-ins). The
  // request stays open for the clip's duration (synchronous), so the row shows a "playing" spinner.
  const playCheckInInMeeting = async (checkInId: string) => {
    if (!focusedMeeting?.id || !workspace?.id) return;
    setPlayingCheckInId(checkInId);
    try {
      await invokeEdgeFn('transcript-orchestrator', {
        action: 'play_check_in',
        meeting_id: focusedMeeting.id,
        workspace_id: workspace.id,
        check_in_id: checkInId,
      });
      toast.success('Played into the meeting.');
    } catch (e: any) {
      toast.error(e?.message || 'Could not play into the meeting');
    } finally {
      setPlayingCheckInId(null);
    }
  };

  const stopMeetingPlayback = async () => {
    if (!focusedMeeting?.id || !workspace?.id) return;
    try {
      await invokeEdgeFn('transcript-orchestrator', {
        action: 'stop_playback',
        meeting_id: focusedMeeting.id,
        workspace_id: workspace.id,
      });
      toast.success('Stopped the video.');
    } catch (e: any) {
      toast.error(e?.message || 'Could not stop playback');
    }
  };

  // Owner refines the AI artifacts with a free-text prompt (e.g. "change all action-item owners to Dennis",
  // "drop the pricing line from the summary"). Re-derives only what the prompt directs, then reloads.
  const refineInsights = async () => {
    const instruction = refinePrompt.trim();
    if (!instruction || !focusedMeeting?.id || !workspace?.id) return;
    setRefineBusy(true);
    try {
      await invokeEdgeFn('transcript-orchestrator', {
        action: 'refine_insights',
        meeting_id: focusedMeeting.id,
        workspace_id: workspace.id,
        refinement_prompt: instruction,
      });
      toast.success('Updated from your prompt.');
      setRefinePrompt('');
      await loadDetail();
      await onRefreshSeries();
    } catch (e: any) {
      toast.error(e?.message || 'Refine failed');
    } finally {
      setRefineBusy(false);
    }
  };

  // The bot must be live in the call to inject playback. Mirror the Pause/Resume recording gate exactly: a
  // live bot reports status 'recording' OR 'processing' (real-time mode reports 'processing' mid-call), and
  // a paused recording still means the bot is present. We deliberately do NOT check bot_left_at - it gets
  // set spuriously (we've seen it stamped a second BEFORE bot_joined_at), which hid these buttons during a
  // genuinely live call.
  const botCanPlay = Boolean(
    isOwner &&
      transcript?.provider_bot_id &&
      (transcript?.status === 'recording' ||
        transcript?.status === 'processing' ||
        transcript?.recording_paused),
  );

  const decideRecapApproval = async (action: 'approve' | 'cancel') => {
    if (!focusedMeeting?.id) return;
    setApprovalBusy(action);
    try {
      const res = await invokeEdgeFn<{ already_decided?: boolean; current_status?: string }>(
        'approval-action',
        { meeting_id: focusedMeeting.id, action },
      );
      if (res.already_decided) {
        toast.info('Recap was already decided.');
      } else if (action === 'approve') {
        toast.success('Recap approved - sending now.');
      } else {
        toast.success('Recap cancelled.');
      }
      await loadDetail();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update recap');
    } finally {
      setApprovalBusy(null);
    }
  };

  const inviteBotForSeries = async () => {
    if (!canManage || !series?.id) return;
    setInviteBotBusy(true);
    try {
      const { error } = await supabase
        .from('meeting_series')
        .update({
          transcript_bot_enabled: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', series.id);
      if (error) throw error;
      setSeriesBotEnabledLocal(true);
      toast.success('Bot invited for future occurrences.');
      if (focusedMeeting?.id && workspace?.id) {
        await invokeEdgeFn('calendar-sync-meeting', {
          meeting_id: focusedMeeting.id,
          workspace_id: workspace.id,
          sync_all_in_series: true,
        });
      }
      if (focusedMeeting?.id && !hasMeetingEnded) {
        await triggerTranscriptAction('retry_capture');
      } else {
        await onRefreshSeries();
      }
    } catch (e: any) {
      toast.error(e?.message || 'Could not invite bot');
    } finally {
      setInviteBotBusy(false);
    }
  };

  const removeBotFromCall = async () => {
    if (!focusedMeeting?.id || !workspace?.id) return;
    setTranscriptBusy(true);
    try {
      const res = await invokeEdgeFn<{ cancelled?: number; failed?: number; total?: number }>(
        'transcript-orchestrator',
        { action: 'cancel_bot', meeting_id: focusedMeeting.id, workspace_id: workspace.id },
      );
      if ((res?.cancelled ?? 0) > 0) {
        toast.success('Bot removed from call');
      } else if ((res?.failed ?? 0) > 0) {
        toast.error('Recall refused the cancel - try again in a moment');
      } else {
        toast.message('No active bot to remove');
      }
      await loadDetail();
    } catch (e: any) {
      toast.error(e?.message || 'Could not remove bot');
    } finally {
      setTranscriptBusy(false);
    }
  };

  const copyAgenda = async () => {
    const { data, error } = await supabase.rpc('copy_agenda_from_previous_meeting', {
      p_meeting_id: focusedMeeting.id,
    });
    if (error) toast.error(error.message);
    else {
      toast.success(`Copied ${data ?? 0} agenda items`);
      loadDetail();
    }
  };

  const transfer = async () => {
    if (!transferTo) return;
    const { error } = await supabase.rpc('transfer_meeting_series_owner', {
      p_series_id: series.id,
      p_new_owner_user_id: transferTo,
    });
    if (error) toast.error(error.message);
    else {
      toast.success('Ownership transferred');
      setTransferOpen(false);
      onRefreshSeries();
    }
  };

  const syncAttendeesFromCalendar = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    const mid = focusedMeeting?.id;
    if (!mid || !workspace?.id) return;
    if (syncAttendeesMeetingLockRef.current.has(mid)) return;
    syncAttendeesMeetingLockRef.current.add(mid);
    setSyncingAtt(true);
    try {
      const json = await invokeEdgeFn<{ synced?: number; deleted_meeting_ids?: string[] }>(
        'calendar-sync-meeting',
        {
          meeting_id: mid,
          workspace_id: workspace.id,
        },
      );
      // The event was hard-deleted from the linked calendar. Auto-sync now handles this SILENTLY: the
      // backend has already marked the occurrence cancelled (and a background reconcile hard-deletes
      // future occurrences that never had content), so it drops off the active list on refresh. No
      // keep-vs-cancel prompt - a calendar removal should just quietly leave Upstand.
      if (Array.isArray(json.deleted_meeting_ids) && json.deleted_meeting_ids.includes(mid)) {
        if (!silent) toast.success('Removed from your calendar - taken off your Upstand list');
        await loadDetail();
        await onRefreshSeries();
        return;
      }
      if (!silent) {
        toast.success(
          `Synced from calendar - ${json.synced ?? 0} invitee${(json.synced ?? 0) === 1 ? '' : 's'}, times & details updated`,
        );
      }
      await loadDetail();
      await onRefreshSeries();
    } catch (e: any) {
      if (!silent) toast.error(e?.message || 'Sync failed');
    } finally {
      syncAttendeesMeetingLockRef.current.delete(mid);
      setSyncingAtt(false);
    }
  }, [focusedMeeting?.id, workspace?.id, loadDetail, onRefreshSeries]);

  /**
   * User confirmed the calendar-deleted prompt: keep the history but flip this occurrence to
   * cancelled (manager RLS allows the direct update). Dismissing leaves the row untouched.
   */
  const confirmCancelCalendarDeleted = useCallback(async () => {
    const mid = focusedMeeting?.id;
    if (!mid) return;
    setCancellingCalendarDeleted(true);
    try {
      const { error } = await supabase
        .from('meetings')
        .update({ status: 'cancelled', updated_at: new Date().toISOString() })
        .eq('id', mid);
      if (error) throw error;
      toast.success('Meeting marked cancelled - history kept');
      setCalendarDeletedOpen(false);
      await loadDetail();
      await onRefreshSeries();
    } catch (e: any) {
      toast.error(e?.message || 'Could not update meeting');
    } finally {
      setCancellingCalendarDeleted(false);
    }
  }, [focusedMeeting?.id, loadDetail, onRefreshSeries]);

  useEffect(() => {
    const mid = focusedMeeting?.id;
    if (!mid) return;
    if (!focusedMeeting?.calendar_event_id) return;
    // Only the series OWNER's calendar holds this event. Auto-syncing as an admin/attendee queries
    // the caller's own calendar (which lacks the event) → a slow provider round-trip that fails
    // (often a 500) and stalls the detail open. Owners sync from their calendar; everyone else just
    // views. (Owner-side freshness is also maintained by the pre-nudge/briefing + day-ahead syncs.)
    if (!isOwner) return;
    if (autoSyncedAttendeesMeetingIdsRef.current.has(mid)) return;
    autoSyncedAttendeesMeetingIdsRef.current.add(mid);
    void syncAttendeesFromCalendar({ silent: true });
  }, [focusedMeeting?.id, focusedMeeting?.calendar_event_id, isOwner, syncAttendeesFromCalendar]);

  const focusDay = focusedMeeting?.starts_at ? new Date(focusedMeeting.starts_at) : null;
  const focusEndsAt = focusedMeeting?.ends_at ? new Date(focusedMeeting.ends_at) : null;
  const hasMeetingEnded = Boolean(
    focusEndsAt && !Number.isNaN(focusEndsAt.getTime()) && focusEndsAt.getTime() <= Date.now(),
  );
  const transcriptText = String(transcript?.transcript_text || '').trim();
  const transcriptHasText = transcriptText.length > 0;
  const transcriptStatus = String(transcript?.status || '').toLowerCase();
  const extractionStatus = String(transcript?.extraction_status || 'pending').toLowerCase();
  const transcriptUpdatedAtMs = transcript?.updated_at
    ? new Date(String(transcript.updated_at)).getTime()
    : Number.NaN;
  const endedAtMs = focusEndsAt ? focusEndsAt.getTime() : Number.NaN;
  const stallReferenceMs = Number.isNaN(transcriptUpdatedAtMs) ? endedAtMs : transcriptUpdatedAtMs;
  const isTranscriptStalledPostEnd = Boolean(
    hasMeetingEnded &&
    !transcriptHasText &&
    (transcriptStatus === 'queued' || transcriptStatus === 'processing') &&
    !Number.isNaN(stallReferenceMs) &&
    Date.now() - stallReferenceMs >= 10 * 60 * 1000,
  );
  const isTranscriptFailed = transcriptStatus === 'failed' || transcriptStatus === 'not_admitted';
  const isTranscriptComplete = transcriptHasText;
  const hasTranscriptRow = Boolean(transcript);
  const hasProviderBot = Boolean(transcript?.provider_bot_id);
  /** Schedule explicitly cancelled (via Remove bot from call, calendar deletion, etc.). No more work will happen. */
  const isScheduleCancelled =
    String(transcript?.provider_schedule_status || '') === 'cancelled' && !transcriptHasText;
  /** Meeting is configured to not produce a transcript (e.g. no video URL). No bot will ever join. */
  const isNotConfigured = transcriptStatus === 'not_configured';
  /**
   * The bot's live run is over even if the scheduled end time hasn't passed. Participants can leave
   * early, so Recall reports the bot left and finishes the transcript/insights well before `ends_at`.
   * In that window `hasMeetingEnded` is still false - which used to leave the live-bot controls
   * (Refresh bot status / Remove bot from call) on screen with nothing left to act on.
   */
  const botRunComplete = Boolean(
    transcript?.bot_left_at ||
      transcriptStatus === 'processing' ||
      transcriptStatus === 'ready' ||
      (transcriptHasText && extractionStatus === 'ready'),
  );
  /** Treat as "joined the call" only when Recall indicated recording/in-call or we have capture evidence - not mere lifecycle processing/ready (waiting room timeout looks like that too). */
  const botJoinedEvidence = Boolean(
    transcript?.bot_joined_at ||
      transcriptHasText ||
      transcriptStatus === 'recording' ||
      (transcriptStatus === 'processing' && Boolean(transcript?.bot_joined_at)) ||
      (transcriptStatus === 'ready' && transcriptHasText),
  );
  const botDidNotJoin = hasMeetingEnded && !botJoinedEvidence;
  /** Desktop-app capture (Recall Desktop SDK) - no bot involved, so bot-centric fallbacks,
   *  strict-mode gating, and the Invite bot affordance don't apply. Backend labels are
   *  authoritative ("Recording from Tim's Mac"). */
  const isDesktopCapture = String((transcript as any)?.capture_source || '').startsWith('desktop');
  /** Ad-hoc in-person capture (no calendar event / bot). Drives the "One-off in person" label and
   *  hides bot/orchestration/notification config + the Agenda/Chat/Check-ins tabs, which are moot. */
  const isOneOffInPerson = String((transcript as any)?.capture_source || '') === 'desktop_adhoc';
  /** Series is SET to record from the desktop app but nothing has been captured yet. Distinct from
   *  isDesktopCapture (a desktop recording exists): this is the intent, known from enrolment, and it
   *  means every bot-shaped affordance and label would be a lie. */
  const isDesktopSeries = seriesCaptureMethodLocal === 'desktop' && !isDesktopCapture;
  const HIDDEN_INPERSON_TABS = ['agenda', 'checkins', 'chat'];
  /** When a tab is hidden for in-person meetings, fall through to Notes rather than a blank pane. */
  const activeSessionTab =
    isOneOffInPerson && HIDDEN_INPERSON_TABS.includes(sessionTab) ? 'summary' : sessionTab;

  /** Plain-text starting point for the "Email notes & decisions" share option. Fully editable in
   *  the dialog before sending; assembled here where the loaded content lives. */
  const summaryEmailDraft = (() => {
    const lines: string[] = [];
    const when = focusedMeeting?.starts_at
      ? new Date(focusedMeeting.starts_at).toLocaleString(undefined, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : '';
    const shortSummary = String((transcript as any)?.short_summary || '').trim();
    const longSummary = String((transcript as any)?.long_summary || '').trim();
    const manualNotes = notes.trim();
    lines.push(series?.title || 'Meeting notes');
    if (when) lines.push(when);
    // Lead with the AI recap (short + long summary) - the manual catch-up notes are often blank.
    if (shortSummary) lines.push('', shortSummary);
    if (longSummary) lines.push('', longSummary);
    if (manualNotes) lines.push('', 'NOTES', manualNotes);
    if (decisions.length) {
      lines.push('', 'DECISIONS', decisions.map((d: any) => `• ${String(d.body || '').trim()}`).join('\n'));
    }
    // Skip the ACTION ITEMS block when the summary's "Next steps" already mirrors it - no duplication.
    if (actions.length && !actionsMirrorNextSteps(longSummary, actions)) {
      lines.push(
        '',
        'ACTION ITEMS',
        actions
          .map((a: any) => {
            const who = String(a.assignee_name || a.assignee_email || '').trim();
            return `• ${String(a.title || '').trim()}${who ? ` - ${who}` : ''}`;
          })
          .join('\n'),
      );
    }
    if (!shortSummary && !longSummary && !manualNotes && !decisions.length && !actions.length) {
      lines.push('', 'No notes, decisions, or action items were captured for this meeting yet.');
    }
    // Flatten markdown (## ** em-dashes) so the email body reads clean.
    return stripMarkdownForPaste(lines.join('\n'));
  })();
  const fallbackBotStatusLabel = !seriesBotEnabledLocal
    ? 'Not scheduled'
    : transcriptStatus === 'not_configured'
      // Distinct from 'failed': the meeting itself isn't configured for capture
      // (no video URL, no start time). User-actionable, not a bot bug.
      ? 'Bot needs a meeting URL'
      : transcriptStatus === 'joining'
        ? 'Bot joining'
        : transcriptStatus === 'recording'
          ? 'Bot recording'
          : transcriptStatus === 'failed'
            ? String(transcript?.failure_reason || '').includes('30 seconds')
              ? 'No bot at meeting start'
              : 'Bot failed'
            : transcriptStatus === 'not_admitted'
              ? 'Waiting room - not admitted'
              // Bot only "ready" once Recall has actually queued it (provider_bot_id set).
              // Without a provider bot, series is enabled but nothing has been scheduled yet → "Bot scheduled".
              : hasProviderBot
                ? 'Bot ready'
                : 'Bot scheduled';
  const rawBotStatusLabel = transcript?.bot_status_label || fallbackBotStatusLabel;
  /** Backend labels drive Bot line; post-meeting fallback uses capture evidence + provider state. */
  const botStatusLabel = isDesktopSeries
    ? (hasMeetingEnded ? 'No desktop recording' : 'Records from the Desktop App')
    : isScheduleCancelled
    ? 'Bot cancelled'
    : isDesktopCapture && transcript?.bot_status_label?.trim()
      ? transcript.bot_status_label.trim()
    : hasMeetingEnded
      ? !seriesBotEnabledLocal && !hasTranscriptRow
        ? 'Not scheduled'
        : botJoinedEvidence
          ? 'Bot joined'
          // No bot was ever scheduled (no provider_bot_id) → it definitively didn't join,
          // regardless of any stale "Bot ready" label written by enqueueTranscriptCapture.
          // Only trust the stored label when there's at least a provider bot id behind it.
          : !hasProviderBot
            ? 'Bot did not join'
            : transcript?.bot_status_label?.trim() || 'Bot did not join'
      : rawBotStatusLabel;
  /** Aligns with Bot line "Waiting room - not admitted"; do not offer retrieval until status advances (Refresh is ok). */
  const isWaitingRoomNotAdmitted =
    transcriptStatus === 'not_admitted' ||
    rawBotStatusLabel === 'Waiting room - not admitted' ||
    String(transcript?.bot_status_label || '').trim() === 'Waiting room - not admitted';
  const transcriptStatusLabel = isDesktopSeries
    ? (hasMeetingEnded
      ? 'Not available'
      : 'Waiting for a desktop recording')
    : isScheduleCancelled || isNotConfigured
    ? 'Not available'
    : hasMeetingEnded
      ? (!seriesBotEnabledLocal && !hasTranscriptRow
        ? 'Transcribing off'
        : botDidNotJoin
          ? 'Not available'
          : isTranscriptComplete
            ? 'Transcript complete'
            : isTranscriptFailed
              ? 'Transcript failed'
              : isTranscriptStalledPostEnd
                ? 'Transcript delayed'
                : !hasTranscriptRow
                  ? 'Transcript scheduled'
                  : 'Transcript pending')
      : (!seriesBotEnabledLocal && !hasTranscriptRow
        ? 'Transcribing off'
        : transcript?.transcript_status_label ||
          (transcriptHasText
            ? 'Transcript complete'
            : transcriptStatus === 'failed' || transcriptStatus === 'not_admitted'
              ? 'Transcript failed'
              // 'joining' (incl. in-waiting-room) is still pre-capture - stay on 'scheduled'
              // until the bot is actually recording.
              : transcriptStatus === 'recording' || transcriptStatus === 'processing'
                ? 'Transcript pending'
                : 'Transcript scheduled'));
  const insightsStatusLabel = isDesktopSeries
    ? (hasMeetingEnded ? 'Not available' : 'Insights pending')
    : isScheduleCancelled || isNotConfigured
    ? 'Not available'
    : hasMeetingEnded
      ? (botDidNotJoin
        ? 'Not available'
        : transcript?.insights_status_label ||
          (extractionStatus === 'ready'
            ? 'Insights complete'
            : extractionStatus === 'failed'
              ? 'Insights failed'
              : 'Insights pending'))
      : (extractionStatus === 'ready'
        ? 'Insights complete'
        : extractionStatus === 'failed'
          ? 'Insights failed'
          : 'Not started');
  // Direct passthrough now that 'Transcript scheduled' explicitly distinguishes pre-join state
  // from in-flight 'Transcript pending'. No more remap to 'Not started'.
  const transcriptStatusLabelDisplay = transcriptStatusLabel;
  // Gate every "did not join / failed / not configured" warning on having actually fetched the
  // transcript row. Without this, transcript is briefly null after a meeting switch and any meeting
  // with hasMeetingEnded=true would flash the warning before the real state lands.
  const showTranscriptWarningBanner = Boolean(
    transcriptLoaded &&
    !isDesktopSeries &&
    !transcriptHasText &&
      (isTranscriptStalledPostEnd ||
        transcript?.status === 'not_admitted' ||
        transcript?.status === 'failed' ||
        transcript?.status === 'not_configured'),
  );
  /**
   * Native-salvage hint: when the Recall bot fails on a Google meeting, the backend trigger
   * enqueues a native_salvage check at ends_at + 45 min, with retries at +90 min and +135 min.
   * Surface that timeline to the user so they're not staring at a "bot did not join" warning
   * wondering whether anything else is happening. States:
   *   • salvage found native links → tell them to open the recording / notes tabs
   *   • salvage waiting on reconnect → tell them to reconnect Google Calendar
   *   • salvage attempted, no artifact → tell them nothing was found
   *   • otherwise (queued / pending / pre-window) → next-attempt timestamp + retry note
   * Only fires for series whose calendar_provider === 'google'.
   */
  /** Native Google Drive links to the original recording / Gemini notes Doc, when salvage
   *  found them. We only ever link out to these - we don't import the text (that would need a
   *  restricted Drive/Docs scope we deliberately don't request). */
  const nativeRecordingUrl: string | null = (() => {
    const u = (transcript as { native_recording_url?: string | null } | null)?.native_recording_url;
    return typeof u === 'string' && u.trim() ? u.trim() : null;
  })();
  const nativeTranscriptUrl: string | null = (() => {
    const u = (transcript as { native_transcript_url?: string | null } | null)?.native_transcript_url;
    return typeof u === 'string' && u.trim() ? u.trim() : null;
  })();
  const hasNativeLinks = Boolean(nativeRecordingUrl || nativeTranscriptUrl);

  const nativeSalvageHint: string | null = (() => {
    if (String(series?.calendar_provider || '').toLowerCase() !== 'google') return null;
    // Fire as soon as the bot is rejected - user shouldn't have to wait for the meeting
    // end-time to learn that we'll fall back to a native Drive check. The salvage timestamps
    // are computed off ends_at, which is fine to surface even while the meeting is in progress.
    if (transcript?.status !== 'not_admitted' && transcript?.status !== 'failed') return null;
    if (transcriptHasText) return null; // salvage already succeeded or transcript came in
    const salvageStatus = String((transcript as { native_salvage_status?: string | null } | null)?.native_salvage_status || '').toLowerCase();
    if (salvageStatus === 'ready') return null;
    if (salvageStatus === 'waiting_reconnect') {
      return 'Native recording check is paused - the meeting owner needs to reconnect Google Calendar.';
    }
    // Google saved native artifacts (recording and/or Gemini notes). We don't auto-import
    // the text, but we link straight to the originals in Google Drive.
    if (hasNativeLinks || salvageStatus === 'links_only') {
      return 'Google Meet saved a native recording and/or notes for this meeting - open them from the Recording and Transcript tabs below.';
    }
    if (salvageStatus === 'no_artifact') {
      return 'No native recording or transcript was found in Google Drive for this meeting.';
    }
    if (salvageStatus === 'failed') {
      return 'Native recording check failed. Ask the meeting owner to retry from the transcript tab.';
    }
    // Compute upcoming attempt timestamp. Attempts at ends_at + 45 / 90 / 135 min.
    if (Number.isNaN(endedAtMs)) {
      return 'We’ll automatically check Google Drive for a native recording within ~2.5 h of the meeting ending.';
    }
    const fmt = (ms: number) =>
      new Date(ms).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    const t1 = endedAtMs + 45 * 60 * 1000;
    const t2 = endedAtMs + 90 * 60 * 1000;
    const t3 = endedAtMs + 135 * 60 * 1000;
    const now = Date.now();
    if (now < t1) {
      return `We’ll auto-check Google Drive for a native recording at ${fmt(t1)} (retries at ${fmt(t2)} and ${fmt(t3)} if needed).`;
    }
    if (now < t2) {
      return `Native recording check in progress - next retry at ${fmt(t2)} (last retry ${fmt(t3)} if needed).`;
    }
    if (now < t3) {
      return `Native recording check in progress - last retry at ${fmt(t3)}.`;
    }
    return null; // past the salvage window with no signal - best to stay quiet
  })();

  const transcriptWarningText = isTranscriptStalledPostEnd
    ? 'Transcript is taking longer than expected after the meeting ended. Tap Retrieve transcript to fetch it from Recall.'
    : transcript?.status === 'not_admitted'
      ? `Bot did not join this meeting.${nativeSalvageHint ? ' ' + nativeSalvageHint : ''}`
      : transcript?.status === 'failed'
        ? `${transcript?.failure_reason || 'Transcript capture failed after the meeting.'}${nativeSalvageHint ? ' ' + nativeSalvageHint : ''}`
        : 'Transcript capture is not configured for this meeting yet.';
  /**
   * Status icon tone derived from the visible label. Three buckets:
   *   - positive (green): completion / success - Bot joined, Transcript complete, Insights complete
   *   - negative (red): failures and unrecoverable terminal states - failed, did not join, cancelled, not available
   *   - neutral (foreground/black): pending, scheduled, in-flight, opted out - work hasn't completed yet
   */
  const toneForLabel = (label: string): 'positive' | 'negative' | 'neutral' => {
    const l = label.trim().toLowerCase();
    if (l === 'bot joined' || l === 'transcript complete' || l === 'insights complete') return 'positive';
    if (
      l === 'bot failed' ||
      l === 'bot cancelled' ||
      l === 'bot did not join' ||
      l === 'no desktop recording' ||
      l === 'no bot at meeting start' ||
      l === 'bot needs a meeting url' ||
      l === 'transcript failed' ||
      l === 'insights failed' ||
      l === 'transcript delayed' ||
      l === 'not available' ||
      l.startsWith('waiting room') ||
      l.startsWith('bot joined - transcript unavailable')
    ) return 'negative';
    return 'neutral';
  };
  const toneClass = (tone: 'positive' | 'negative' | 'neutral'): string =>
    tone === 'positive' ? 'text-green-600 dark:text-green-400' :
    tone === 'negative' ? 'text-red-600 dark:text-red-400' :
    'text-foreground/70';
  const botIconClass = toneClass(toneForLabel(botStatusLabel));
  const transcriptIconClass = toneClass(toneForLabel(transcriptStatusLabel));
  const insightsIconClass = toneClass(toneForLabel(insightsStatusLabel));
  /** When set, shown under Status: - stalled, not admitted, failed, not configured, or post-meeting bot did not join. */
  const transcriptStatusHeadline = showTranscriptWarningBanner
    ? transcriptWarningText
    : transcriptLoaded && isDesktopSeries && hasMeetingEnded
      ? 'No desktop recording was made for this meeting. Nothing recorded it, so there is no transcript.'
    : transcriptLoaded && hasMeetingEnded && botDidNotJoin && !showTranscriptWarningBanner && !isDesktopSeries
      ? 'Bot did not join this meeting.'
      : null;
  const transcriptTabBadge = transcriptStatusLabelDisplay
    ? `(${transcriptStatusLabelDisplay.toLowerCase()})`
    : '';
  const strictFutureNotReadyMode =
    !hasMeetingEnded && !seriesBotEnabledLocal && !isDesktopCapture && !isDesktopSeries;
  const canInviteBot =
    !seriesBotEnabledLocal && canManage && !hasMeetingEnded && !isDesktopCapture && !isDesktopSeries;
  const canRefreshBotStatus =
    canManage &&
    !isDesktopSeries &&
    !isScheduleCancelled &&
    !strictFutureNotReadyMode &&
    !botRunComplete &&
    hasProviderBot &&
    (!hasMeetingEnded || isWaitingRoomNotAdmitted);
  const canRetrieveTranscript =
    canManage &&
    !isDesktopSeries &&
    hasMeetingEnded &&
    !isTranscriptComplete &&
    !isWaitingRoomNotAdmitted &&
    (hasProviderBot || seriesBotEnabledLocal || transcriptStatus === 'failed' || isTranscriptStalledPostEnd);
  /** Bot failed, was removed/cancelled, or never got created - let the user re-invite a fresh bot
   *  (so "Remove bot from call" leaves a one-click way back in). */
  const canRetryBotCapture =
    !hasMeetingEnded &&
    canManage &&
    !isDesktopSeries &&
    (transcriptStatus === 'failed' ||
      ((!hasProviderBot || isScheduleCancelled) && hasTranscriptRow && seriesBotEnabledLocal));
  /** A live bot exists for an in-progress meeting - let the user yank it out of the call. */
  const canRemoveBotFromCall =
    !hasMeetingEnded &&
    !botRunComplete &&
    canManage &&
    hasProviderBot &&
    String(transcript?.provider_schedule_status || '') !== 'cancelled' &&
    transcriptStatus !== 'failed' &&
    transcriptStatus !== 'not_admitted';
  // Available whenever a finished meeting has a transcript - owners can re-run insights to recover a
  // `failed` extraction, refresh a stale `ready` one, or backfill newly-added outputs (e.g. agenda
  // block summaries). Hidden only while a run is actively `processing` to avoid a double-trigger.
  const canRegenerateInsights =
    canManage && hasMeetingEnded && transcriptHasText && extractionStatus !== 'processing';
  const focusDayNum =
    focusDay && !Number.isNaN(focusDay.getTime()) ? focusDay.getDate() : '-';
  const focusMonthYear =
    focusDay && !Number.isNaN(focusDay.getTime())
      ? focusDay.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
      : '-';
  const sessionDateVersusToday = formatMeetingDateVersusToday(focusedMeeting?.starts_at);
  const doneActions = actions.filter((a) => a.status === 'done').length;
  const totalActions = actions.length;
  const hasAgendaItems = agenda.length > 0;
  const hasSessionNotes = notes.trim().length > 0;
  const acceptedAttendeesCount = attendees.filter((a) => a?.rsvp === 'accepted').length;
  const attendeeCount = attendees.length;
  const attendeeLastSynchLabel = useMemo(() => {
    let latestMs = Number.NaN;
    for (const a of attendees) {
      const candidates = [a?.updated_at, a?.created_at, a?.synced_at];
      for (const raw of candidates) {
        if (!raw) continue;
        const ms = new Date(String(raw)).getTime();
        if (Number.isNaN(ms)) continue;
        if (Number.isNaN(latestMs) || ms > latestMs) latestMs = ms;
      }
    }
    if (Number.isNaN(latestMs)) return 'Unknown';
    return new Date(latestMs).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  }, [attendees]);
  const focusScore = Math.min(
    99,
    52 + decisions.length * 8 + doneActions * 3,
  );

  const quickInvite = () => {
    if (focusedMeeting?.video_meeting_url) {
      window.open(focusedMeeting.video_meeting_url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (inboundAddress) {
      void navigator.clipboard.writeText(inboundAddress);
      toast.success('Copied workspace calendar address');
      return;
    }
    toast.message('Add a video link or connect a calendar to share invites.');
  };

  const focusedIndex = focusedMeetingId && sessionsChronoDesc.length
    ? sessionsChronoDesc.findIndex((x) => x.id === focusedMeetingId)
    : -1;

  const sessionCount = sessionsChronoDesc.length;
  const seriesDescView = seriesDescription(series);

  return (
    <div className="flex h-[calc(100dvh-7rem)] max-h-[calc(100dvh-7rem)] min-h-0 flex-col overflow-hidden pb-2 sm:h-[calc(100dvh-6.5rem)] sm:max-h-[calc(100dvh-6.5rem)]">
      <div className="shrink-0 space-y-4">
      <header className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
        <div className="space-y-1">
          <h1 className="font-display text-xl font-semibold tracking-tight text-foreground md:text-2xl">
            {series.title}
          </h1>
          <div className="max-w-2xl space-y-1">
            <p className="text-sm leading-snug text-muted-foreground">
              {seriesDescView ? (
                <span className="line-clamp-2">{seriesDescView}</span>
              ) : (
                <span className="italic text-muted-foreground/90">
                  {seriesUsesCalendarSource(series)
                    ? 'No description yet - add one in Series settings for context beside your calendar invite.'
                    : 'No description yet.'}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <div className="flex flex-wrap justify-end gap-2">
            {focusedMeeting?.video_meeting_url && (
              <Button
                type="button"
                variant="outline"
                className="border-primary/40 bg-primary/10 text-primary hover:bg-primary/15"
                onClick={quickInvite}
              >
                <Video className="mr-1.5 h-4 w-4" />
                Join video call
              </Button>
            )}
            {canManage &&
              transcript?.provider_bot_id &&
              (transcript?.status === 'recording' ||
                transcript?.status === 'processing' ||
                transcript?.recording_paused) && (
              <Button
                type="button"
                variant="outline"
                disabled={transcriptBusy}
                onClick={() =>
                  triggerTranscriptAction(transcript?.recording_paused ? 'resume_recording' : 'pause_recording')
                }
              >
                {transcript?.recording_paused ? (
                  <>
                    <Play className="mr-1.5 h-4 w-4" />
                    Resume recording
                  </>
                ) : (
                  <>
                    <Pause className="mr-1.5 h-4 w-4" />
                    Pause recording
                  </>
                )}
              </Button>
            )}
            {canManage && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShareDialogOpen(true)}
              >
                <Share2 className="mr-1.5 h-4 w-4" />
                Share
              </Button>
            )}
            {canManage && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setSeriesFlyoutOpen(true)}
              >
                <Settings className="mr-1.5 h-4 w-4" />
                Series settings
              </Button>
            )}
          </div>
          {isOneOffInPerson ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  tabIndex={0}
                  className="inline-flex cursor-help items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-primary"
                >
                  <Users className="h-3 w-3" />
                  Recorded on desktop
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="end" className="max-w-xs text-xs leading-relaxed">
                Recorded with the Upstand desktop app rather than a calendar meeting with a bot.
                There&rsquo;s no agenda or check-ins; the recording was captured on the spot.
              </TooltipContent>
            </Tooltip>
          ) : isDesktopSeries ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  tabIndex={0}
                  className="inline-flex cursor-help items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground"
                >
                  <Monitor className="h-3 w-3" />
                  Desktop App
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="end" className="max-w-xs text-xs leading-relaxed">
                No bot joins this meeting. The Upstand desktop app records it from an attendee&rsquo;s
                computer. Nothing is recorded if no attendee has the app running.
              </TooltipContent>
            </Tooltip>
          ) : !series.is_recurring_series ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  tabIndex={0}
                  className="inline-flex cursor-help items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground"
                >
                  <Info className="h-3 w-3" />
                  One-off
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="end" className="max-w-xs text-xs leading-relaxed">
                This calendar event is not recurring - there is no future series in the source
                calendar. History and &ldquo;copy from last session&rdquo; only apply if you add
                more instances manually.
              </TooltipContent>
            </Tooltip>
          ) : null}
        </div>
      </header>
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col">
        {sessionCount > 0 && (
          <MeetingOccurrencePicker
            sessions={sessionsChronoDesc}
            focusedIndex={focusedIndex}
            onSelect={(id) => setFocusedMeetingId(id)}
          />
        )}
        <div className="flex min-h-0 flex-1 flex-col">
          <div
            ref={bindTimelineScrollRef}
            tabIndex={-1}
            className="h-full min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain outline-none [-webkit-overflow-scrolling:touch]"
          >
            <div className="pb-6 pt-2">
              <article className="overflow-hidden rounded-lg border border-outline-variant/10 bg-card shadow-sm">
                <div className="border-b border-outline-variant/10 px-5 py-4 md:px-6 md:py-5">
                  <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                    <span className="inline-block rounded-full bg-tertiary-container/20 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-on-tertiary-container">
                      {formatSessionTimeRange(focusedMeeting)}
                    </span>
                    <AttendeeBubbleStack attendees={focusedAttendees} maxVisible={2} showRsvp />
                  </div>

                  {(transcript as any)?.recap_status === 'awaiting_approval' && canManage && (
                    <div className="mb-6 flex flex-col gap-3 rounded-lg border border-amber-300/60 bg-amber-50/80 px-4 py-3 dark:border-amber-700/60 dark:bg-amber-950/30 sm:flex-row sm:items-center sm:justify-between">
                      <div className="space-y-0.5">
                        <p className="text-sm font-semibold text-amber-950 dark:text-amber-100">
                          Recap awaiting your approval
                        </p>
                        <p className="text-xs text-amber-900/80 dark:text-amber-200/80">
                          Review the summary, decisions, and action items below.
                          {(transcript as any)?.recap_approval_deadline_at && (
                            <>
                              {' '}Auto-releases{' '}
                              {new Date((transcript as any).recap_approval_deadline_at).toLocaleString('en-GB', {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                              .
                            </>
                          )}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-700/40 text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/40"
                          onClick={() => void decideRecapApproval('cancel')}
                          disabled={approvalBusy !== null}
                        >
                          {approvalBusy === 'cancel' ? (
                            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                          ) : null}
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          className="bg-emerald-600 text-white hover:bg-emerald-700"
                          onClick={() => void decideRecapApproval('approve')}
                          disabled={approvalBusy !== null}
                        >
                          {approvalBusy === 'approve' ? (
                            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                          ) : null}
                          Approve &amp; send
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="mb-6 flex flex-wrap gap-x-6 gap-y-2 border-b border-outline-variant/10">
                    {(
                      [
                        [
                          'agenda',
                          'Agenda',
                          List,
                          null,
                          'text-muted-foreground',
                          hasAgendaItems ? CheckCircle2 : XCircle,
                          hasAgendaItems ? 'text-emerald-600' : 'text-muted-foreground',
                        ],
                        ['checkins', 'Check-ins', MessageSquare, `(${checkIns.length})`, '', null, ''],
                        [
                          'summary',
                          'Notes',
                          Pencil,
                          null,
                          'text-muted-foreground',
                          hasSessionNotes ? CheckCircle2 : XCircle,
                          hasSessionNotes ? 'text-emerald-600' : 'text-muted-foreground',
                        ],
                        ['actions', 'Action items', ListChecks, `(${totalActions})`, '', null, ''],
                        ['decisions', 'Decisions', Gavel, `(${decisions.length})`, '', null, ''],
                        ['transcript', 'Transcript', FileText, transcriptTabBadge, '', null, ''],
                        ['chat', 'Chat', MessagesSquare, chatMessages.length ? `(${chatMessages.length})` : '', '', null, ''],
                        ['recording', 'Recording', Video, '', '', null, ''],
                      ] as const
                    )
                      .filter(([id]) => !(isOneOffInPerson && HIDDEN_INPERSON_TABS.includes(id)))
                      .map(([id, label, Icon, countLabel, iconClassName, StatusIcon, statusIconClassName]) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setSessionTab(id)}
                        className={cn(
                          'pb-3 text-sm font-medium transition-colors',
                          activeSessionTab === id
                            ? 'border-b-2 border-primary font-semibold text-primary'
                            : 'text-muted-foreground hover:text-primary',
                        )}
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <Icon className={cn('h-3.5 w-3.5', iconClassName)} strokeWidth={1.8} />
                          <span>{label}</span>
                          {countLabel ? <span>{countLabel}</span> : null}
                          {StatusIcon ? (
                            <StatusIcon className={cn('h-3.5 w-3.5', statusIconClassName)} strokeWidth={1.8} />
                          ) : null}
                        </span>
                      </button>
                    ))}
                  </div>

                  <div className="space-y-6">
                      {activeSessionTab === 'agenda' && (
                        <>
                          {renderCopyButton('agenda')}
                          {canEditAgenda && (
                            <div className="mb-1 space-y-2">
                              <div className="flex w-full flex-wrap items-start gap-3">
                                <Textarea
                                  placeholder="New agenda item - paste multiple lines to add several at once"
                                  value={newAgendaTitle}
                                  onChange={(e) => setNewAgendaTitle(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                      e.preventDefault();
                                      void addAgendaItem();
                                    }
                                  }}
                                  rows={3}
                                  className="min-h-[5.5rem] min-w-[min(100%,240px)] flex-1 resize-y"
                                />
                                <div className="flex w-full min-w-[min(100%,12rem)] shrink-0 flex-col gap-2 sm:w-auto sm:min-w-[12rem]">
                                  {canManage ? (
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      className="w-full justify-center"
                                      onClick={copyAgenda}
                                    >
                                      Copy from last session
                                    </Button>
                                  ) : null}
                                  <Button
                                    type="button"
                                    size="sm"
                                    className="w-full justify-center"
                                    onClick={() => void addAgendaItem()}
                                    disabled={agendaAddBusy || !splitAgendaInputLines(newAgendaTitle).length}
                                  >
                                    {agendaAddBusy ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      'Add'
                                    )}
                                  </Button>
                                </div>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Use line breaks for a list; leading •, -, *, or 1. prefixes are stripped.{' '}
                                <span className="whitespace-nowrap">⌘/Ctrl + Enter</span> to add.
                              </p>
                            </div>
                          )}
                          <div className="space-y-8">
                            {agenda.map((item, i) => (
                              <div
                                key={item.id}
                                className={cn(i > 0 && 'opacity-80')}
                              >
                                <div className="flex items-start gap-2">
                                  {canEditAgenda && agendaEditingId !== item.id ? (
                                    <div className="flex shrink-0 flex-col">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                                        aria-label={`Move agenda item ${i + 1} up`}
                                        disabled={
                                          agendaReorderBusy ||
                                          Boolean(agendaEditingId) ||
                                          i === 0
                                        }
                                        onClick={() => void reorderAgendaStep(i, -1)}
                                      >
                                        <ChevronUp className="h-4 w-4" strokeWidth={2} />
                                      </Button>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                                        aria-label={`Move agenda item ${i + 1} down`}
                                        disabled={
                                          agendaReorderBusy ||
                                          Boolean(agendaEditingId) ||
                                          i >= agenda.length - 1
                                        }
                                        onClick={() => void reorderAgendaStep(i, 1)}
                                      >
                                        <ChevronDown className="h-4 w-4" strokeWidth={2} />
                                      </Button>
                                    </div>
                                  ) : null}
                                  <div className="min-w-0 flex-1 space-y-2">
                                    {canEditAgenda && agendaEditingId === item.id ? (
                                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                        <span className="shrink-0 font-display text-lg font-bold text-muted-foreground">
                                          {i + 1}.
                                        </span>
                                        <Input
                                          value={agendaEditDraft}
                                          onChange={(e) => setAgendaEditDraft(e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                              e.preventDefault();
                                              void saveAgendaEdit();
                                            }
                                          }}
                                          className="min-w-0 flex-1 font-display text-lg font-bold"
                                          autoFocus
                                        />
                                        <div className="flex shrink-0 gap-2">
                                          <Button
                                            type="button"
                                            size="sm"
                                            onClick={() => void saveAgendaEdit()}
                                            disabled={agendaSavingId === item.id}
                                          >
                                            {agendaSavingId === item.id ? (
                                              <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                              'Save'
                                            )}
                                          </Button>
                                          <Button type="button" size="sm" variant="outline" onClick={cancelAgendaEdit}>
                                            Cancel
                                          </Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <h3 className="flex flex-wrap items-center gap-x-2 gap-y-1 font-display text-lg font-bold text-foreground">
                                        <span>
                                          {i + 1}. {item.title}
                                        </span>
                                        {canEditAgenda ? (
                                          <span className="inline-flex items-center gap-0.5">
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              className="h-8 px-2 text-muted-foreground hover:text-foreground"
                                              aria-label={`Edit agenda item ${i + 1}`}
                                              onClick={() => {
                                                setAgendaEditingId(item.id);
                                                setAgendaEditDraft(String(item.title ?? ''));
                                              }}
                                            >
                                              <Pencil className="h-4 w-4" strokeWidth={1.8} />
                                            </Button>
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              className="h-8 px-2 text-muted-foreground hover:text-destructive"
                                              aria-label={`Delete agenda item ${i + 1}`}
                                              disabled={
                                                agendaReorderBusy ||
                                                agendaDeletingId != null ||
                                                Boolean(agendaEditingId)
                                              }
                                              onClick={() =>
                                                setAgendaDeleteDialog({
                                                  id: item.id,
                                                  title: String(item.title ?? '').trim() || 'Untitled',
                                                })
                                              }
                                            >
                                              {agendaDeletingId === item.id ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                              ) : (
                                                <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                                              )}
                                            </Button>
                                          </span>
                                        ) : null}
                                      </h3>
                                    )}
                                    {item.time_minutes != null && (
                                      <p className="text-xs text-muted-foreground">{item.time_minutes} min</p>
                                    )}
                                    {item.created_by && (
                                      <p
                                        className="text-xs text-muted-foreground/80"
                                        title={item.created_at ? new Date(item.created_at).toLocaleString() : undefined}
                                      >
                                        Added by {checkInSubmitterLabel(String(item.created_by))}
                                        {item.created_at &&
                                          ` · ${new Date(item.created_at).toLocaleDateString(undefined, {
                                            month: 'short',
                                            day: 'numeric',
                                          })}`}
                                      </p>
                                    )}
                                    {/* Block notes - one editable field. The transcript-derived AI summary
                                        pre-populates it (shown until someone edits); manual edits then take over,
                                        and a re-generate never clobbers them (the pipeline writes only auto_summary).
                                        Clearing the field falls back to the AI summary again. */}
                                    {(() => {
                                      const manual = String(item.notes || '').trim();
                                      const ai = String(item.auto_summary || '').trim();
                                      const effective = manual || ai;
                                      const aiSourced = !manual && Boolean(ai);

                                      if (agendaNotesEditingId === item.id) {
                                        return (
                                          <div className="space-y-2">
                                            <Textarea
                                              rows={4}
                                              value={agendaNotesDraft}
                                              onChange={(e) => setAgendaNotesDraft(e.target.value)}
                                              placeholder="Add notes for this agenda block…"
                                              autoFocus
                                            />
                                            <div className="flex gap-2">
                                              <Button
                                                type="button"
                                                size="sm"
                                                onClick={() => void saveAgendaNotes(item.id)}
                                                disabled={agendaNotesSavingId === item.id}
                                              >
                                                {agendaNotesSavingId === item.id ? (
                                                  <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                  'Save notes'
                                                )}
                                              </Button>
                                              <Button type="button" size="sm" variant="outline" onClick={cancelAgendaNotesEdit}>
                                                Cancel
                                              </Button>
                                            </div>
                                          </div>
                                        );
                                      }

                                      if (effective) {
                                        return (
                                          <div className="space-y-1">
                                            {aiSourced && (
                                              <div className="flex items-center gap-1.5">
                                                <Sparkles className="h-3.5 w-3.5 text-primary" strokeWidth={1.8} />
                                                <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                                  AI-generated
                                                </span>
                                              </div>
                                            )}
                                            <p className="whitespace-pre-wrap leading-relaxed text-foreground/90">{effective}</p>
                                            {canEditAgenda && (
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 px-2 text-muted-foreground hover:text-foreground"
                                                onClick={() => startAgendaNotesEdit(item)}
                                              >
                                                <Pencil className="mr-1 h-3.5 w-3.5" strokeWidth={1.8} /> Edit notes
                                              </Button>
                                            )}
                                          </div>
                                        );
                                      }

                                      if (canEditAgenda) {
                                        return (
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 px-2 text-muted-foreground hover:text-foreground"
                                            onClick={() => startAgendaNotesEdit(item)}
                                          >
                                            <Plus className="mr-1 h-4 w-4" strokeWidth={1.8} /> Add notes
                                          </Button>
                                        );
                                      }

                                      return (
                                        <p className="text-sm text-muted-foreground">No notes captured for this block.</p>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            ))}
                            {decisions[0] && (
                              <div className="flex items-start gap-3 rounded-md bg-surface-container-low px-4 py-3">
                                <Lightbulb className="mt-0.5 h-5 w-5 shrink-0 text-primary" strokeWidth={1.5} />
                                <span className="text-sm font-medium">
                                  Decision: {decisions[0].body}
                                </span>
                              </div>
                            )}
                            {agenda.length === 0 && (
                              <p className="text-sm text-muted-foreground">No agenda items yet.</p>
                            )}
                          </div>
                        </>
                      )}

                      {activeSessionTab === 'checkins' && (
                        <div className="space-y-4">
                          {renderCopyButton('checkins')}
                          <div className="space-y-2 rounded-md border border-border bg-card p-4">
                            <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                              Add an update
                            </Label>
                            {focusedMeeting?.id && (
                              <CheckInRecorder
                                meetingId={focusedMeeting.id}
                                userId={userId}
                                onSubmitted={loadDetail}
                              />
                            )}
                          </div>
                          <div className="space-y-3 rounded-md border border-border bg-card p-4">
                            <div className="flex items-center justify-between gap-2">
                              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                Submissions
                              </Label>
                              {botCanPlay && (
                                <button
                                  type="button"
                                  onClick={() => void stopMeetingPlayback()}
                                  title="Stop the video currently playing in the meeting"
                                  className="text-xs font-medium text-muted-foreground transition-colors hover:text-destructive"
                                >
                                  Stop video
                                </button>
                              )}
                            </div>
                            {botCanPlay && (
                              <p className="text-xs text-muted-foreground">
                                The bot is in the call - use ▶ to play a check-in into the meeting.
                              </p>
                            )}
                            <CheckInList
                              items={checkIns}
                              authorLabel={checkInSubmitterLabel}
                              emptyText="No updates yet."
                              currentUserId={userId}
                              canManage={isOwner}
                              onChanged={loadDetail}
                              canPlayInMeeting={botCanPlay}
                              onPlay={playCheckInInMeeting}
                              playingId={playingCheckInId}
                            />
                          </div>
                        </div>
                      )}

                      {activeSessionTab === 'summary' && (
                        <div className="space-y-6">
                          {renderCopyButton('summary')}
                          {/* AI-generated summaries - shown once insights are ready */}
                          {transcript?.short_summary?.trim() && (
                            <div className="space-y-1.5">
                              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                Short summary
                              </Label>
                              <div className="rounded-md border border-border bg-surface-container-low/60 p-3 text-sm leading-relaxed">
                                {transcript.short_summary}
                              </div>
                            </div>
                          )}
                          {transcript?.long_summary?.trim() && (
                            <div className="space-y-1.5">
                              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                Full summary
                              </Label>
                              <MarkdownLite
                                text={transcript.long_summary}
                                className="rounded-md border border-border bg-surface-container-low/60 p-3 text-sm"
                              />
                            </div>
                          )}
                          {/* Owner: refine the AI artifacts with a free-text prompt */}
                          {isOwner &&
                            Boolean(transcript?.short_summary?.trim() || transcript?.long_summary?.trim()) && (
                              <div className="space-y-2 rounded-md border border-primary/30 bg-primary/5 p-3">
                                <div className="flex items-center gap-1.5">
                                  <Sparkles className="h-3.5 w-3.5 text-primary" strokeWidth={1.8} />
                                  <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                    Refine with a prompt
                                  </Label>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  Correct or reshape the summary, decisions, and action items together - e.g. “the action
                                  items assigned to Darren are actually Dazza”, “change all action-item owners to Dennis”,
                                  or “drop the pricing discussion from the short summary”.
                                </p>
                                <Textarea
                                  rows={2}
                                  value={refinePrompt}
                                  onChange={(e) => setRefinePrompt(e.target.value)}
                                  placeholder="Tell the assistant what to change…"
                                  disabled={refineBusy}
                                />
                                <div className="flex justify-end">
                                  <Button
                                    size="sm"
                                    onClick={() => void refineInsights()}
                                    disabled={refineBusy || !refinePrompt.trim()}
                                  >
                                    {refineBusy ? (
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                      <Sparkles className="mr-2 h-4 w-4" />
                                    )}
                                    Refine
                                  </Button>
                                </div>
                              </div>
                            )}

                          {/* Running notes */}
                          <div className="space-y-2">
                            {(transcript?.short_summary || transcript?.long_summary) && (
                              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                Your notes
                              </Label>
                            )}
                            <Textarea
                              rows={10}
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              placeholder="Running notes…"
                            />
                            <Button size="sm" onClick={saveNotes} disabled={saving}>
                              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save notes'}
                            </Button>
                          </div>
                          {/* Owner-only private notes - never shared with attendees or admins. */}
                          {isOwner && (
                            <div className="space-y-2 rounded-md border border-dashed border-border bg-surface-container-low/40 p-3">
                              <div className="flex items-center gap-1.5">
                                <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                                <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                  Private notes
                                </Label>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Only you (the meeting owner) can see these. Not shared with attendees or admins.
                              </p>
                              <Textarea
                                rows={6}
                                value={privateNotes}
                                onChange={(e) => setPrivateNotes(e.target.value)}
                                placeholder="Private notes, only visible to you…"
                              />
                              <Button size="sm" onClick={savePrivateNotes} disabled={savingPrivate}>
                                {savingPrivate ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save private notes'}
                              </Button>
                            </div>
                          )}
                        </div>
                      )}

                      {activeSessionTab === 'actions' && (
                        <div className="space-y-3">
                          {renderCopyButton('actions')}
                          <ul className="space-y-2">
                            {actions.map((a) => {
                              const pickVal = actionToAssigneePick(a, attendees);
                              const rowSelectValue =
                                actionManualEditId === a.id ? '__manual__' : pickVal;
                              return (
                                <li
                                  key={a.id}
                                  className="rounded-md border border-border px-3 py-2 text-sm"
                                >
                                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                                    {actionTitleEditId === a.id ? (
                                      <div className="flex min-w-0 flex-1 items-center gap-2">
                                        <Input
                                          className="h-8 text-sm"
                                          autoFocus
                                          value={actionTitleEditText}
                                          onChange={(e) => setActionTitleEditText(e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              void saveActionTitle(a.id, actionTitleEditText);
                                            } else if (e.key === 'Escape') {
                                              setActionTitleEditId(null);
                                              setActionTitleEditText('');
                                            }
                                          }}
                                        />
                                        <Button
                                          type="button"
                                          size="sm"
                                          className="h-8"
                                          onClick={() => void saveActionTitle(a.id, actionTitleEditText)}
                                        >
                                          Save
                                        </Button>
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="ghost"
                                          className="h-8"
                                          onClick={() => {
                                            setActionTitleEditId(null);
                                            setActionTitleEditText('');
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="group flex min-w-0 flex-1 items-center gap-2">
                                        <p className="min-w-0 flex-1 font-medium text-foreground">{a.title}</p>
                                        <button
                                          type="button"
                                          className="opacity-0 transition-opacity group-hover:opacity-100"
                                          onClick={() => {
                                            setActionTitleEditId(a.id);
                                            setActionTitleEditText(a.title || '');
                                          }}
                                          aria-label="Edit action item"
                                        >
                                          <Pencil className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                                        </button>
                                      </div>
                                    )}
                                    <div className="flex flex-wrap items-center gap-2">
                                      <Select
                                        value={rowSelectValue}
                                        onValueChange={(v) => {
                                          if (v === '__manual__') {
                                            setActionManualEditId(a.id);
                                            setActionManualEditText(
                                              (a.assignee_name || '').trim() ||
                                                actionAssigneeDisplay(a, attendees, members) ||
                                                '',
                                            );
                                            return;
                                          }
                                          if (actionManualEditId === a.id) setActionManualEditId(null);
                                          void saveActionAssignee(a.id, v);
                                        }}
                                      >
                                        <SelectTrigger className="h-8 w-[180px] text-xs">
                                          <SelectValue placeholder="Assignee" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {renderAssigneeSelectItems(
                                            pickVal,
                                            attendees,
                                            members,
                                            assigneeOrphanUserIds,
                                            assigneeOrphanEmails,
                                          )}
                                        </SelectContent>
                                      </Select>
                                      <Select
                                        value={normalizeActionItemStatus(a.status)}
                                        disabled={actionStatusSavingId === a.id}
                                        onValueChange={(v) => void saveActionStatus(a.id, v)}
                                      >
                                        <SelectTrigger
                                          className={cn(
                                            'h-8 w-[132px] shrink-0 text-xs border',
                                            actionStatusSelectTriggerClass(
                                              normalizeActionItemStatus(a.status),
                                            ),
                                          )}
                                        >
                                          <SelectValue placeholder="Status" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {ACTION_ITEM_STATUS_OPTIONS.map((s) => (
                                            <SelectItem key={s.value} value={s.value}>
                                              {s.label}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                      {actionStatusSavingId === a.id ? (
                                        <Loader2
                                          className="h-4 w-4 shrink-0 animate-spin text-muted-foreground"
                                          aria-hidden
                                        />
                                      ) : null}
                                      <button
                                        type="button"
                                        className={cn(
                                          'rounded p-1 transition-colors hover:bg-muted',
                                          actionThreadOpenId === a.id
                                            ? 'text-primary'
                                            : 'text-muted-foreground hover:text-foreground',
                                        )}
                                        onClick={() =>
                                          setActionThreadOpenId(actionThreadOpenId === a.id ? null : a.id)
                                        }
                                        aria-label="Updates and comments"
                                      >
                                        <MessageSquare className="h-3.5 w-3.5" />
                                      </button>
                                      <button
                                        type="button"
                                        className="rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                                        onClick={() => void deleteAction(a.id)}
                                        aria-label="Delete action item"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  </div>
                                  {actionThreadOpenId === a.id ? (
                                    <ActionItemThread
                                      actionItemId={a.id}
                                      currentUserId={userId || null}
                                      currentUserEmail={authUserForAgenda?.email || null}
                                    />
                                  ) : null}
                                  {actionManualEditId === a.id ? (
                                    <div className="mt-2 flex flex-wrap items-end gap-2">
                                      <div className="min-w-[200px] flex-1 space-y-1">
                                        <Label className="text-xs text-muted-foreground">
                                          Name (not on attendee list)
                                        </Label>
                                        <Input
                                          className="h-8 text-sm"
                                          placeholder="e.g. contractor or guest name"
                                          value={actionManualEditText}
                                          onChange={(e) => setActionManualEditText(e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              const trimmed = actionManualEditText.trim();
                                              void saveActionAssignee(
                                                a.id,
                                                trimmed ? `m:${encodeURIComponent(trimmed)}` : '_none',
                                              );
                                              setActionManualEditId(null);
                                            }
                                          }}
                                        />
                                      </div>
                                      <Button
                                        type="button"
                                        size="sm"
                                        className="h-8"
                                        onClick={() => {
                                          const trimmed = actionManualEditText.trim();
                                          void saveActionAssignee(
                                            a.id,
                                            trimmed ? `m:${encodeURIComponent(trimmed)}` : '_none',
                                          );
                                          setActionManualEditId(null);
                                        }}
                                      >
                                        Save name
                                      </Button>
                                    </div>
                                  ) : null}
                                </li>
                              );
                            })}
                          </ul>
                          {actions.length === 0 && (
                            <p className="text-sm text-muted-foreground">No action items yet.</p>
                          )}
                          <div className="space-y-2 border-t border-outline-variant/10 pt-4">
                            <Label className="text-xs text-muted-foreground">Add action</Label>
                            <Input
                              placeholder="What needs to be done?"
                              value={newAction}
                              onChange={(e) => setNewAction(e.target.value)}
                            />
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                              <Select
                                value={newActionAssigneePick}
                                onValueChange={(v) => {
                                  setNewActionAssigneePick(v);
                                  if (v !== '__manual__') setNewActionAssigneeManual('');
                                }}
                              >
                                <SelectTrigger className="h-9 w-full min-w-[220px] max-w-[320px] text-sm">
                                  <SelectValue placeholder="Assignee" />
                                </SelectTrigger>
                                <SelectContent>
                                  {renderAssigneeSelectItems(
                                    undefined,
                                    attendees,
                                    members,
                                    assigneeOrphanUserIds,
                                    assigneeOrphanEmails,
                                  )}
                                </SelectContent>
                              </Select>
                              {newActionAssigneePick === '__manual__' ? (
                                <Input
                                  className="h-9 min-w-[200px] flex-1 text-sm"
                                  placeholder="Type assignee name"
                                  value={newActionAssigneeManual}
                                  onChange={(e) => setNewActionAssigneeManual(e.target.value)}
                                />
                              ) : null}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Choose a synced invitee from the list, or pick &quot;Other&quot; to enter any name.
                              {attendees.length === 0
                                ? ' Calendar-linked meetings load invitees on enrol or via Re-sync / Sync RSVPs on Attendees.'
                                : ''}
                            </p>
                            <Button type="button" size="sm" onClick={addAction}>
                              Add action
                            </Button>
                          </div>
                        </div>
                      )}

                      {activeSessionTab === 'decisions' && (
                        <div className="space-y-4">
                          {renderCopyButton('decisions')}
                          <ul className="space-y-2">
                            {decisions.map((d) => (
                              <li key={d.id} className="rounded-md border border-border p-2 text-sm">
                                {decisionEditId === d.id ? (
                                  <div className="flex items-center gap-2">
                                    <Input
                                      className="h-8 text-sm"
                                      autoFocus
                                      value={decisionEditText}
                                      onChange={(e) => setDecisionEditText(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          void saveDecisionBody(d.id, decisionEditText);
                                        } else if (e.key === 'Escape') {
                                          setDecisionEditId(null);
                                          setDecisionEditText('');
                                        }
                                      }}
                                    />
                                    <Button
                                      type="button"
                                      size="sm"
                                      className="h-8"
                                      onClick={() => void saveDecisionBody(d.id, decisionEditText)}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      className="h-8"
                                      onClick={() => {
                                        setDecisionEditId(null);
                                        setDecisionEditText('');
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="group flex items-center gap-2">
                                    <span className="min-w-0 flex-1">{d.body}</span>
                                    <button
                                      type="button"
                                      className="opacity-0 transition-opacity group-hover:opacity-100"
                                      onClick={() => {
                                        setDecisionEditId(d.id);
                                        setDecisionEditText(d.body || '');
                                      }}
                                      aria-label="Edit decision"
                                    >
                                      <Pencil className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                                    </button>
                                    <button
                                      type="button"
                                      className="rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                                      onClick={() => void deleteDecision(d.id)}
                                      aria-label="Delete decision"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                          {decisions.length === 0 && (
                            <p className="text-sm text-muted-foreground">No decisions recorded yet.</p>
                          )}
                          <div className="flex gap-2">
                            <Input
                              placeholder="New decision"
                              value={newDecision}
                              onChange={(e) => setNewDecision(e.target.value)}
                            />
                            <Button type="button" onClick={addDecision}>
                              Add
                            </Button>
                          </div>
                        </div>
                      )}


                      {activeSessionTab === 'chat' && (
                        <div className="space-y-3">
                          {renderCopyButton('chat')}
                          {chatMessages.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                              No in-meeting chat was captured for this meeting.
                            </p>
                          ) : (
                            <ul className="space-y-2">
                              {chatMessages.map((m) => (
                                <li key={m.id} className="rounded-lg border border-border/60 bg-card p-3">
                                  <div className="mb-0.5 flex items-baseline justify-between gap-2">
                                    <span className="text-sm font-medium text-foreground">
                                      {m.sender_name || 'Unknown'}
                                    </span>
                                    {m.sent_at ? (
                                      <span className="shrink-0 text-xs text-muted-foreground">
                                        {new Date(m.sent_at).toLocaleString(undefined, {
                                          day: 'numeric',
                                          month: 'short',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                        })}
                                      </span>
                                    ) : null}
                                  </div>
                                  <p className="whitespace-pre-wrap text-sm text-muted-foreground">{m.body}</p>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}

                      {activeSessionTab === 'transcript' && (
                        <div className="space-y-5">
                          {renderCopyButton('transcript')}
                          {/* Phase A - speaker attribution: out-of-date banner, resend, reassignment dialog.
                              Speaker attribution is owner-managed (no admin bypass); attendees may
                              additionally self-claim their own label (see the transcript below). */}
                          {isOwner &&
                            transcript &&
                            Number(transcript.resolutions_version || 0) >
                              Number(transcript.resolutions_applied_version || 0) && (
                              <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300/60 bg-amber-50/80 px-4 py-3 text-sm dark:border-amber-700/60 dark:bg-amber-950/30">
                                <span>Speaker names changed - insights and the recap are out of date.</span>
                                <Button
                                  size="sm"
                                  disabled={transcriptBusy}
                                  onClick={() => triggerTranscriptAction('regenerate_insights')}
                                >
                                  {transcriptBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Re-extract insights'}
                                </Button>
                              </div>
                            )}

                          {isOwner &&
                            transcript &&
                            String(transcript.recap_status) === 'sent' &&
                            transcript.extracted_at &&
                            transcript.recap_sent_at &&
                            new Date(transcript.extracted_at).getTime() >
                              new Date(transcript.recap_sent_at).getTime() && (
                              <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card px-4 py-3 text-sm">
                                <span>The recap was sent before the latest correction.</span>
                                <Button size="sm" variant="outline" disabled={resendBusy} onClick={resendCorrectedRecap}>
                                  {resendBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Resend corrected recap'}
                                </Button>
                              </div>
                            )}

                          {/* Name the speakers: one discoverable place to assign every unnamed voice, whether
                              or not auto-ID had a confident guess. Voices with a suggestion get one-click
                              Confirm (+ Someone else); voices with none get an explicit Assign. Without this,
                              a recording with no voiceprints (e.g. "Voice 1 / Voice 2") had no visible way to
                              name anyone - only a hover-underline on the transcript label. */}
                          {isOwner && speakerResolution.unresolvedVoices.length > 0 && (
                            <div className="space-y-2 rounded-md border border-border bg-card px-4 py-3 text-sm">
                              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                Name the speakers
                              </p>
                              {speakerResolution.unresolvedVoices.map((v) => (
                                <div key={v.label} className="flex flex-wrap items-center justify-between gap-2">
                                  {v.suggestion ? (
                                    <>
                                      <span>
                                        <span className="font-medium">{speakerResolution.displayFor(v.label)}</span>{' '}
                                        looks like <span className="font-medium">{v.suggestion.display_name}</span>{' '}
                                        <span className="text-muted-foreground">
                                          ({Math.round(v.suggestion.confidence * 100)}%)
                                        </span>
                                      </span>
                                      <div className="flex gap-2">
                                        <Button size="sm" disabled={reassignBusy} onClick={() => setReassignLabel(v.label)}>
                                          Someone else
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          disabled={reassignBusy}
                                          onClick={() =>
                                            void saveSpeakerResolution({
                                              resolved_user_id: v.suggestion!.user_id,
                                              label: v.label,
                                            })
                                          }
                                        >
                                          Confirm {v.suggestion.display_name}
                                        </Button>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <span className="font-medium">{speakerResolution.displayFor(v.label)}</span>
                                      <Button size="sm" disabled={reassignBusy} onClick={() => setReassignLabel(v.label)}>
                                        Assign
                                      </Button>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          <Dialog
                            open={!!reassignLabel}
                            onOpenChange={(o) => {
                              if (!o) {
                                setReassignLabel(null);
                                setReassignGuest('');
                                setReassignGuestEmail('');
                              }
                            }}
                          >
                            <DialogContent className="sm:max-w-md">
                              {isOwner ? (
                                <>
                                  <DialogHeader>
                                    <DialogTitle>Who is {reassignLabel}?</DialogTitle>
                                    <DialogDescription>
                                      Assign this speaker to a workspace member, or label them as a guest.
                                    </DialogDescription>
                                  </DialogHeader>
                                  <Command className="rounded-md border border-border">
                                    <CommandInput placeholder="Search members…" />
                                    <CommandList>
                                      <CommandEmpty>No members found.</CommandEmpty>
                                      {speakerPickerCandidates.attendees.length > 0 && (
                                        <CommandGroup heading="In this meeting">
                                          {speakerPickerCandidates.attendees.map((m) => (
                                            <CommandItem
                                              key={m.user_id}
                                              value={m.label}
                                              disabled={reassignBusy}
                                              onSelect={() => void saveSpeakerResolution({ resolved_user_id: m.user_id })}
                                            >
                                              {m.label}
                                            </CommandItem>
                                          ))}
                                        </CommandGroup>
                                      )}
                                      {speakerPickerCandidates.others.length > 0 && (
                                        <CommandGroup heading="Other members">
                                          {speakerPickerCandidates.others.map((m) => (
                                            <CommandItem
                                              key={m.user_id}
                                              value={m.label}
                                              disabled={reassignBusy}
                                              onSelect={() => void saveSpeakerResolution({ resolved_user_id: m.user_id })}
                                            >
                                              {m.label}
                                            </CommandItem>
                                          ))}
                                        </CommandGroup>
                                      )}
                                    </CommandList>
                                  </Command>
                                  <div className="space-y-2">
                                    <Input
                                      placeholder="Or add as guest (e.g. Sarah from Acme)"
                                      value={reassignGuest}
                                      onChange={(e) => setReassignGuest(e.target.value)}
                                      disabled={reassignBusy}
                                    />
                                    <div className="flex items-center gap-2">
                                      <Input
                                        type="email"
                                        placeholder="Their email (optional - invite to view notes + confirm voice)"
                                        value={reassignGuestEmail}
                                        onChange={(e) => setReassignGuestEmail(e.target.value)}
                                        disabled={reassignBusy}
                                      />
                                      <Button
                                        size="sm"
                                        className="shrink-0"
                                        disabled={!reassignGuest.trim() || reassignBusy}
                                        onClick={() => void saveGuest()}
                                      >
                                        {reassignGuestEmail.trim() ? 'Save & invite' : 'Save'}
                                      </Button>
                                    </div>
                                  </div>
                                  {reassignLabel && speakerResolution.byLabel.get(reassignLabel) ? (
                                    <DialogFooter className="sm:justify-start">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        disabled={reassignBusy}
                                        onClick={() => void saveSpeakerResolution({ clear: true })}
                                        className="text-muted-foreground"
                                      >
                                        Clear assignment
                                      </Button>
                                    </DialogFooter>
                                  ) : null}
                                </>
                              ) : (
                                /* Non-owner attendee: self-claim only. RLS lets a member set/clear a
                                   label as themselves, never as anyone else. */
                                <>
                                  <DialogHeader>
                                    <DialogTitle>Is {reassignLabel} you?</DialogTitle>
                                    <DialogDescription>
                                      You can mark this speaker as yourself so the transcript and recap
                                      attribute it correctly. Only the meeting owner can assign it to
                                      someone else.
                                    </DialogDescription>
                                  </DialogHeader>
                                  {reassignLabel &&
                                  speakerResolution.byLabel.get(reassignLabel)?.resolved_user_id === userId ? (
                                    <Button
                                      variant="ghost"
                                      disabled={reassignBusy}
                                      onClick={() => void saveSpeakerResolution({ clear: true })}
                                      className="text-muted-foreground"
                                    >
                                      Remove “that’s me”
                                    </Button>
                                  ) : (
                                    <Button
                                      disabled={reassignBusy}
                                      onClick={() => void saveSpeakerResolution({ resolved_user_id: userId })}
                                    >
                                      {reassignBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Yes, that’s me'}
                                    </Button>
                                  )}
                                </>
                              )}
                            </DialogContent>
                          </Dialog>

                          {/* 1. Transcript - per-speaker segments (preferred) or flat fallback */}
                          {Array.isArray(transcript?.transcript_segments) && transcript.transcript_segments.length > 0 ? (
                            <div className="space-y-3">
                              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                Transcript
                              </Label>
                              <div className="ph-no-capture space-y-2 rounded-md border border-border bg-surface-container-low/40 p-4 text-sm">
                                {transcript.transcript_segments.map((seg: any, i: number) => {
                                  const label = String(seg.speaker || '');
                                  const res = label ? speakerResolution.byLabel.get(label) : null;
                                  const confPct =
                                    label && speakerResolution.confidenceFor(label) != null
                                      ? Math.round((speakerResolution.confidenceFor(label) as number) * 100)
                                      : null;
                                  // Owner can reassign any label; an attendee can only self-claim a label
                                  // that's unresolved or already points to them (mirrors RLS).
                                  const canClick =
                                    !!label &&
                                    (isOwner || (isAttendeeOnMeeting && (!res || res.resolved_user_id === userId)));
                                  // Phase D: surface auto-ID confidence as a subtle hover tooltip (D.2).
                                  const autoNote = confPct != null ? `Auto-identified · ${confPct}%` : '';
                                  return (
                                    <div key={i} className="flex gap-2.5">
                                      {label ? (
                                        canClick ? (
                                          <button
                                            type="button"
                                            onClick={() => setReassignLabel(label)}
                                            title={[autoNote, isOwner ? 'Click to reassign' : 'Is this you?']
                                              .filter(Boolean)
                                              .join(' · ')}
                                            className="w-28 shrink-0 truncate text-left font-medium text-foreground/80 hover:text-primary hover:underline"
                                          >
                                            {speakerResolution.displayFor(label)}
                                          </button>
                                        ) : (
                                          <span
                                            title={autoNote || undefined}
                                            className="w-28 shrink-0 truncate font-medium text-foreground/80"
                                          >
                                            {speakerResolution.displayFor(label)}
                                          </span>
                                        )
                                      ) : null}
                                      <span className={`leading-relaxed text-foreground/90 ${!label ? 'w-full' : 'min-w-0 flex-1'}`}>
                                        {seg.text}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                Transcript
                              </Label>
                              <Textarea
                                rows={12}
                                readOnly
                                value={transcript?.transcript_text || ''}
                                placeholder="Transcript will appear once capture is complete."
                              />
                            </div>
                          )}

                          {/* 1b. Native Google Meet notes/transcript link - shown when the bot
                                 didn't capture a transcript but Google saved a Gemini notes Doc.
                                 We link out to the original rather than importing the text. */}
                          {nativeTranscriptUrl && !transcriptHasText && (
                            <div className="rounded-md border border-border bg-surface-container-low/40 px-4 py-3 text-sm">
                              <p className="leading-relaxed text-foreground/90">
                                Google Meet saved AI notes for this meeting. We can&apos;t import the text
                                automatically, but you can open the original:
                              </p>
                              <a
                                href={nativeTranscriptUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-1 inline-flex items-center gap-1 text-primary hover:underline"
                              >
                                Open Google Meet notes in Google Docs ↗
                              </a>
                            </div>
                          )}

                          {/* 2. Speaker activity timeline */}
                          {Array.isArray(transcript?.transcript_segments) && transcript.transcript_segments.length > 0 && (
                            <SpeakerTimeline
                              segments={(transcript.transcript_segments as any[]).map((s) => ({
                                ...s,
                                speaker: speakerResolution.displayFor(s.speaker),
                              }))}
                            />
                          )}

                          {/* 3. Bot / Transcript / Insights status + action buttons */}
                          <div className="space-y-3">
                            {transcriptStatusHeadline && (
                              <div className="rounded-md border border-amber-300/50 bg-amber-50/95 px-3 py-2.5 text-sm leading-relaxed text-amber-950 shadow-sm dark:border-amber-800/40 dark:bg-amber-950/35 dark:text-amber-50">
                                {transcriptStatusHeadline}
                              </div>
                            )}
                            <div className="grid grid-cols-1 gap-3 rounded-md border border-outline-variant/20 bg-surface-container-low/40 p-4 sm:grid-cols-3">
                              <div className="flex gap-3">
                                <Bot className={cn('h-5 w-5 shrink-0 stroke-[1.75]', botIconClass)} aria-hidden />
                                <div className="min-w-0 flex-1">
                                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Bot</p>
                                  <p className="text-sm font-medium leading-snug">{botStatusLabel}</p>
                                </div>
                              </div>
                              <div className="flex gap-3">
                                <FileText className={cn('h-5 w-5 shrink-0 stroke-[1.5]', transcriptIconClass)} aria-hidden />
                                <div className="min-w-0 flex-1">
                                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Transcript</p>
                                  <p className="text-sm font-medium leading-snug">{transcriptStatusLabelDisplay}</p>
                                </div>
                              </div>
                              <div className="flex gap-3">
                                <Sparkles className={cn('h-5 w-5 shrink-0 stroke-[1.5]', insightsIconClass)} aria-hidden />
                                <div className="min-w-0 flex-1">
                                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Insights</p>
                                  <p className="text-sm font-medium leading-snug">{insightsStatusLabel}</p>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {canInviteBot && (
                                <Button type="button" size="sm" disabled={inviteBotBusy || transcriptBusy} onClick={() => void inviteBotForSeries()}>
                                  {inviteBotBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Invite bot'}
                                </Button>
                              )}
                              {canRefreshBotStatus && (
                                <Button type="button" size="sm" variant="outline" disabled={transcriptBusy} onClick={() => void triggerTranscriptAction('sync_status')}>
                                  {transcriptBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh bot status'}
                                </Button>
                              )}
                              {canRetryBotCapture && (
                                <Button type="button" size="sm" disabled={transcriptBusy} onClick={() => void triggerTranscriptAction('retry_capture')}>
                                  {transcriptBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Retry bot capture'}
                                </Button>
                              )}
                              {canRemoveBotFromCall && (
                                <Button type="button" size="sm" variant="destructive" disabled={transcriptBusy} onClick={() => void removeBotFromCall()}>
                                  {transcriptBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Remove bot from call'}
                                </Button>
                              )}
                              {!strictFutureNotReadyMode && canRetrieveTranscript && (
                                <Button type="button" size="sm" variant="secondary" disabled={transcriptBusy} onClick={() => void triggerTranscriptAction('sync_status')}>
                                  {transcriptBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Retrieve transcript'}
                                </Button>
                              )}
                              {!strictFutureNotReadyMode && canRegenerateInsights && (
                                <Button type="button" size="sm" disabled={transcriptBusy} onClick={() => void triggerTranscriptAction('regenerate_insights')}>
                                  {transcriptBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Re-generate insights'}
                                </Button>
                              )}
                            </div>
                            {!hasMeetingEnded && (
                              <p className="text-xs text-muted-foreground">
                                The transcript bot is scheduled automatically the day before this meeting (in your
                                workspace&apos;s timezone). Transcript retrieval and insight generation are available
                                after the meeting end time.
                              </p>
                            )}
                          </div>

                          {/* 4. IDs for debugging / cross-referencing */}
                          {focusedMeeting?.id && (
                            <div className="border-t border-outline-variant/25 pt-4">
                              <dl className="grid grid-cols-1 gap-3 font-mono text-[11px] leading-snug text-foreground sm:grid-cols-2">
                                <div>
                                  <dt className="text-muted-foreground">Occurrence ID</dt>
                                  <dd className="mt-1 break-all select-all">{focusedMeeting.id}</dd>
                                </div>
                                <div>
                                  <dt className="text-muted-foreground">Recall bot ID</dt>
                                  <dd className="mt-1 break-all select-all">
                                    {transcript?.provider_bot_id?.trim() ? transcript.provider_bot_id : '-'}
                                  </dd>
                                </div>
                              </dl>
                            </div>
                          )}
                        </div>
                      )}

                      {activeSessionTab === 'recording' && (
                        <div className="space-y-4">
                          <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                            Recording
                          </Label>

                          {!recordingState || recordingState.loading ? (
                            <div className="flex h-48 items-center justify-center rounded-md border border-border bg-surface-container-low/40 text-sm text-muted-foreground">
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Loading recording…
                            </div>
                          ) : recordingState.status === 'no_recording' ? (
                            nativeRecordingUrl ? (
                              <div className="space-y-2 rounded-md border border-border bg-surface-container-low/40 px-4 py-4 text-sm">
                                <p className="leading-relaxed text-foreground/90">
                                  Google Meet saved a native recording for this meeting in Google Drive.
                                </p>
                                <a
                                  href={nativeRecordingUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-primary hover:underline"
                                >
                                  Open recording in Google Drive ↗
                                </a>
                              </div>
                            ) : (
                              <div className="rounded-md border border-border bg-surface-container-low/40 px-4 py-6 text-sm text-muted-foreground">
                                No recording was captured for this meeting.
                              </div>
                            )
                          ) : recordingState.status === 'processing' ? (
                            <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface-container-low/40 px-4 py-3 text-sm text-muted-foreground">
                              <span>Recording is still being processed by Recall. Try again in a minute.</span>
                              <Button type="button" size="sm" variant="outline" onClick={() => void loadRecording()}>
                                Retry
                              </Button>
                            </div>
                          ) : recordingState.status === 'error' ? (
                            <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
                              <span className="text-destructive">{recordingState.error || 'Failed to load recording.'}</span>
                              <Button type="button" size="sm" variant="outline" onClick={() => void loadRecording()}>
                                Retry
                              </Button>
                            </div>
                          ) : recordingState.status === 'expired' ? (
                            <div className="rounded-md border border-border bg-surface-container-low/40 px-4 py-6 text-sm text-muted-foreground">
                              This recording is no longer available - meeting recordings are kept by our
                              recording provider for a limited time, and this one has passed that window.
                              The transcript, summary, and action items above are kept permanently.
                            </div>
                          ) : recordingState.video?.url ? (
                            <div className="space-y-2">
                              <video
                                key={recordingState.video.url}
                                controls
                                preload="metadata"
                                src={recordingState.video.url}
                                className="w-full rounded-md border border-border bg-black"
                              />
                              <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <span>Playback URL expires periodically; press refresh if playback fails.</span>
                                <Button type="button" size="sm" variant="ghost" onClick={() => void loadRecording()}>
                                  Refresh URL
                                </Button>
                              </div>
                            </div>
                          ) : recordingState.audio?.url ? (
                            <div className="space-y-2">
                              <div className="rounded-md border border-border bg-surface-container-low/40 px-4 py-6">
                                <p className="mb-3 text-xs text-muted-foreground">Audio-only recording.</p>
                                <audio
                                  key={recordingState.audio.url}
                                  controls
                                  preload="metadata"
                                  src={recordingState.audio.url}
                                  className="w-full"
                                />
                              </div>
                              <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <span>Playback URL expires periodically; press refresh if playback fails.</span>
                                <Button type="button" size="sm" variant="ghost" onClick={() => void loadRecording()}>
                                  Refresh URL
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="rounded-md border border-border bg-surface-container-low/40 px-4 py-6 text-sm text-muted-foreground">
                              This recording isn’t available to play. The transcript and summary above are unaffected.
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog
        open={agendaDeleteDialog != null}
        onOpenChange={(open) => {
          if (!open) setAgendaDeleteDialog(null);
        }}
      >
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove agenda item?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes “{agendaDeleteDialog?.title}” from this session’s agenda.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel type="button" disabled={agendaDeletingId != null}>
              Cancel
            </AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              disabled={agendaDeletingId != null}
              onClick={() => void confirmDeleteAgendaItem()}
            >
              {agendaDeletingId != null ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removing…
                </>
              ) : (
                'Remove'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={calendarDeletedOpen}
        onOpenChange={(open) => {
          if (!open) setCalendarDeletedOpen(false);
        }}
      >
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Removed from your calendar</AlertDialogTitle>
            <AlertDialogDescription>
              This meeting no longer exists in the linked calendar - it looks like it was deleted
              there. You can keep it in Upstand as-is (history preserved) or mark it cancelled.
              Nothing changes unless you choose to.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel type="button" disabled={cancellingCalendarDeleted}>
              Keep in Upstand
            </AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              disabled={cancellingCalendarDeleted}
              onClick={() => void confirmCancelCalendarDeleted()}
            >
              {cancellingCalendarDeleted ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cancelling…
                </>
              ) : (
                'Mark cancelled'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer ownership</DialogTitle>
            <DialogDescription>
              The new owner must be an active workspace member. Admins can also transfer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>New owner</Label>
            <Select value={transferTo} onValueChange={setTransferTo}>
              <SelectTrigger>
                <SelectValue placeholder="Choose member" />
              </SelectTrigger>
              <SelectContent>
                {members
                  .filter((m) => m.user_id !== series.owner_user_id)
                  .map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id}>
                      {m.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferOpen(false)}>
              Cancel
            </Button>
            <Button onClick={transfer}>Transfer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SeriesSettingsFlyout
        open={seriesFlyoutOpen}
        onOpenChange={setSeriesFlyoutOpen}
        series={series}
        canManage={canManage}
        isInPerson={isOneOffInPerson}
        workspaceShared={workspace?.meeting_visibility === 'shared'}
        desktopAppInstalled={desktopApp.loading ? undefined : desktopApp.installed}
        autoDesktopAllowed={autoDesktopRecordingAllowed}
        botName={resolvedSeriesBotName}
        botNameOwnedByCurrentUser={series?.owner_user_id === userId}
        onSaved={(updated) => {
          // Reflect bot-enabled toggle immediately in local state
          if (updated.transcript_bot_enabled !== undefined) {
            setSeriesBotEnabledLocal(Boolean(updated.transcript_bot_enabled));
          }
          if (updated.capture_method !== undefined) {
            setSeriesCaptureMethodLocal(String(updated.capture_method || 'bot'));
          }
          void onRefreshSeries();
        }}
      />

      {series && workspace?.id && (
        <ShareSeriesDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          seriesId={series.id}
          workspaceId={workspace.id}
          seriesTitle={series.title || 'this series'}
          meetingType={series.meeting_type}
          currentMeetingId={focusedMeeting?.id ?? null}
          summaryDraft={summaryEmailDraft}
          currentMeetingTitle={series.title || null}
          currentMeetingLabel={
            focusedMeeting?.starts_at
              ? new Date(focusedMeeting.starts_at).toLocaleString(undefined, {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : null
          }
        />
      )}
    </div>
  );
}
